// src/album/AlbumFromPast.jsx – Full-screen editor overlay + mini layout preview
// Preserves your working logic; only adds overlay & mini preview

import RichTextEditor from '../components/AlbumPageTemplates/RichTextEditor';
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSmartVibes } from '../hooks/use-smart-vibes';
import FlipbookPreview from '../components/FlipbookPreview';
import AlbumPageEditor from './AlbumPageEditor'; // (not used in overlay path; left for compatibility)
import { semiAutoCandidates } from '../utils/selection';
import { buildAlbumFromPast, buildDigestAlbum, convertAlbumMapsToDataUrls } from '../utils/albumFromPast.builder';
import { buildStatsFromSteps, pointsFromSteps } from './SpecialPages';
import { getTemplateOptions, templateFor } from '../utils/pastTripsAlbumTemplate';
import { DEFAULT_TEMPLATE_KEY } from '../utils/pastTripsAlbumTemplate';

import { makeKey, savePreviewCache, getPreviewCache, hasPreviewCache, clearPreviewCache } from '../utils/shareCache';
import { prewarmUpload, getOrCreateUpload } from '../utils/shareCache';
import { useNavigate } from 'react-router-dom'; // 1. ADD THIS IMPORT
import { shareAsStory, previewStoryLocally, formatStoryDuration, uploadDecoupledAlbum } from '../utils/share-v2'; // 2. ADD 'uploadDecoupledAlbum' HERE
import { selectPagesForShare, getSelectionSummary } from '../utils/pageSelector'; // Page selection for 50-page limit
import { Capacitor } from '@capacitor/core';

// 🆕 NEW: IndexedDB storage imports (replaces localStorage for large albums)
import { useAlbumStorage } from '../hooks/useAlbumStorage';
import { generatePhotoId, storePhotosBatch, loadAlbumWithPhotos, getStorageStats, saveAlbum } from '../utils/photoStore';
import { MigrationBanner, needsMigration } from '../utils/migrateToIndexedDB';
import LazyPhoto from '../components/LazyPhoto';
import SaveStatusIndicator, { SaveStatusBadge } from '../components/SaveStatusIndicator';
import html2canvas from 'html2canvas'; // For thumbnail capture
import SharedLinksManager from '../components/SharedLinksManager'; // 🆕 Shared links management
import { saveShareRecord } from '../utils/shareStore';

// ✅ NEW: Subscription imports
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import {
  canCreateAlbum,
  incrementAlbumCount,
  getAlbumsCreatedThisMonth,
  getAlbumsRemaining
} from '../utils/albumTracking';
import { getPhotoSelectionMode } from '../utils/featureGate';
import { incrementAlbumCount as incrementFirestoreCount } from '../firebaseClient';


// --- Trip-specific session cache for preview/back navigation ---
// CACHE STRATEGY:
// - Each trip gets its own sessionStorage cache: `vvd:albumCache:${tripId}`
// - Cache is ONLY restored when returning from preview (checks vvd:returnContext)
// - Cache TTL: 5 minutes (prevents stale data across sessions)
// - Fallback: initialData from IndexedDB (when opening saved albums)
// - This prevents wrong album restoration when switching trips
// 🔹 DEPRECATED: Old localStorage keys (now using IndexedDB via photoStore.js)
// const ALBUM_CACHE_KEY = 'vvd:lastAlbumPages';
// const ALBUM_CACHE_TTL_MS = 10 * 60 * 1000;
// const ALBUM_LIBRARY_INDEX_KEY = 'vvd:album:list';
// const ALBUM_ITEM_PREFIX = 'vvd:album:item:';
const ALBUM_CACHE_KEY = 'vvd:lastAlbumPages'; // Keep for migration detection
const ALBUM_LIBRARY_INDEX_KEY = 'vvd:album:list'; // Keep for migration detection

// 🔹 Create a quick fingerprint of album pages to detect changes
// Used for caching preview - avoids re-uploading unchanged albums
function createAlbumFingerprint(albumPages) {
  if (!albumPages || !albumPages.length) return null;
  
  // Create a lightweight fingerprint based on:
  // - Number of pages
  // - Page types
  // - Number of photos per page
  // - First few characters of each photo URL (to detect photo changes)
  const parts = albumPages.map(page => {
    const type = page.type || page.templateKey || 'unknown';
    const photoCount = page.photos?.length || 0;
    const photoHints = (page.photos || [])
      .slice(0, 3)
      .map(p => (p.url || p.src || '').slice(-20)) // Last 20 chars of URL
      .join('|');
    return `${type}:${photoCount}:${photoHints}`;
  });
  
  return `${albumPages.length}:${parts.join(';')}`;
}

// 🔹 GLOBAL CACHE for preview navigation (bypasses IndexedDB complexity)
// This survives navigation within the same app session
if (typeof window !== 'undefined') {
  window.__vivajourPreviewCache = window.__vivajourPreviewCache || null;
}

// 🔔 Toast notification helper for user feedback
function showToast(message, options = {}) {
  const {
    duration = 3000,
    background = '#3b82f6', // Blue for info
    icon = 'ℹ️'
  } = options;
  
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    background: ${background};
    color: white;
    padding: 12px 20px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    z-index: 99999;
    font-weight: 500;
    font-size: 14px;
    max-width: 90%;
    text-align: center;
    display: flex;
    align-items: center;
    gap: 8px;
  `;
  toast.innerHTML = `<span>${icon}</span><span>${message}</span>`;
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.3s';
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

// Check WebP support (cached result for performance)
let _webpSupported = null;
function supportsWebP() {
  if (_webpSupported !== null) return _webpSupported;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const dataUrl = canvas.toDataURL('image/webp');
    _webpSupported = dataUrl.startsWith('data:image/webp');
    console.log('[WebP Support]', _webpSupported ? '✅ Enabled' : '❌ Disabled (falling back to JPEG)');
    return _webpSupported;
  } catch (err) {
    console.warn('[WebP Support] Detection failed:', err);
    _webpSupported = false;
    return false;
  }
}

// =====================================================================
// THUMBNAIL CACHE UTILITIES - For ViewAllPagesModal performance
// =====================================================================

/**
 * Generate a fingerprint for a page to detect changes.
 * When fingerprint changes, we know to re-capture the thumbnail.
 */
function getPageFingerprint(page) {
  if (!page) return 'empty';
  
  const parts = [
    page.templateKey || page.type || 'unknown',
    // Photo IDs in order
    (page.items || []).map(item => item?.photoId || 'no-photo').join(','),
    // Key meta fields that affect appearance
    page.meta?.caption || '',
    page.meta?.title || '',
    page.meta?.subtitle || '',
    page.meta?.date || '',
  ];
  
  return parts.join('|');
}

/**
 * In-memory thumbnail cache.
 * Key: page fingerprint
 * Value: { dataUrl: string, timestamp: number }
 */
const thumbnailCache = new Map();

// Cache TTL: 10 minutes (thumbnails are cheap to regenerate if needed)
const THUMBNAIL_CACHE_TTL_MS = 10 * 60 * 1000;

/**
 * Get cached thumbnail if valid, otherwise return null
 */
function getCachedThumbnail(fingerprint) {
  const cached = thumbnailCache.get(fingerprint);
  if (!cached) return null;
  
  // Check if cache is still fresh
  if (Date.now() - cached.timestamp > THUMBNAIL_CACHE_TTL_MS) {
    thumbnailCache.delete(fingerprint);
    return null;
  }
  
  return cached.dataUrl;
}

/**
 * Store thumbnail in cache
 */
function setCachedThumbnail(fingerprint, dataUrl) {
  thumbnailCache.set(fingerprint, {
    dataUrl,
    timestamp: Date.now()
  });
}

/**
 * Clear all cached thumbnails (call when switching albums)
 */
function clearThumbnailCache() {
  thumbnailCache.clear();
  console.log('[ThumbnailCache] Cleared');
}

// Expose for external use (e.g., when switching albums)
if (typeof window !== 'undefined') {
  window.__vivajour_clearThumbnailCache = clearThumbnailCache;
}

// src/album/AlbumFromPast.jsx (around line 118)
async function optimizePhotoForSharing(url, maxWidth = 800, quality = 0.6, useWebP = true) {
  return new Promise(async (resolve, reject) => { // ⬅️ MAKE ASYNC
    
    // 🆕 STEP 1: Convert non-data-URLs to data-URLs before proceeding
    let dataUrl = url;
    if (!dataUrl || !dataUrl.startsWith('data:')) {
      try {
        // Use the existing utility to fetch and convert native URI to data URL
        dataUrl = await convertUriToDataUrl(url); 
        if (!dataUrl.startsWith('data:')) {
           // If conversion still fails to produce a data URL, return the original
           console.warn('[Photo Optimization] Failed to convert URI to data URL, skipping optimization:', url);
           return resolve(url); 
        }
      } catch (err) {
        console.error('[Photo Optimization] URI conversion failed:', err);
        return resolve(url); // Return original on error
      }
    }
    
    // Use the converted/validated dataUrl for the rest of the function
    const img = new Image();
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      
      // Calculate new dimensions (maintain aspect ratio)
      let width = img.width;
      let height = img.height;
      
      const originalSize = dataUrl.length; // ⬅️ USE dataUrl
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      // If already small enough, don't resize (but still re-encode with compression)
      const needsResize = width !== img.width || height !== img.height;
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw resized image
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Choose format: WebP if supported and requested, otherwise JPEG
      const shouldUseWebP = useWebP && supportsWebP();
      const format = shouldUseWebP ? 'image/webp' : 'image/jpeg';
      
      // Convert with compression
      const optimizedUrl = canvas.toDataURL(format, quality);
      const optimizedSize = optimizedUrl.length;
      
      // Calculate savings
      const reduction = ((1 - optimizedSize / originalSize) * 100).toFixed(1);
      const originalKB = (originalSize / 1024).toFixed(1);
      const optimizedKB = (optimizedSize / 1024).toFixed(1);
      
      console.log(
        `[Photo Optimization] ${img.width}x${img.height} → ${width}x${height} | ` +
        `${format.split('/')[1].toUpperCase()} | ` +
        `${originalKB}KB → ${optimizedKB}KB (${reduction}% reduction)`
      );
      
      resolve(optimizedUrl);
    };
    
    img.onerror = (err) => {
      console.error('[Photo Optimization] Failed to load image:', err);
      resolve(url); // Return original on error
    };
    
    // Use the potentially converted dataUrl for the image source
    img.src = dataUrl; // ⬅️ USE dataUrl
  });
}

/**
 * Convert Capacitor URI to data URL (only when needed for upload)
 */
async function convertUriToDataUrl(uri) {
  if (!uri || uri.startsWith('data:')) return uri;

  return new Promise((resolve, reject) => {
    const img = new Image();

    // Only use crossOrigin for remote HTTP(S) URLs
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No 2D context'));
        return;
      }

      try {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve(dataUrl);
      } catch (err) {
        console.error('[convertUriToDataUrl] Failed to convert to data URL for URI', uri, err);
        reject(err);
      }
    };

    img.onerror = (err) => {
      console.error('[convertUriToDataUrl] Failed to load image for URI', uri, err);
      reject(new Error('Failed to load image'));
    };

    img.src = uri;
  });
}


/**
 * Optimize all photos in pages before sharing/preview
 * This dramatically reduces payload size (34MB → 12-18MB)
 */
async function optimizeAllPhotosInPages(pages) {
  console.log('🎨 [Optimization] Starting photo optimization...');
  const startTime = Date.now();
  
  let totalOriginalSize = 0;
  let totalOptimizedSize = 0;
  let photoCount = 0;
  
  // Process all pages
  const optimizedPages = await Promise.all(
    pages.map(async (page) => {
      if (!page.items || page.items.length === 0) return page;
      
      // Optimize all photos in this page
      const optimizedItems = await Promise.all(
  page.items.map(async (item) => {
    if (!item.url) {
      return item;
    }

    let dataUrl = item.url;

    // If it is not already a data URL, try to convert (Android content://, file://, etc.)
    if (!dataUrl.startsWith('data:')) {
      try {
        dataUrl = await convertUriToDataUrl(dataUrl);
      } catch (err) {
        console.warn('[Optimization] Failed to convert URI to data URL for page item:', err);
        return item; // Keep as-is if conversion fails
      }
    }

    if (!dataUrl || !dataUrl.startsWith('data:')) {
      // Still not a data URL; nothing we can safely optimize
      return item;
    }

    const originalSize = dataUrl.length;
    totalOriginalSize += originalSize;
    photoCount++;

    try {
      const optimizedUrl = await optimizePhotoForSharing(dataUrl);
      totalOptimizedSize += optimizedUrl.length;

      // Use short, stable id derived from the optimized data URL
      const shortId = generateShortPhotoId(optimizedUrl);

      return {
        ...item,
        url: optimizedUrl,
        photoId: shortId,
        id: shortId,
      };
    } catch (err) {
      console.error('[Optimization] Failed to optimize photo:', err);
      totalOptimizedSize += originalSize; // Count as no savings
      return item; // Keep original on error
    }
  })
);

      
      return { ...page, items: optimizedItems };
    })
  );
  
  const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
  const originalMB = (totalOriginalSize / (1024 * 1024)).toFixed(2);
  const optimizedMB = (totalOptimizedSize / (1024 * 1024)).toFixed(2);
  const reduction = totalOriginalSize > 0 
    ? ((1 - totalOptimizedSize / totalOriginalSize) * 100).toFixed(1)
    : 0;
  
  console.log(
    `✅ [Optimization Complete] ${photoCount} photos | ` +
    `${originalMB}MB → ${optimizedMB}MB (${reduction}% reduction) | ` +
    `${elapsed}s`
  );
  
  return optimizedPages;
}

/**
 * Generate a short, stable ID from a data URL
 */
function generateShortPhotoId(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') {
    return 'ph_' + Math.random().toString(36).slice(2, 10);
  }
  
  // If it's already a short ID, keep it
  if (!dataUrl.startsWith('data:')) {
    return dataUrl.slice(0, 64); // Limit length
  }
  
  // Create a short hash from the data URL
  const b64 = (dataUrl || '').split(',')[1] || dataUrl || '';
  let h = 5381;
  // Sample every 100th character for speed (still deterministic)
  for (let i = 0; i < b64.length; i += 100) {
    h = ((h << 5) + h) ^ b64.charCodeAt(i);
  }
  return 'ph_' + (h >>> 0).toString(36);
}

/**
 * Optimize the photos array (separate from pages)
 */
async function optimizePhotosArray(photos) {
  if (!Array.isArray(photos) || photos.length === 0) {
    return photos;
  }
  
  console.log('🎨 [Optimization] Starting photos array optimization...');
  const startTime = Date.now();
  
  let totalOriginalSize = 0;
  let totalOptimizedSize = 0;
  
  const optimizedPhotos = await Promise.all(
    photos.map(async (photo) => {
      const url = photo.url || photo.src;
      
      // Only optimize base64 data URLs
     if (!url) return photo;
      
      // Convert URI to data URL if needed (for upload)
      let dataUrl = url;
      if (!url.startsWith('data:')) {
        try {
          dataUrl = await convertUriToDataUrl(url);
        } catch (err) {
          console.error('Failed to convert URI:', err);
          return photo;
        }
      }
      
      const originalSize = dataUrl.length;
      totalOriginalSize += originalSize;
      
      try {
        const optimizedUrl = await optimizePhotoForSharing(dataUrl);
        totalOptimizedSize += optimizedUrl.length;
        
        // CRITICAL FIX: Generate a SHORT id, not the full image!
        const shortId = generateShortPhotoId(dataUrl);
        
        // Create clean photo with NO large fields
        return {
          id: shortId,              // ← SHORT hash, not full image!
          url: optimizedUrl,        // ← Optimized image
          src: optimizedUrl,        // ← Same optimized image
          fileName: photo.fileName,
          timestamp: photo.timestamp,
          gps: photo.gps,
        };
      } catch (err) {
        console.error('[Optimization] Failed to optimize photo in array:', err);
        totalOptimizedSize += originalSize;
        return photo;
      }
    })
  );
  
  const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
  const originalMB = (totalOriginalSize / (1024 * 1024)).toFixed(2);
  const optimizedMB = (totalOptimizedSize / (1024 * 1024)).toFixed(2);
  const reduction = totalOriginalSize > 0 
    ? ((1 - totalOptimizedSize / totalOriginalSize) * 100).toFixed(1)
    : 0;
  
  console.log(
    `✅ [Photos Array Optimization Complete] ${optimizedPhotos.length} photos | ` +
    `${originalMB}MB → ${optimizedMB}MB (${reduction}% reduction) | ` +
    `${elapsed}s`
  );
  
  return optimizedPhotos;
}

function sanitizeRichText(html) {
  if (typeof html !== 'string') return '';
  try {
    const div = document.createElement('div');
    div.innerHTML = html;

    // Strip attributes except for class on whitelisted tags
    const ALLOWED_TAGS = new Set(['b','strong','i','em','u','br','p','h1','h2','h3','ul','ol','li']);
    const walker = document.createTreeWalker(div, NodeFilter.SHOW_ELEMENT, null);
    const toRemove = [];
    while (walker.nextNode()) {
      const el = walker.currentNode;
      const tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (!ALLOWED_TAGS.has(tag)) {
        // unwrap unknown tags but keep inner content
        while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
        toRemove.push(el);
      } else {
        // remove style/on* attrs
        [...el.attributes].forEach(attr => {
          const n = attr.name.toLowerCase();
          if (n.startsWith('on') || n === 'style') el.removeAttribute(attr.name);
        });
      }
    }
    toRemove.forEach(n => n.parentNode && n.parentNode.removeChild(n));

    return div.innerHTML;
  } catch {
    return '';
  }
}


// --- FAST FACE HINTS (main thread) ---------------------------------
let __fdInst = null;
async function ensureFaceDetectorForDiverse() {
  if ('FaceDetector' in window) {
    if (!__fdInst) __fdInst = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 10 });
    return { type: 'browser', inst: __fdInst };
  }
  if (!window.__mpFD_DIVERSE) {
    await new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/face_detection.js';
      s.onload = res; s.onerror = () => rej(new Error('mediapipe load failed'));
      document.head.appendChild(s);
    });
    // eslint-disable-next-line no-undef
    window.__mpFD_DIVERSE = new FaceDetection({ locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${f}` });
    window.__mpFD_DIVERSE.setOptions({ model: 'short', minDetectionConfidence: 0.5 });
  }
  return { type: 'mp', inst: window.__mpFD_DIVERSE };
}

async function faceHintsForPhotos(items, { minCoverage = 0.03, resize = 512 } = {}) {
  try {
    const { type, inst } = await ensureFaceDetectorForDiverse();
    const out = {}; // url -> { faceCount, faceCoverage, pass }

    for (const it of items) {
      if (!it?.url) continue;
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = it.url; });
      const scale = Math.min(1, resize / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, c.width, c.height);

      let faces = [];
      if (type === 'browser') {
        const bmp = await createImageBitmap(c);
        try {
          const dets = await inst.detect(bmp);
          faces = (dets || []).map(f => f.boundingBox);
        } catch { faces = []; }
      } else {
        const res = await new Promise((resolve) => { inst.onResults(resolve); inst.send({ image: c }); });
        const dets = (res && res.detections) || [];
        const w = c.width, h = c.height;
        faces = dets.map(d => {
          const bb = d.boundingBox || (d.locationData && d.locationData.relativeBoundingBox) || {};
          let cx = Number(bb.xCenter || 0), cy = Number(bb.yCenter || 0), bw = Number(bb.width || 0), bh = Number(bb.height || 0);
          const norm = cx <= 1 && cy <= 1 && bw <= 1 && bh <= 1 && (bw > 0 || bh > 0);
          if (norm) { cx *= w; cy *= h; bw *= w; bh *= h; }
          return { x: cx - bw/2, y: cy - bh/2, width: bw, height: bh };
        });
      }

      const area = c.width * c.height;
      let cover = 0;
      for (const b of faces) cover += Math.max(0, Math.min(b.width, c.width) * Math.min(b.height, c.height));
      const faceCoverage = area > 0 ? cover / area : 0;
      out[it.url] = { faceCount: faces.length, faceCoverage, pass: faces.length > 0 && faceCoverage >= minCoverage };
    }
    return out;
  } catch {
    return {};
  }
}

// --- Mini Page Thumbnail Renderer (layout-aware) ---
// --- Mini Page Thumbnail Renderer (layout-aware) ---
function PageThumb({ page }) {
  const tplKey = ((page?.templateKey || page?.type) || '').toLowerCase();
  const photos = Array.isArray(page?.photos) ? page.photos : [];
  const p0 = photos[0]?.url || '';
  const p1 = photos[1]?.url || '';
  const p2 = photos[2]?.url || '';
  const p3 = photos[3]?.url || '';
  const textHtml = page?.meta?.journalText || page?.meta?.text || '';

  // generic frame (NO footer here; the modal already renders "Page {idx+1}")
  const Frame = ({ children }) => (
    <div className="rounded-lg overflow-hidden border-2 border-slate-700 hover:border-blue-500 transition-all bg-slate-900">
      <div className="aspect-[3/2] bg-slate-700 relative">{children}</div>
    </div>
  );

  const MapPlaceholder = () => (
    <div className="w-full h-full">
      <svg viewBox="0 0 300 200" className="w-full h-full">
        <rect width="300" height="200" fill="#0f172a" />
        <g stroke="#334155" strokeWidth="1" opacity="0.6">
          {Array.from({ length: 15 }).map((_, i) => (
            <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2="200" />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 20} x2="300" y2={i * 20} />
          ))}
        </g>
        <circle cx="150" cy="100" r="8" fill="#10b981" stroke="#fff" strokeWidth="2" />
      </svg>
    </div>
  );

  const TextPreview = () => (
    <div className="w-full h-full p-2 overflow-hidden">
      <div
        className="w-full h-full bg-slate-800/60 rounded p-2 text-[10px] leading-snug text-slate-200 prose prose-invert"
        style={{ WebkitLineClamp: 6, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        // show safe HTML snippet if present
        dangerouslySetInnerHTML={{ __html: sanitizeRichText(textHtml || 'Add your story...') }}
      />
    </div>
  );

  // Cover page
  if (tplKey === 'cover') {
    return (
      <Frame>
        <div className="absolute inset-0">
          {p0 ? (
            <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-slate-800" />
          )}
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="text-[14px] font-bold">Cover</div>
            </div>
          </div>
        </div>
      </Frame>
    );
  }

  // Stats page
  if (tplKey === 'stats') {
    return (
      <Frame>
        <div className="absolute inset-0 bg-slate-900 p-2 flex flex-col gap-1">
          <div className="text-[11px] font-semibold text-slate-200">Statistics</div>
          <div className="flex-1 grid grid-cols-2 gap-1">
            <div className="bg-slate-800 rounded p-1">
              <div className="text-[9px] text-slate-400">Distance</div>
              <div className="text-[10px] text-white font-bold">123 mi</div>
            </div>
            <div className="bg-slate-800 rounded p-1">
              <div className="text-[9px] text-slate-400">Chapters</div>
              <div className="text-[10px] text-white font-bold">5</div>
            </div>
            <div className="bg-slate-800 rounded p-1">
              <div className="text-[9px] text-slate-400">Photos</div>
              <div className="text-[10px] text-white font-bold">42</div>
            </div>
            <div className="bg-slate-800 rounded p-1">
              <div className="text-[9px] text-slate-400">Days</div>
              <div className="text-[10px] text-white font-bold">7</div>
            </div>
          </div>
        </div>
      </Frame>
    );
  }

  // Map/Route page
  if (tplKey === 'map' || tplKey === 'route') {
    return (
      <Frame>
        <div className="absolute inset-0">
          <MapPlaceholder />
        </div>
      </Frame>
    );
  }

  // Timeline page
  if (tplKey === 'timeline') {
    return (
      <Frame>
        <div className="absolute inset-0 bg-slate-900 p-2">
          <div className="text-[11px] font-bold text-white mb-1">Timeline</div>
          <div className="space-y-0.5 text-[8px]">
            <div className="flex gap-2">
              <span className="text-slate-400">05/11</span>
              <span className="text-slate-200">City 1</span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400">05/12</span>
              <span className="text-slate-200">City 2</span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400">05/13</span>
              <span className="text-slate-200">City 3</span>
            </div>
          </div>
        </div>
      </Frame>
    );
  }

  // Step intro: map/metadata left, hero photo right
  if (tplKey === 'step-intro-left') {
    return (
      <Frame>
        <div className="absolute inset-0 grid grid-cols-2">
          <div className="bg-slate-900 p-2 flex flex-col gap-1">
            <div className="text-[10px] font-semibold text-slate-200 truncate">
              {page.meta?.title || 'Step intro'}
            </div>
            <div className="text-[9px] text-slate-400">{page.meta?.date || ''}</div>
            <div className="flex-1 rounded overflow-hidden">
              <MapPlaceholder />
            </div>
          </div>
          <div className="relative">
            {p0 ? (
              <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-[10px] text-slate-400">Add photo…</div>
            )}
          </div>
        </div>
      </Frame>
    );
  }

  // Journal variants (support left/right/short/plain)
  if (['journal-right', 'journal-short', 'journal'].includes(tplKey)) {
    return (
      <Frame>
        <div className="absolute inset-0 grid grid-cols-2">
          <div className="relative">
            {p0 ? (
              <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-[10px] text-slate-400">Add photo…</div>
            )}
          </div>
          <TextPreview />
        </div>
      </Frame>
    );
  }

  if (tplKey === 'journal-left') {
    return (
      <Frame>
        <div className="absolute inset-0 grid grid-cols-2">
          <TextPreview />
          <div className="relative">
            {p0 ? (
              <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-[10px] text-slate-400">Add photo…</div>
            )}
          </div>
        </div>
      </Frame>
    );
  }

  // 2-photo layouts (tall)
  if (['grid-2-tall', 'grid-left', 'grid-right', 'two-photos-tall', 'two-photos', '2-photos', '2 photos (tall)'].includes(tplKey)) {
    return (
      <Frame>
        <div className="absolute inset-0 grid grid-cols-2 gap-0.5 p-0.5">
          <div className="relative bg-slate-800">{p0 && <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p1 && <img src={p1} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
        </div>
      </Frame>
    );
  }

  // 3-photo layouts (wide)
  if (['grid-3-wide', 'three-photos', '3-photos', '3 photos (wide)'].includes(tplKey)) {
    return (
      <Frame>
        <div className="absolute inset-0 grid grid-cols-3 gap-0.5 p-0.5">
          <div className="relative bg-slate-800">{p0 && <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p1 && <img src={p1} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p2 && <img src={p2} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
        </div>
      </Frame>
    );
  }

  // 4-photo layouts (square grid)
  if (['grid-4-square', 'four-photos', '4-photos', '4 photos (square)'].includes(tplKey)) {
    return (
      <Frame>
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-0.5 p-0.5">
          <div className="relative bg-slate-800">{p0 && <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p1 && <img src={p1} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p2 && <img src={p2} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p3 && <img src={p3} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
        </div>
      </Frame>
    );
  }

  // Collage: 1 Main + 3 Small (hero-left)
  if (tplKey === 'collage-hero-left') {
    return (
      <Frame>
        <div className="absolute inset-0 grid gap-0.5 p-0.5" style={{ gridTemplateColumns: '2fr 1fr', gridTemplateRows: 'repeat(3, 1fr)' }}>
          <div className="relative bg-slate-800 row-span-3">{p0 && <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p1 && <img src={p1} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p2 && <img src={p2} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p3 && <img src={p3} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
        </div>
      </Frame>
    );
  }

  // Collage: 1 Tall + 2 Small (hero-left-tall)
  if (tplKey === 'collage-hero-left-tall') {
    return (
      <Frame>
        <div className="absolute inset-0 grid gap-0.5 p-0.5" style={{ gridTemplateColumns: '2fr 1fr', gridTemplateRows: 'repeat(2, 1fr)' }}>
          <div className="relative bg-slate-800 row-span-2">{p0 && <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p1 && <img src={p1} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p2 && <img src={p2} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
        </div>
      </Frame>
    );
  }

  // Collage: 1 Wide + 2 Square (hero-top-wide)
  if (tplKey === 'collage-hero-top-wide') {
    return (
      <Frame>
        <div className="absolute inset-0 grid gap-0.5 p-0.5" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: '2fr 1fr' }}>
          <div className="relative bg-slate-800 col-span-2">{p0 && <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p1 && <img src={p1} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
          <div className="relative bg-slate-800">{p2 && <img src={p2} alt="" className="absolute inset-0 w-full h-full object-cover" />}</div>
        </div>
      </Frame>
    );
  }

  // Full page / hero (default fallback for hero-right, hero-left, etc.)
  return (
    <Frame>
      {p0 ? (
        <img src={p0} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-slate-400 text-xs capitalize">
          {tplKey.replace(/-/g, ' ') || 'Empty page'}
        </div>
      )}
    </Frame>
  );
}

function toUrls(items = [], photoIndex = {}) {
  if (!Array.isArray(items)) return [];
  if (items.length && items[0] && 'url' in items[0]) return items;
  return items.map((item) => {
    const photoId = item?.photoId;
    const p = photoId && photoIndex[photoId] ? photoIndex[photoId] : null;
    return p
      ? { url: p.url, position: p.position || item?.position || { x:50, y:50, scale:100 }, id: p.id, ...item }
      : { url: '', position: item?.position || { x:50, y:50, scale:100 }, ...item };
  });
}

function MiniRender({ page, photoIndex }) {
  const template = templateFor(page.templateKey || page.type);
  if (!template || !template.component) return null;
  const PageComponent = template.component;
  const itemsWithUrls = toUrls(page.items, photoIndex);
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <PageComponent items={itemsWithUrls} meta={page.meta} {...template.props} isPrinting />
    </div>
  );
}
// MiniPage: scale a real page into a crisp thumbnail
function MiniPage({ page, photoIndex, targetWidth = null }) {
  const containerRef = React.useRef(null);
  const [width, setWidth] = React.useState(targetWidth || 340);
  
  const tpl = templateFor(page.templateKey || page.type);
  // Prefer template dimensions if present, fall back to 1200x800 (3:2)
  const baseW = (tpl && tpl.props && tpl.props.pageWidth)  || 1200;
  const baseH = (tpl && tpl.props && tpl.props.pageHeight) || 800;
  
  React.useEffect(() => {
    if (targetWidth !== null) return; // Use fixed width if provided
    
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        if (w > 0) setWidth(w);
      }
    };
    
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, [targetWidth]);
  
  const scale = width / baseW;

  return (
    <div
      ref={containerRef}
      style={{
        width: targetWidth !== null ? targetWidth : '100%',
        height: baseH * scale,
        background: '#f9f7f5',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: baseW,
          height: baseH,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <MiniRender page={page} photoIndex={photoIndex} />
      </div>
    </div>
  );
}



// ===== NEW: View All Pages Modal Component =====
// Compact, template-aware page thumbnail with THUMBNAIL CACHING for iOS performance


// =====================================================================
// ThumbnailCapture - Renders page off-screen and captures as image
// =====================================================================
function ThumbnailCapture({ page, photoIndex, onCapture, targetWidth = 300 }) {
  const containerRef = React.useRef(null);
  const capturedRef = React.useRef(false);
  
  const tpl = templateFor(page.templateKey || page.type);
  const baseW = tpl?.props?.pageWidth || 1024;
  const baseH = tpl?.props?.pageHeight || 680;
  const aspectRatio = baseW / baseH;
  
  React.useEffect(() => {
    if (capturedRef.current) return;
    
    const captureTimeout = setTimeout(async () => {
      if (!containerRef.current || capturedRef.current) return;
      capturedRef.current = true;
      
      try {
        const canvas = await html2canvas(containerRef.current, {
          scale: 0.5, // Capture at half size for speed
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#f9f7f5',
          logging: false,
        });
        
        // Scale down to thumbnail size
        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = targetWidth;
        thumbCanvas.height = Math.round(targetWidth / aspectRatio);
        
        const ctx = thumbCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);
        
        // Convert to JPEG for smaller size
        const dataUrl = thumbCanvas.toDataURL('image/jpeg', 0.7);
        onCapture(dataUrl);
      } catch (err) {
        console.warn('[ThumbnailCapture] Failed:', err);
        onCapture(null);
      }
    }, 100); // Small delay to ensure render is complete
    
    return () => clearTimeout(captureTimeout);
  }, [page, onCapture, targetWidth, aspectRatio]);
  
  // Render the page off-screen
  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        width: `${baseW}px`,
        height: `${baseH}px`,
        background: '#f9f7f5',
        overflow: 'hidden',
      }}
    >
      <MiniRender page={page} photoIndex={photoIndex} />
    </div>
  );
}


// =====================================================================
// PageThumbnail - Displays a single page thumbnail with loading state
// =====================================================================
function PageThumbnail({ page, pageIndex, photoIndex, onNeedsCapture, allThumbnails }) {
  const fingerprint = React.useMemo(() => getPageFingerprint(page), [page]);
  const thumbnail = allThumbnails[fingerprint];
  
  const tpl = templateFor(page.templateKey || page.type);
  const baseW = tpl?.props?.pageWidth || 1024;
  const baseH = tpl?.props?.pageHeight || 680;
  
  // Check if we need to capture this thumbnail
  React.useEffect(() => {
    if (!thumbnail) {
      // Check cache first
      const cached = getCachedThumbnail(fingerprint);
      if (cached) {
        onNeedsCapture(fingerprint, cached, false); // false = don't need to capture
      } else {
        onNeedsCapture(fingerprint, null, true); // true = needs capture
      }
    }
  }, [fingerprint, thumbnail, onNeedsCapture]);
  
  // Loading state
  if (!thumbnail) {
    return (
      <div 
        style={{
          width: '100%',
          aspectRatio: `${baseW} / ${baseH}`,
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <div 
          style={{
            width: '32px',
            height: '32px',
            border: '3px solid #475569',
            borderTopColor: '#60a5fa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <div style={{ color: '#64748b', fontSize: '11px' }}>
          {pageIndex + 1}
        </div>
      </div>
    );
  }
  
  // Render thumbnail image
  return (
    <img
      src={thumbnail}
      alt={`Page ${pageIndex + 1}`}
      style={{
        width: '100%',
        aspectRatio: `${baseW} / ${baseH}`,
        objectFit: 'cover',
        background: '#f9f7f5',
      }}
      draggable={false}
    />
  );
}


// =====================================================================
// ViewAllPagesModal - Uses thumbnails for smooth scrolling on iOS
// =====================================================================
function ViewAllPagesModal({ pages, photos = [], onClose, onDeletePage, onAddPageAtIndex, onNavigateToPage }) {
  // Thumbnail state: fingerprint -> dataUrl
  const [thumbnails, setThumbnails] = useState({});
  // Queue of pages that need thumbnail capture
  const [captureQueue, setCaptureQueue] = useState([]);
  // Currently capturing
  const [capturing, setCapturing] = useState(null);

  const photoIndex = React.useMemo(() => (
    Array.isArray(photos) ? Object.fromEntries(photos.map(p => [p.id, p])) : (photos || {})
  ), [photos]);

  // Handle thumbnail request from PageThumbnail
  const handleNeedsCapture = useCallback((fingerprint, cachedDataUrl, needsCapture) => {
    if (cachedDataUrl) {
      // Use cached version
      setThumbnails(prev => ({ ...prev, [fingerprint]: cachedDataUrl }));
    } else if (needsCapture) {
      // Add to capture queue
      setCaptureQueue(prev => {
        if (prev.some(item => item.fingerprint === fingerprint)) return prev;
        // Find the page for this fingerprint
        const pageIndex = pages.findIndex(p => getPageFingerprint(p) === fingerprint);
        if (pageIndex === -1) return prev;
        return [...prev, { fingerprint, pageIndex }];
      });
    }
  }, [pages]);

  // Process capture queue one at a time (avoids memory spikes)
  useEffect(() => {
    if (capturing || captureQueue.length === 0) return;
    
    // Take next from queue
    const next = captureQueue[0];
    setCapturing(next);
    setCaptureQueue(prev => prev.slice(1));
  }, [capturing, captureQueue]);

  // Handle capture completion
  const handleCaptureComplete = useCallback((dataUrl) => {
    if (!capturing) return;
    
    if (dataUrl) {
      // Store in state and cache
      setThumbnails(prev => ({ ...prev, [capturing.fingerprint]: dataUrl }));
      setCachedThumbnail(capturing.fingerprint, dataUrl);
    }
    
    setCapturing(null);
  }, [capturing]);

  // Calculate loading progress
  const totalPages = pages.length;
  const loadedCount = Object.keys(thumbnails).length;
  const isLoading = loadedCount < totalPages;

  return (
    <div 
      className="fixed inset-0 z-[1100] bg-black/80 backdrop-blur-sm overflow-hidden"
      onClick={onClose}
    >
      {/* Off-screen capture area */}
      {capturing && (
        <ThumbnailCapture
          page={pages[capturing.pageIndex]}
          photoIndex={photoIndex}
          onCapture={handleCaptureComplete}
          targetWidth={300}
        />
      )}
      
      {/* Main scrollable content */}
      <div 
        className="h-full overflow-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-h-screen p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            
            {/* Header - Sticky with iOS safe area */}
            <div className="flex items-center justify-between mb-4 md:mb-6 sticky top-0 z-10 bg-black/70 backdrop-blur-md -mx-4 px-4 py-3 md:-mx-8 md:px-8 border-b border-white/10" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">All Album Pages</h2>
                {isLoading && (
                  <div className="text-sm text-slate-400 mt-1">
                    Loading thumbnails... {loadedCount}/{totalPages}
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-3 md:px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm md:text-base"
              >
                Close
              </button>
            </div>
            
            {/* Mobile instruction */}
            <div className="mb-4 text-sm text-slate-300 bg-slate-800/50 rounded-lg p-3 md:hidden">
              Tap a page to view it, or use the buttons below each page
            </div>
            
            {/* Pages Grid - More columns for thumbnails */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {pages.map((page, idx) => {
                const tplKey = ((page.templateKey || page.type) || '').toLowerCase();
                const isSpecial = ['cover', 'stats', 'map', 'route', 'step-intro-left'].includes(tplKey);
                const pageType = tplKey;
                const isLastPage = idx === pages.length - 1;
                const isJournalPage = pageType.includes('journal');
                const isSpecialForAdd = ['cover', 'stats', 'map', 'route', 'step-intro-left', 'timeline'].includes(pageType);
                const shouldShowAdd = !isSpecialForAdd && !(isLastPage && isJournalPage);
                const fingerprint = getPageFingerprint(page);
                
                return (
                  <div 
                    key={`${idx}-${fingerprint}`}
                    className="relative bg-slate-900 rounded-lg overflow-hidden border-2 border-slate-700 hover:border-slate-500 transition-colors"
                  >
                    {/* Clickable Thumbnail */}
                    <div
                      className="cursor-pointer relative"
                      onClick={() => onNavigateToPage(idx)}
                    >
                      <PageThumbnail
                        page={page}
                        pageIndex={idx}
                        photoIndex={photoIndex}
                        onNeedsCapture={handleNeedsCapture}
                        allThumbnails={thumbnails}
                      />
                      
                      {/* Page number badge */}
                      <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white font-medium">
                        {idx + 1}
                      </div>
                    </div>

                    {/* Action Buttons - Compact for thumbnails */}
                    <div className="p-2 bg-slate-800 flex gap-2">
                      {shouldShowAdd && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddPageAtIndex(idx + 1);
                          }}
                          className="flex-1 px-2 py-1.5 bg-green-600 hover:bg-green-500 active:bg-green-700 text-white text-xs rounded font-medium"
                          title="Add page after this one"
                        >
                          + Add
                        </button>
                      )}
                      
                      {!isSpecial ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete page ${idx + 1}?`)) {
                              onDeletePage(idx);
                            }
                          }}
                          className="flex-1 px-2 py-1.5 bg-red-600 hover:bg-red-500 active:bg-red-700 text-white text-xs rounded font-medium"
                          title="Delete this page"
                        >
                          Delete
                        </button>
                      ) : (
                        <div className="flex-1 text-[10px] text-slate-500 italic text-center py-1.5">
                          Locked
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Add Page at End */}
            <div className="mt-6 text-center pb-8">
              <button
                onClick={() => onAddPageAtIndex(pages.length)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white rounded-lg font-medium"
              >
                + Add Page at End
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const { useMemo: ReactUseMemo } = React;

const ALL_VIBES = ['people', 'scenery', 'activity', 'highlight'];
const LAYOUT = { DENSE: 'dense', ELEGANT: 'elegant' };

// Upload Progress Modal Component
function UploadProgressModal({ progress }) {
  if (!progress) return null;
  
  const getProgressMessage = () => {
    if (progress.phase === 'starting') {
      return 'Preparing album...';
    } else if (progress.phase === 'photos') {
      return `Uploading photos: ${progress.completed || 0}/${progress.total || 0}`;
    } else if (progress.phase === 'structure') {
      return 'Uploading album structure...';
    } else if (progress.phase === 'complete') {
      return 'Complete!';
    }
    return 'Processing...';
  };
  
  const getProgressPercent = () => {
    if (progress.phase === 'photos' && progress.total > 0) {
      return Math.round((progress.completed / progress.total) * 100);
    } else if (progress.phase === 'structure') {
      return 95;
    } else if (progress.phase === 'complete') {
      return 100;
    }
    return 0;
  };
  
  const percent = getProgressPercent();
  
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {/* Modal */}
      <div className="relative bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Icon */}
          <div className="mb-6">
            {progress.phase === 'complete' ? (
              <div className="w-16 h-16 mx-auto bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 mx-auto">
                <svg className="animate-spin text-blue-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}
          </div>
          
          {/* Title */}
          <h3 className="text-2xl font-bold text-white mb-2">
            {progress.phase === 'complete' ? 'Upload Complete!' : 'Uploading Album'}
          </h3>
          
          {/* Message */}
          <p className="text-slate-300 mb-6">
            {getProgressMessage()}
          </p>
          
          {/* Progress Bar */}
          {progress.phase !== 'complete' && (
            <div className="mb-4">
              <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-300 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="text-slate-400 text-sm mt-2">{percent}%</p>
            </div>
          )}
          
          {/* Details */}
          {progress.phase === 'photos' && progress.total > 0 && (
            <div className="text-sm text-slate-400 space-y-1">
              <p>Processing photo {progress.completed} of {progress.total}</p>
              <p className="text-xs text-slate-500">
                This may take a minute for large albums
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function AlbumFromPast({ 
  steps = [], 
  tripName = 'My Trip', 
  onBack,
  resolving = false,
  geoResolutionQueue = [],
  digestMeta = null,
  initialData = null,
  currentTripId = null,
  onAlbumReady = null,
  onGenerateStart = null,  // 🔹 NEW: Callback to show gathering loader
}) {

  const navigate = useNavigate(); // <-- ADD THIS LINE

  // ✅ NEW: Subscription hooks
  const { user, isAuthenticated } = useAuth();
  const { isPro, openUpgradeModal } = useSubscription();

  // 🆕 NEW: IndexedDB storage hook (replaces localStorage for large albums)
  // MUST be declared before any useEffect that uses its functions
  const { 
    saveAlbumToStorage, 
    loadAlbumFromStorage, 
    hydratePages,
    saveStatus, 
    saveProgress,
    isSaving 
  } = useAlbumStorage();

// If we came from Daily Digest, override the trip name with the digest title
  if (digestMeta && digestMeta.title) {
    tripName = digestMeta.title;
  }

  // 🔹 SMART CACHE RESTORATION
  // Priority: 1) Global cache (if from preview), 2) InitialData (if opening saved), 3) Fresh start
  useEffect(() => {
    console.log('[AlbumFromPast] Restoration check:', {
      hasInitialData: !!(initialData?.pages?.length),
      hasTripId: !!currentTripId,
      hasGlobalCache: !!(window.__vivajourPreviewCache?.pages?.length),
    });

    // PRIORITY 1: Check if we're returning from preview (use GLOBAL CACHE first)
    const loadFromPreviewCache = async () => {
      try {
        const previewReturnFlag = sessionStorage.getItem('vvd:returningFromPreview');
        if (previewReturnFlag === 'true' && currentTripId) {
          console.log('[AlbumFromPast] Detected return from preview...');
          
          // Clear the flag immediately
          sessionStorage.removeItem('vvd:returningFromPreview');
          
          // 🔹 NEW: Check global cache FIRST (most reliable)
          const globalCache = window.__vivajourPreviewCache;
          if (globalCache?.pages?.length > 0 && globalCache.tripId === currentTripId) {
            console.log('[AlbumFromPast] ✅ Restoring from GLOBAL cache:', globalCache.pages.length, 'pages');
            setAlbumPages(globalCache.pages);
            if (globalCache.vibes) setVibes(new Set(globalCache.vibes));
            if (globalCache.layout) setLayout(globalCache.layout);
            setShowControls(false);
            setHasAutoGenerated(true);
            
            // Clear the global cache after use
            window.__vivajourPreviewCache = null;
            sessionStorage.removeItem(`vvd:previewCacheId:${currentTripId}`);
            return true;
          }
          
          // Fallback: Try IndexedDB cache
          const cacheId = sessionStorage.getItem(`vvd:previewCacheId:${currentTripId}`);
          if (cacheId && cacheId !== 'global') {
            const cached = await loadAlbumFromStorage(cacheId, { hydratePhotos: true });
            
            if (cached?.pages?.length > 0) {
              console.log('[AlbumFromPast] ✅ Restoring from IndexedDB preview cache');
              setAlbumPages(cached.pages);
              if (cached.vibes) setVibes(new Set(cached.vibes));
              if (cached.layout) setLayout(cached.layout);
              setShowControls(false);
              setHasAutoGenerated(true);
              
              // Clean up
              sessionStorage.removeItem(`vvd:previewCacheId:${currentTripId}`);
              return true;
            }
          }
          
          // Fallback: try old sessionStorage cache (for migration)
          const oldCacheKey = `vvd:albumCache:${currentTripId}`;
          const oldCached = sessionStorage.getItem(oldCacheKey);
          if (oldCached) {
            try {
              const saved = JSON.parse(oldCached);
              if (saved.pages?.length > 0) {
                console.log('[AlbumFromPast] ✅ Restoring from legacy sessionStorage cache');
                setAlbumPages(saved.pages);
                if (saved.vibes) setVibes(new Set(saved.vibes));
                if (saved.layout) setLayout(saved.layout);
                setShowControls(false);
                setHasAutoGenerated(true);
                sessionStorage.removeItem(oldCacheKey);
                return true;
              }
            } catch {}
            sessionStorage.removeItem(oldCacheKey);
          }
        }
      } catch (err) {
        console.warn('[AlbumFromPast] Cache restoration failed:', err);
      }
      return false;
    };
    
    // Execute async load
    loadFromPreviewCache().then(restored => {
      if (restored) return;
      
      // PRIORITY 2: Load from initialData (opening a saved album)
      if (initialData && initialData.pages && initialData.pages.length > 0) {
        console.log('[AlbumFromPast] ✅ Loading saved album from initialData');
        setAlbumPages(initialData.pages);
        
        if (initialData.vibes) setVibes(new Set(initialData.vibes));
        if (initialData.layout) setLayout(initialData.layout);
        
        setShowControls(false);
        setHasAutoGenerated(true);
        return;
      }

      // PRIORITY 3: No data to restore - start fresh
      console.log('[AlbumFromPast] Starting fresh - no saved data');
      setAlbumPages([]);
      setShowControls(true);
      setHasAutoGenerated(false);
    });
  }, [initialData, currentTripId, loadAlbumFromStorage]);

  // 1) Steps to include
  // 1) Steps to include
  // Initialize excluded set with steps that have no geocoded city
const [excludedStepIds, setExcluded] = useState(new Set());

// Track which steps have city data (for detecting manual additions)
const stepsWithCityRef = useRef(new Set());
const initialExclusionDone = useRef(false);

// Effect 1: Initial auto-exclude of steps without city data
useEffect(() => {
  if (initialExclusionDone.current || !steps || steps.length === 0) return;
  
  const stepsWithoutCity = [];
  const stepsWithCity = new Set();
  
  steps.forEach(step => {
    const addr = step.address || {};
    const hasCity = !!(addr.city || addr.town || addr.village || addr.municipality);
    
    if (hasCity) {
      stepsWithCity.add(step.id);
    } else {
      stepsWithoutCity.push(step.id);
    }
  });
  
  // Store initial state of which steps have city data
  stepsWithCityRef.current = stepsWithCity;
  
  if (stepsWithoutCity.length > 0) {
    console.log(`[AlbumFromPast] Auto-excluding ${stepsWithoutCity.length} steps without geocoded city`);
    setExcluded(new Set(stepsWithoutCity));
  }
  
  initialExclusionDone.current = true;
}, [steps]);

// Effect 2: Auto-INCLUDE steps when user manually adds location
useEffect(() => {
  // Skip if initial exclusion hasn't run yet
  if (!initialExclusionDone.current || !steps || steps.length === 0) return;
  
  const newlyGainedCity = [];
  
  steps.forEach(step => {
    const addr = step.address || {};
    const hasCity = !!(addr.city || addr.town || addr.village || addr.municipality);
    
    // Check if this step JUST gained city data (wasn't in our tracked set before)
    if (hasCity && !stepsWithCityRef.current.has(step.id)) {
      newlyGainedCity.push(step.id);
      stepsWithCityRef.current.add(step.id); // Update our tracking
    }
  });
  
  // Auto-include any steps that just gained city data
  if (newlyGainedCity.length > 0) {
    console.log(`[AlbumFromPast] Auto-including ${newlyGainedCity.length} steps that gained location:`, newlyGainedCity);
    
    setExcluded(prev => {
      const next = new Set(prev);
      newlyGainedCity.forEach(id => next.delete(id));
      return next;
    });
  }
}, [steps]);
  
  // Helper function to merge consecutive same-city steps
  const mergeConsecutiveCitySteps = useCallback((stepsList) => {
    if (!stepsList || stepsList.length === 0) return [];
    
    const merged = [];
    let currentGroup = null;
    
    for (const step of stepsList) {
      const addr = step.address || {};
      const city = (addr.city || addr.town || addr.village || step.location || '').trim().toLowerCase();
      
      if (currentGroup && currentGroup.city === city && city) {
        // Same city - merge into current group
        currentGroup.photos = [...(currentGroup.photos || []), ...(step.photos || [])];
        currentGroup.mergedIds.push(step.id);
        // Track BOTH earliest and latest dates for merged steps
        // - date/startDate: earliest (when first visited) - needed for trip start
        // - endDate: latest (when last visited) - needed for trip end
        if (step.date) {
          if (!currentGroup.startDate || step.date < currentGroup.startDate) {
            currentGroup.startDate = step.date;
            currentGroup.date = step.date; // Keep date as earliest for backwards compatibility
          }
          if (!currentGroup.endDate || step.date > currentGroup.endDate) {
            currentGroup.endDate = step.date;
          }
        }
      } else {
        // Different city - start new group
        if (currentGroup) {
          merged.push(currentGroup);
        }
        currentGroup = {
          ...step,
          city: city,
          mergedIds: [step.id], // Track which original steps were merged
          photos: [...(step.photos || [])],
          startDate: step.date || null, // Track earliest date
          endDate: step.date || null,   // Track latest date
        };
      }
    }
    
    // Don't forget the last group
    if (currentGroup) {
      merged.push(currentGroup);
    }
    
    return merged;
  }, []);
  
  // 🔹 NEW: Filter steps by digestMeta.selectedDates if in digest mode
  const dateFilteredSteps = useMemo(() => {
    // If not in digest mode, use all steps
    if (!digestMeta || !digestMeta.selectedDates || digestMeta.selectedDates.length === 0) {
      return steps;
    }

    // Filter steps to only include those matching the selected dates (UTC format)
    const selectedDatesSet = new Set(digestMeta.selectedDates);
    
    const filtered = steps.filter(step => {
      const stepDate = step.date; // Format: YYYY-MM-DD (UTC)
      return stepDate && selectedDatesSet.has(stepDate);
    });
    
    console.log(`[Date Filter] Selected: ${JSON.stringify(digestMeta.selectedDates)} | Found ${filtered.length}/${steps.length} matching steps`);
    
    return filtered;
  }, [steps, digestMeta]);
  
  // First filter excluded steps, then merge consecutive same-city steps
  // Merge all steps for display (don't filter by exclusion)
  const mergedSteps = useMemo(() => {
    return mergeConsecutiveCitySteps(dateFilteredSteps);
  }, [dateFilteredSteps, mergeConsecutiveCitySteps]);
  
  // Filter only for album generation (excluded steps removed)
  const includedSteps = useMemo(() => {
    return mergedSteps.filter(step => {
      // A merged step is included if NONE of its merged IDs are excluded
      return !step.mergedIds?.some(id => excludedStepIds.has(id));
    });
  }, [mergedSteps, excludedStepIds]);
// Build unique option lists for the Home pickers
const uniqueStates = useMemo(() => {
  const set = new Set();
  for (const s of steps) {
    const st =
      (s.state || s.address?.state || s.address?.region || '').trim();
    if (st) set.add(st);
  }
  return [...set].sort();
}, [steps]);

const uniqueCountries = useMemo(() => {
  const set = new Set();
  for (const s of steps) {
    const c =
      (s.country || s.address?.country || '').trim();
    if (c) set.add(c);
  }
  return [...set].sort();
}, [steps]);

// Lightweight global suggestions (users can still type any country)
const COMMON_COUNTRIES = [
  'United States', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Chile',
  'United Kingdom', 'Ireland', 'France', 'Spain', 'Portugal', 'Italy',
  'Germany', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Czechia',
  'Poland', 'Greece', 'Turkey', 'Egypt', 'Morocco', 'South Africa',
  'United Arab Emirates', 'India', 'Thailand', 'Vietnam', 'Japan', 'South Korea',
  'Australia', 'New Zealand'
];
const countrySuggestions = useMemo(
  
  () => Array.from(new Set([...COMMON_COUNTRIES, ...uniqueCountries])).sort(),
  [uniqueCountries]
  
);
// ---- US states support for the Home "State" field ----
const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia',
  'Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts',
  'Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey',
  'New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia',
  'Washington','West Virginia','Wisconsin','Wyoming','District of Columbia'
];

function isUSA(name) {
  const s = String(name || '').trim().toLowerCase();
  return s === 'usa' || s === 'u.s.a.' || s === 'united states' ||
         s === 'united states of america' || s === 'us' || s === 'u.s.';
}

// If country is USA, show the full US state list; otherwise fall back to states seen in steps



  // Step 0 (Home)
  const [includeHome, setIncludeHome] = useState(() => !digestMeta);
  const [homeState, setHomeState] = useState('');
  const [homeCountry, setHomeCountry] = useState('');
  // Home coordinates for distance calculation
  const [homeCoords, setHomeCoords] = useState(null); // { lat, lng }
  // show/hide dropdown menus for the custom comboboxes
const [showStateMenu, setShowStateMenu] = useState(false);
const [showCountryMenu, setShowCountryMenu] = useState(false);

// Geocode home location when state/country changes
useEffect(() => {
  if (!includeHome || (!homeState && !homeCountry)) {
    setHomeCoords(null);
    return;
  }
  
  const query = [homeState, homeCountry].filter(Boolean).join(', ');
  if (!query) return;
  
  // Debounce geocoding to avoid too many requests
  const timer = setTimeout(async () => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&limit=1`;
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (resp.ok) {
        const data = await resp.json();
        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          console.log(`[Home Geocode] "${query}" → lat: ${lat}, lng: ${lon}`);
          setHomeCoords({ lat: parseFloat(lat), lng: parseFloat(lon) });
        } else {
          console.warn(`[Home Geocode] No results for "${query}"`);
          setHomeCoords(null);
        }
      }
    } catch (e) {
      console.warn('[Home Geocode] Error:', e);
    }
  }, 500); // 500ms debounce
  
  return () => clearTimeout(timer);
}, [includeHome, homeState, homeCountry]);

  const stateSuggestions = useMemo(
  () => (isUSA(homeCountry) ? US_STATES : uniqueStates),
  [homeCountry, uniqueStates]
);
  function makeHomeStep() {
    const step = {
      id: '_home',
      _key: '_home',
      isHome: true,
      title: 'Home',
      date: '',
      location: '',
      address: (homeState || homeCountry)
        ? `${homeState}${homeState && homeCountry ? ', ' : ''}${homeCountry}`
        : '',
      state: homeState || '',
      country: homeCountry || '',
      photos: [],
    };
    
    // Add coordinates if we have them (needed for mileage calculation)
    if (homeCoords && homeCoords.lat && homeCoords.lng) {
      step.lat = homeCoords.lat;
      step.lng = homeCoords.lng;
    }
    
    return step;
  }
  const stepsForAnalysis = useMemo(
    () => (includeHome ? [makeHomeStep(), ...includedSteps] : includedSteps),
    [includeHome, includedSteps, homeState, homeCountry, homeCoords]
  );

  // 2) Vibes & Likes
  const [vibes, setVibes] = useState(() => new Set(['people']));
  const [likesOnly, setLikesOnly] = useState(false);
  const [selectionMode, setSelectionMode] = useState('diverse'); // 'diverse' or 'vibes'

  // 3) Layout choice
  const [layout, setLayout] = useState(LAYOUT.ELEGANT);

  // Worker & analysis
  const { isReady, vibesAvailable, isAnalyzing, results, analyzePhotos, modelStatuses } = useSmartVibes();

  const [vibesBusy, setVibesBusy] = useState(false);
  const prevIsAnalyzing = useRef(isAnalyzing);
  const generateRequestedRef = useRef(false);



  // Normalizes URLs so worker output keys match our photo URLs 1:1
const normalize = (u) => {
  try {
    const url = new URL(u, window.location.origin);
    url.search = '';               // strip cache-busters if any
    return url.href;
  } catch {
    return (u || '').replace(/\?.*$/, '');
  }
};

// Map worker array -> { [url]: { ...result, scores } }
const resultsByUrl = useMemo(() => {
  if (!Array.isArray(results) || results.length === 0) return null;
  const map = {};
  for (const r of results) {
    if (!r || !r.url) continue;
    const key = normalize(r.url);
    map[key] = { ...r, url: key, scores: r.scores || r.score || r.vibes || null };
  }
  return map;
}, [results]);


  // Flatten selection -> photos to score
  const allCandidatePhotos = useMemo(() => {
    const pruned = [];
    for (const step of includedSteps) {
      for (const p of step.photos || []) {
        if (likesOnly && !(p.likes > 0)) continue;
        pruned.push({ ...p, url: normalize(p.url) });

      }
    }
    return pruned;
  }, [includedSteps, likesOnly]);

  // Add geocoding progress detection
const geocodingInProgress = resolving || geoResolutionQueue.length > 0;

// src/album/AlbumFromPast.jsx

const isDigestFlow = !!digestMeta;
const canGenerate =
  isReady &&
  allCandidatePhotos.length > 0 &&
  // In digest mode, don't block on geocoding - we check stepsHaveAddressData separately
  (isDigestFlow ? true : !geocodingInProgress) &&
  (
    isDigestFlow
      ? true
      : (selectionMode === 'vibes' ? vibes.size > 0 : true) // only enforce vibes when in 'vibes' mode
  ) &&
  !vibesBusy;


  // Album state
  const [albumPages, setAlbumPages] = useState([]); // [{type, photos, meta, stepId?}]

  const [currentAlbumId, setCurrentAlbumId] = useState(null);
  const [currentAlbumMeta, setCurrentAlbumMeta] = useState(null);

  // Start a new album session whenever a fresh album is generated
  const beginAlbumSession = useCallback(
    (mode) => {
      const baseTitle = tripName || 'My Trip';

      let suffix = '';
      if (digestMeta) {
        suffix = '_Digest';
      } else if (mode === 'vibes') {
        suffix = '_Vibe';
      } else {
        // default: diverse/manual
        suffix = '_Diverse';
      }

      const now = Date.now();
      const id = `alb-${now.toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

      const meta = {
        id,
        title: `${baseTitle}${suffix}`,
        tripName: baseTitle,
        selectionMode: mode,
        digest: !!digestMeta,
        createdAt: now,
        updatedAt: now,
      };

      setCurrentAlbumId(id);
      setCurrentAlbumMeta(meta);

      return { id, meta };
    },
    [tripName, digestMeta]
  );


  // 🆕 UPDATED: Save album cache for preview using GLOBAL VARIABLE (simpler, more reliable)
  // 🔹 FIX: Bypasses IndexedDB complexity - just keep state in memory during navigation
  const saveAlbumCacheForPreview = useCallback(async () => {
    if (!albumPages || !albumPages.length || !currentTripId) return;
    
    try {
      // 🔹 SIMPLE FIX: Store album state in global variable
      // This survives navigation within the same app session
      window.__vivajourPreviewCache = {
        pages: albumPages,
        vibes: Array.from(vibes || []),
        layout,
        tripId: currentTripId,
        timestamp: Date.now()
      };
      
      // Also set the return flag
      sessionStorage.setItem('vvd:returningFromPreview', 'true');
      sessionStorage.setItem(`vvd:previewCacheId:${currentTripId}`, 'global'); // marker
      
      console.log('[AlbumFromPast] 💾 Cached album in global variable for preview return');
      console.log('[AlbumFromPast] 📊 Cache contains', albumPages.length, 'pages');
    } catch (err) {
      console.warn('[AlbumFromPast] Failed to cache album for preview:', err);
    }
  }, [albumPages, currentTripId, vibes, layout]);

  // 🔹 CLEANUP: Remove legacy cache key on mount (migration)
  useEffect(() => {
    try {
      const legacyKey = 'vvd:lastAlbumPages';
      if (sessionStorage.getItem(legacyKey)) {
        sessionStorage.removeItem(legacyKey);
        console.log('[AlbumFromPast] 🧹 Removed legacy cache key');
      }
    } catch (err) {
      // Non-critical, ignore
    }
  }, []);

  // 🆕 NEW: Auto-save to IndexedDB (replaces localStorage - no more freezing!)
  // Debounced: waits 3 seconds of inactivity before saving
  useEffect(() => {
    if (!currentAlbumId || !albumPages?.length) return;
    
    console.log('[AlbumFromPast] 📝 Queueing auto-save to IndexedDB...');
    
    saveAlbumToStorage(currentAlbumId, {
      pages: albumPages,
      tripName: tripName || 'My Trip',
      vibes: Array.from(vibes || []),
      layout,
      title: currentAlbumMeta?.title || tripName || 'My Trip',
      selectionMode: currentAlbumMeta?.selectionMode,
      digest: currentAlbumMeta?.digest,
      createdAt: currentAlbumMeta?.createdAt
    }, {
      debounceMs: 3000, // Wait 3 seconds of inactivity before saving
      onProgress: (progress) => {
        if (progress.phase === 'photos') {
          console.log(`[AutoSave] Storing photos: ${progress.done}/${progress.total}`);
        }
      }
    }).then(() => {
      console.log('[AlbumFromPast] ✅ Auto-saved to IndexedDB');
    }).catch(err => {
      console.warn('[AlbumFromPast] ⚠️ Auto-save failed:', err);
    });
  }, [albumPages, currentAlbumId, tripName, vibes, layout, saveAlbumToStorage, currentAlbumMeta]);

  /* 🔹 DEPRECATED: Old localStorage auto-save (replaced by IndexedDB above)
  useEffect(() => {
    if (!currentAlbumId || !currentAlbumMeta) return;
    if (!albumPages || albumPages.length === 0) return;

    try {
      const now = Date.now();
      const fullPayload = {
        ...currentAlbumMeta,
        updatedAt: now,
        pages: albumPages,
      };
      localStorage.setItem(`vvd:album:item:${currentAlbumId}`, JSON.stringify(fullPayload));
      // ... rest of old localStorage code ...
    } catch (err) {
      console.warn('[AlbumFromPast] Failed to save album to library:', err);
    }
  }, [albumPages, currentAlbumId, currentAlbumMeta]);
  */
  

    // 🔹 Auto-save album as DRAFT whenever pages change (dispatch event for other listeners)
  useEffect(() => {
    if (!albumPages || albumPages.length === 0) return;

    try {
      const albumData = {
        pages: albumPages,
        trip: {
          name: tripName || 'My Trip',
          stepCount: Array.isArray(steps) ? steps.length : 0,
        },
        v: 1,
        savedAt: Date.now(),
        isDraft: true,   // 🔹 NEW: Mark as draft
        isSaved: false,  // 🔹 NEW: Not explicitly saved yet
      };

      window.dispatchEvent(
        new CustomEvent('app:autoSaveAlbum', {
          detail: { albumData },
        })
      );
    } catch (err) {
      console.warn('[AlbumFromPast] Failed to dispatch auto-save event:', err);
    }
  }, [albumPages, tripName, steps]);



  const [showShareSheet, setShowShareSheet] = useState(false);
const [shareUrl, setShareUrl] = useState('');

  // Busy flags for button UX
  const [shareBusy, setShareBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // ← ADD THIS LINE
  const [albumSaved, setAlbumSaved] = useState(false); // 🔹 NEW: Track if album has been explicitly saved

  const [showSharedLinks, setShowSharedLinks] = useState(false); // 🆕 Shared links modal


    // Cache of the last previewed payload + its in-flight prewarm
  const previewPayloadRef = useRef(null);
  const prewarmPromiseRef = useRef(null);
  
  // NEW: Cache for instant re-preview of unchanged albums
  const lastPreviewHashRef = useRef(null);      // The Firebase hash from last preview
  const lastAlbumFingerprintRef = useRef(null); // Fingerprint to detect changes

  // 🆕 NEW: Explicitly save album to IndexedDB (replaces localStorage)
  const handleSaveAlbum = useCallback(async () => {
    if (!albumPages || albumPages.length === 0) {
      showToast('No pages to save yet', { background: '#dc2626', icon: '⚠️' });
      return;
    }

    try {
      // Generate album ID if not exists
      const albumId = currentAlbumId || `album_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      
      console.log('[AlbumFromPast] 💾 Saving album to IndexedDB:', albumId);
      
      const albumDataToSave = {
        pages: albumPages,
        tripName: tripName || 'My Trip',
        vibes: Array.from(vibes || []),
        layout,
        title: tripName || 'My Trip',
        selectionMode: currentAlbumMeta?.selectionMode || selectionMode,
        isSaved: true,
        savedAt: Date.now()
      };
      
      // 1. Save via hook (handles photo storage + debouncing)
      await saveAlbumToStorage(albumId, albumDataToSave, { 
        debounceMs: 0 // Save immediately
      });
      
      // 2. 🆕 Also save to standalone album store in IndexedDB (for "My Albums" list)
      try {
        await saveAlbum(albumId, albumDataToSave);
        console.log('[AlbumFromPast] ✅ Album also saved to standalone album store');
      } catch (albumStoreErr) {
        console.warn('[AlbumFromPast] ⚠️ Standalone album store save failed (non-critical):', albumStoreErr);
      }
      
      // Update album ID if it was newly created
      if (!currentAlbumId) {
        setCurrentAlbumId(albumId);
      }
      
      setAlbumSaved(true);
      
      // Show success toast
      showToast('✓ Album saved!', { 
        background: '#059669', 
        icon: '💾',
        duration: 2000 
      });
      
      // 3. Also dispatch event for trip storage (backward compatibility)
      window.dispatchEvent(
        new CustomEvent('app:autoSaveAlbum', {
          detail: { 
            albumData: {
              pages: albumPages,
              trip: { name: tripName || 'My Trip', stepCount: steps?.length || 0 },
              isSaved: true
            }
          },
        })
      );
      
    } catch (err) {
      console.error('[AlbumFromPast] ❌ Failed to save album:', err);
      showToast('Failed to save album. Please try again.', { 
        background: '#dc2626', 
        icon: '✕' 
      });
    }
  }, [albumPages, tripName, steps, currentAlbumId, vibes, layout, saveAlbumToStorage, currentAlbumMeta, selectionMode]);

  // Share handler - now properly scoped within component
  // Share handler - generates Story links (auto-playing mode)// Share handler - generates compressed Story links
    // Replace your handleShareAlbumClick with this version:

const handleShareAlbumClick = useCallback(async () => {
  console.group('🔍 === SHARE DIAGNOSTIC === 🔍');
  setShareBusy(true);
  setUploadProgress({ phase: 'starting', completed: 0, total: 0 }); // ← NEW

  // ✅ Subscription-aware share messaging
  if (!isPro) {
    // Check if native app (iOS/Android)
    const isNative = !!window?.Capacitor?.isNativePlatform?.();
    
    if (isNative) {
      // Native: Simple confirm with sync mention (App Store compliant)
      const proceed = window.confirm(
        '📤 Share Your Album\n\n' +
        'Your share link will be active for 3 days.\n\n' +
        'Already a Pro member? Use "Sync Subscription" in settings to restore your access.\n\n' +
        'Continue with 3-day link?'
      );
      if (!proceed) {
        setShareBusy(false);
        setUploadProgress(null);
        console.log('User cancelled share');
        console.groupEnd();
        return;
      }
    } else {
      // Web: Show upgrade modal (non-blocking) - share continues below
      openUpgradeModal('share-link');
    }
  }
  
  try {
    // Step 1: Check input
    console.log('📥 Step 1: Input check');
    console.log('   albumPages:', albumPages?.length, 'pages');
    
    if (!albumPages || albumPages.length === 0) {
      console.error('❌ No album pages to share!');
      alert('No pages to share yet. Please generate the album first.');
      console.groupEnd();
      return;
    }
    
    // 🆕 Step 1.5: Hydrate photos from IndexedDB if needed
    let pagesToProcess = albumPages;
    const needsHydration = albumPages.some(p => p.photoIds && (!p.photos || p.photos.length === 0));
    if (needsHydration) {
      console.log('🔄 Hydrating photos from IndexedDB...');
      setUploadProgress({ phase: 'hydrating', completed: 0, total: albumPages.length });
      try {
        pagesToProcess = await hydratePages(albumPages);
        console.log('   ✅ Photos hydrated');
      } catch (err) {
        console.warn('   ⚠️ Hydration failed, using original pages:', err);
        pagesToProcess = albumPages;
      }
    }
    
    // Step 2: Build flipbook
    console.log('📦 Step 2: Building flipbook...');
    
    // Apply 50-page limit for sharing
    const { selectedPages, wasLimited, originalCount, selectedCount } = selectPagesForShare(pagesToProcess || []);
    if (wasLimited) {
      console.log(`📋 [Share] Limited from ${originalCount} to ${selectedCount} pages for optimal sharing`);
      showToast(`Sharing ${selectedCount} highlights from your ${originalCount}-page album`, {
        icon: '✨',
        background: '#6366f1', // Indigo
        duration: 4000
      });
    }
    
    const { pages, photos } = toFlipbook(selectedPages);
    
    console.log('   ✅ Flipbook built');
    console.log('   📄 Pages:', pages?.length);
    console.log('   🖼️  Photos:', photos?.length);

    if (!pages || pages.length === 0) {
      console.error('❌ toFlipbook returned no pages!');
      alert('No pages to share yet. Please generate the album first.');
      console.groupEnd();
      return;
    }

    // Step 3: Calculate story duration
    console.log('⏱️  Step 3: Calculate duration');
    const durationMs = pages.reduce((total, page) => {
      const baseTime = 3000;
      const hasText = page.caption || page.meta?.caption;
      return total + baseTime + (hasText ? 2000 : 0);
    }, 0);
    const durationText = formatStoryDuration(durationMs);
    console.log('   Duration:', durationText);

    // Step 4: Build trip metadata
    console.log('🗺️  Step 4: Build trip metadata');
    const tripMeta = {
      name: typeof tripName !== 'undefined' ? tripName : 'My Travel Story',
      dates:
        includedSteps.length > 0
          ? `${includedSteps[0]?.date || ''} - ${
              includedSteps[includedSteps.length - 1]?.date || ''
            }`
          : '',
      route: includedSteps
        .map((s) => s.location || s.city || '')
        .filter(Boolean)
        .join(' → '),
    };
    console.log('   Trip:', tripMeta.name);

    // Step 4.5: Optimize photos BEFORE creating payload ← NEW
    console.log('🎨 Step 4.5: Optimizing photos...');
    const optimizedPages = await optimizeAllPhotosInPages(pages);
    const optimizedPhotos = await optimizePhotosArray(photos);
    
    // Step 5: Create payload
    console.log('📦 Step 5: Create payload');
    const payload = { 
      pages: optimizedPages, 
      photos: optimizedPhotos,
      trip: tripMeta, 
      v: 1 
    };
    
    // Track payload size before upload
    const payloadJson = JSON.stringify(payload);
    const payloadSizeMB = (payloadJson.length / (1024 * 1024)).toFixed(2);
    const photoCount = optimizedPages.reduce((sum, p) => sum + (p.items?.length || 0), 0);
    console.log(`📊 [Payload Stats] Size: ${payloadSizeMB} MB | Photos: ${photoCount} | Pages: ${optimizedPages.length}`);
    
    

        // Step 6: Use cached preview + prewarmed upload for instant share
    console.log('🔗 Step 6: Using cached preview (if available)…');

    // Prefer the exact payload the user just previewed; fallback to the freshly-built one
    const payloadForShare = previewPayloadRef.current || payload;

    // If prewarming is still in flight, let it finish (dedupes work)
    if (prewarmPromiseRef.current) {
      try { await prewarmPromiseRef.current; } catch (_) {}
    }

    // Small UX hint: we are finalizing the link
    setUploadProgress({
      phase: 'finalizing',
      completed: 95,
      total: 100,
      message: 'Finalizing share link…'
    });

    // Ask the cache/uploader for the hash (instant if already cached)
const uploadResult = await getOrCreateUpload(payloadForShare, 'story');

// Support multiple return shapes from shareCache:
// - { hash, shareId, photoCount, storagePaths, ... }
// - { hash }
// - "hash" (string)
const hash =
  (uploadResult && typeof uploadResult === 'object' && uploadResult.hash)
    ? uploadResult.hash
    : (typeof uploadResult === 'string' ? uploadResult : null);

if (!hash) {
  console.warn('[AlbumFromPast] Share failed: missing hash from getOrCreateUpload()', uploadResult);
  setUploadProgress(null);
  setShareBusy(false);
  return;
}

// ✅ FIX: Use production URL on Android/Capacitor/localhost instead of window.location.origin
const getShareBaseUrl = () => {
  const origin = window.location.origin || '';

  // Android emulator / Capacitor / local dev cases
  if (
    origin.includes('localhost') ||
    origin.includes('127.0.0.1') ||
    origin.includes('10.0.2.2') ||
    origin.startsWith('capacitor://')
  ) {
    return 'https://travel-app-2-five.vercel.app';
  }

  return origin;
};

// Build a clean story viewer link
const shareLink = `${getShareBaseUrl()}/story#${hash}`;
console.log('🔗 Generated share link:', shareLink);

// Extract shareId for deletion.
// Prefer uploadResult.shareId (if shareCache returns it)
// Fallback: parse from v2 hash format: vvd2_<shareId>.<key>.<iv> (or S-<shareId>.<...>)
let shareId =
  (uploadResult && typeof uploadResult === 'object' && uploadResult.shareId)
    ? uploadResult.shareId
    : null;

if (!shareId && typeof hash === 'string') {
  try {
    const withoutPrefix = hash.replace(/^vvd2_/, '').replace(/^S-/, '');
    shareId = (withoutPrefix.split('.')[0] || '').trim() || null;
  } catch (_) {
    shareId = null;
  }
}

// Save a local record so SharedLinksManager can list/deactivate links later
try {
  const tripId = sessionStorage.getItem('vvd:currentTripId') || null;

  // IMPORTANT: use the already-computed `photoCount` from earlier in this handler.
  // (Do NOT redeclare `const photoCount = ...` here.)
  const recordPhotoCount =
    (uploadResult && typeof uploadResult === 'object' && typeof uploadResult.photoCount === 'number')
      ? uploadResult.photoCount
      : (typeof photoCount === 'number' ? photoCount : 0);

  const storagePaths =
    (uploadResult && typeof uploadResult === 'object' && Array.isArray(uploadResult.storagePaths))
      ? uploadResult.storagePaths
      : [];

  if (shareId) {
    await saveShareRecord({
      shareId, // must be the folder id, not the hash
      albumTitle: tripName || payloadForShare?.trip?.name || 'Untitled Album',
      shareUrl: shareLink,
      createdAt: Date.now(),
      photoCount: recordPhotoCount || 0,
      tripId,
      storagePaths,
      mode: 'story',
    });
    console.log('[AlbumFromPast] ✅ Share record saved:', shareId);
  } else {
    console.warn('[AlbumFromPast] Share record not saved (missing shareId). Hash:', hash);
  }
} catch (err) {
  console.warn('[AlbumFromPast] Failed to save share record:', err);
}

// Copy to clipboard (best-effort) and surface your existing share sheet
try { await navigator.clipboard?.writeText(shareLink); } catch (_) {}
setShareUrl(shareLink);
setShowShareSheet(true);


    setUploadProgress(null);
    console.log('   ✅ Shared successfully (cached)');

    
    console.groupEnd();
   
  } catch (err) {
    console.error('❌ Share error:', err);
    setUploadProgress(null); // ← NEW: Clear progress on error
    alert('Failed to share: ' + err.message);
  } finally {
    setShareBusy(false);
  }
}, [albumPages, tripName, includedSteps, hydratePages, isPro, openUpgradeModal]);




      // Preview Story handler - opens story in new tab WITHOUT sharing
  // Preview Story handler - opens story in new tab WITHOUT sharing
// Preview Story handler - opens story in new tab WITHOUT sharing
const handlePreviewStory = useCallback(async () => {
  setPreviewBusy(true);
  setUploadProgress({ phase: 'starting', completed: 0, total: 0 }); // ← NEW
  
  // Let React render the spinner first
  await new Promise(resolve => setTimeout(resolve, 0));
  
  try {
    // 🆕 Hydrate photos from IndexedDB if needed
    let pagesToProcess = albumPages;
    const needsHydration = albumPages?.some(p => p.photoIds && (!p.photos || p.photos.length === 0));
    if (needsHydration) {
      console.log('🔄 Hydrating photos from IndexedDB for preview...');
      setUploadProgress({ phase: 'hydrating', completed: 0, total: albumPages.length });
      try {
        pagesToProcess = await hydratePages(albumPages);
        console.log('   ✅ Photos hydrated for preview');
      } catch (err) {
        console.warn('   ⚠️ Hydration failed, using original pages:', err);
        pagesToProcess = albumPages;
      }
    }
    
    // =====================================================
    // CACHE CHECK: Skip expensive work if album unchanged
    // =====================================================
    const currentFingerprint = createAlbumFingerprint(pagesToProcess);
    const cachedHash = lastPreviewHashRef.current;
    const cachedFingerprint = lastAlbumFingerprintRef.current;
    
    if (cachedHash && cachedFingerprint && currentFingerprint === cachedFingerprint) {
      console.log('⚡ [Preview Cache HIT] Album unchanged, reusing cached preview');
      console.log('   Cached hash:', cachedHash.substring(0, 20) + '...');
      
      // Skip all expensive work - just navigate!
      // ✅ FIX: Use production URL on Android/localhost
      const baseUrl = (window.location.origin || '').includes('localhost') || 
                      (window.location.origin || '').includes('127.0.0.1') || 
                      (window.location.origin || '').includes('10.0.2.2')
                        ? 'https://travel-app-2-five.vercel.app' 
                        : window.location.origin;
      const storyUrl = `${baseUrl}/story#${cachedHash}`;
      const platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : 'web';
      
      // Save context for return navigation
      try {
        const tripId = sessionStorage.getItem('vvd:currentTripId');
        sessionStorage.setItem('vvd:returnContext', JSON.stringify({
          view: 'past',
          albumFromPastOpen: true,
          tripName: tripName || '',
          currentTripId: tripId,
          timestamp: Date.now()
        }));
        sessionStorage.setItem('vvd:returningFromPreview', 'true');
      } catch (e) {
        console.warn('[Preview Cache] Failed to save context:', e);
      }
      
      if (platform === 'web') {
        window.open(storyUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(`/story#${cachedHash}`);
      }
      
      setUploadProgress(null);
      setPreviewBusy(false);
      return; // Exit early - no expensive work needed!
    }
    
    console.log('🎬 Building your story preview...');
    if (cachedFingerprint) {
      console.log('   (Album changed since last preview, regenerating...)');
    }
    // =====================================================
    
    // Apply 50-page limit for preview/sharing
    const { selectedPages, wasLimited, originalCount, selectedCount } = selectPagesForShare(pagesToProcess || []);
    if (wasLimited) {
      console.log(`📋 [Preview] Limited from ${originalCount} to ${selectedCount} pages for optimal sharing`);
      showToast(`Previewing ${selectedCount} highlights from your ${originalCount}-page album`, {
        icon: '✨',
        background: '#6366f1', // Indigo
        duration: 4000
      });
    }
    
    const { pages, photos } = toFlipbook(selectedPages); // ← ADD photos
    
    if (!pages.length) {
      alert('No pages to preview yet. Please generate the album first.');
      setPreviewBusy(false);
      setUploadProgress(null); // ← NEW
      return;
    }

    console.log('📤 Preparing preview...');
    const tripMeta = {
      name: typeof tripName !== 'undefined' ? tripName : 'My Travel Story',
      dates:
        includedSteps.length > 0
          ? `${includedSteps[0]?.date || ''} - ${
              includedSteps[includedSteps.length - 1]?.date || ''
            }`
          : '',
      route: includedSteps
        .map((s) => s.location || s.city || '')
        .filter(Boolean)
        .join(' → '),
    };

    // Optimize photos BEFORE creating payload ← NEW
    console.log('🎨 Optimizing photos for preview...');
    const optimizedPages = await optimizeAllPhotosInPages(pages);
    const optimizedPhotos = await optimizePhotosArray(photos);

    // DIAGNOSTIC: Check what's actually in the arrays
    console.log('🔍 [DIAGNOSTIC] Checking payload contents...');
    
    // Check a sample photo to see what fields it has
    if (optimizedPhotos && optimizedPhotos.length > 0) {
      const samplePhoto = optimizedPhotos[0];
      console.log('   Sample photo fields:', Object.keys(samplePhoto));
      console.log('   Sample photo url length:', samplePhoto.url?.length || 0);
      console.log('   Sample photo src length:', samplePhoto.src?.length || 0);
      if (samplePhoto.url && samplePhoto.src && samplePhoto.url !== samplePhoto.src) {
        console.warn('   ⚠️  WARNING: Photo has BOTH url and src with different values!');
      }
      
      // Check for hidden large fields
      Object.keys(samplePhoto).forEach(key => {
        const val = samplePhoto[key];
        if (val && typeof val === 'string' && val.length > 10000) {
          console.warn(`   ⚠️  Large field found: "${key}" = ${(val.length / 1024).toFixed(1)} KB`);
        }
        if (val && typeof val === 'object') {
          const valJson = JSON.stringify(val);
          if (valJson.length > 10000) {
            console.warn(`   ⚠️  Large object found: "${key}" = ${(valJson.length / 1024).toFixed(1)} KB`);
          }
        }
      });
    }
    
    // Check sample page for hidden bloat
    if (optimizedPages && optimizedPages.length > 0) {
      const samplePage = optimizedPages[0];
      console.log('   Sample page fields:', Object.keys(samplePage));
      
      // Check for large fields in page
      Object.keys(samplePage).forEach(key => {
        const val = samplePage[key];
        if (val && typeof val === 'string' && val.length > 10000) {
          const sizeKB = (val.length / 1024).toFixed(1);
          const isImage = val.startsWith('data:image');
          const isPNG = val.startsWith('data:image/png');
          console.warn(`   ⚠️  Large page field: "${key}" = ${sizeKB} KB ${isImage ? '(IMAGE!)' : ''} ${isPNG ? '[PNG - likely a map!]' : ''}`);
        }
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          const valJson = JSON.stringify(val);
          if (valJson.length > 10000) {
            console.warn(`   ⚠️  Large page object: "${key}" = ${(valJson.length / 1024).toFixed(1)} KB`);
          }
        }
      });
      
      // Check meta object specifically
      if (samplePage.meta) {
        const metaJson = JSON.stringify(samplePage.meta);
        console.log(`   Sample page.meta size: ${(metaJson.length / 1024).toFixed(1)} KB`);
        console.log(`   Sample page.meta fields:`, Object.keys(samplePage.meta));
        
        // Check for maps in meta
        Object.keys(samplePage.meta).forEach(key => {
          const val = samplePage.meta[key];
          if (val && typeof val === 'string' && val.length > 10000) {
            const sizeKB = (val.length / 1024).toFixed(1);
            const isImage = val.startsWith('data:image');
            const isPNG = val.startsWith('data:image/png');
            console.warn(`   ⚠️  Large meta.${key}: ${sizeKB} KB ${isImage ? '(IMAGE!)' : ''} ${isPNG ? '[PNG - likely a map!]' : ''}`);
          }
        });
      }
      
      // Special check for map-specific fields
      const mapFields = ['mapImage', 'staticMap', 'backgroundImage', 'mapUrl', 'mapData', 'mapSnapshot'];
      mapFields.forEach(field => {
        if (samplePage[field] && typeof samplePage[field] === 'string' && samplePage[field].length > 10000) {
          console.warn(`   🗺️  MAP FOUND: page.${field} = ${(samplePage[field].length / 1024).toFixed(1)} KB`);
        }
        if (samplePage.meta && samplePage.meta[field] && typeof samplePage.meta[field] === 'string' && samplePage.meta[field].length > 10000) {
          console.warn(`   🗺️  MAP FOUND: page.meta.${field} = ${(samplePage.meta[field].length / 1024).toFixed(1)} KB`);
        }
      });
    }
    
    // Check pages
    let pagesPhotoSize = 0;
    let pagesPhotoCount = 0;
    optimizedPages.forEach(page => {
      if (page.items) {
        page.items.forEach(item => {
          if (item.url) {
            pagesPhotoSize += item.url.length;
            pagesPhotoCount++;
          }
        });
      }
    });
    console.log(`   Pages: ${pagesPhotoCount} photos, ${(pagesPhotoSize / (1024 * 1024)).toFixed(2)} MB`);
    
    // Check photos array
    let photosArraySize = 0;
    let photosArraySrcSize = 0;
    optimizedPhotos?.forEach(photo => {
      if (photo.url) photosArraySize += photo.url.length;
      if (photo.src) photosArraySrcSize += photo.src.length;
    });
    console.log(`   Photos array url field: ${optimizedPhotos?.length || 0} photos, ${(photosArraySize / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`   Photos array src field: ${(photosArraySrcSize / (1024 * 1024)).toFixed(2)} MB`);
    
    // Check for other data
    const pagesJson = JSON.stringify(optimizedPages);
    const photosJson = JSON.stringify(optimizedPhotos);
    console.log(`   Pages JSON total: ${(pagesJson.length / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`   Photos JSON total: ${(photosJson.length / (1024 * 1024)).toFixed(2)} MB`);
    
    // SCAN ALL PAGES for maps or large data
    console.log('   🔍 Scanning ALL pages for hidden bloat...');
    let totalMapSize = 0;
    let mapsFound = 0;
    optimizedPages.forEach((page, idx) => {
      let pageMapSize = 0;
      
      // Check all fields in page
      Object.keys(page).forEach(key => {
        const val = page[key];
        if (val && typeof val === 'string' && val.startsWith('data:image') && val.length > 50000) {
          const sizeKB = (val.length / 1024).toFixed(1);
          const isPNG = val.startsWith('data:image/png');
          console.warn(`   📍 Page ${idx}: ${key} = ${sizeKB} KB ${isPNG ? '[PNG - likely a map!]' : '[Image]'}`);
          pageMapSize += val.length;
          if (isPNG) mapsFound++;
        }
      });
      
      // Check meta fields
      if (page.meta) {
        Object.keys(page.meta).forEach(key => {
          const val = page.meta[key];
          if (val && typeof val === 'string' && val.startsWith('data:image') && val.length > 50000) {
            const sizeKB = (val.length / 1024).toFixed(1);
            const isPNG = val.startsWith('data:image/png');
            console.warn(`   📍 Page ${idx} meta.${key} = ${sizeKB} KB ${isPNG ? '[PNG - likely a map!]' : '[Image]'}`);
            pageMapSize += val.length;
            if (isPNG) mapsFound++;
          }
        });
      }
      
      if (pageMapSize > 0) {
        totalMapSize += pageMapSize;
        console.log(`   → Page ${idx} total large images: ${(pageMapSize / 1024 / 1024).toFixed(2)} MB`);
      }
    });
    
    if (totalMapSize > 0) {
      console.warn(`   🗺️  TOTAL MAPS/LARGE IMAGES: ${(totalMapSize / 1024 / 1024).toFixed(2)} MB across ${mapsFound} images`);
      console.warn(`   💡 SOLUTION: These need to be optimized too!`);
    }
    
    // Calculate the mysterious overhead
    const pageOverhead = pagesJson.length - pagesPhotoSize;
    const photoOverhead = photosJson.length - (photosArraySize + photosArraySrcSize);
    console.log(`   📊 Pages overhead (non-photo data): ${(pageOverhead / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`   📊 Photos overhead (non-photo data): ${(photoOverhead / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`   📊 Total overhead: ${((pageOverhead + photoOverhead) / (1024 * 1024)).toFixed(2)} MB`);

    const payload = { 
      pages: optimizedPages, 
      photos: optimizedPhotos,
      trip: tripMeta, 
      v: 1 
    };

        // Remember this exact payload so Share can be instant
    previewPayloadRef.current = payload;

    // Fire-and-forget: prewarm the upload so the share hash is ready
    try {
      prewarmPromiseRef.current = prewarmUpload(payload, 'story').catch(() => null);
    } catch (_) {
      // non-fatal
    }

    
    // Track payload size before upload
    const payloadJson = JSON.stringify(payload);
    const payloadSizeMB = (payloadJson.length / (1024 * 1024)).toFixed(2);
    const photoCount = optimizedPages.reduce((sum, p) => sum + (p.items?.length || 0), 0);
    console.log(`📊 [Preview Payload Stats] Size: ${payloadSizeMB} MB | Photos: ${photoCount} | Pages: ${optimizedPages.length}`);
    
    // Preview with progress callback ← NEW
    // --- THIS IS THE REPLACEMENT ---
console.log('Uploading for local preview...');

// We replace 'previewStoryLocally' with 'uploadDecoupledAlbum'
// which we imported. This gives us the hash without opening a new window.
const { hash } = await uploadDecoupledAlbum(payload, 'story-preview', (progress) => {
  setUploadProgress(progress); // Update progress
});

if (hash) {
  console.log('Upload complete, preparing to open story viewer...');
  
  // 🔹 NEW: Cache the hash for instant re-preview
  lastPreviewHashRef.current = hash;
  lastAlbumFingerprintRef.current = createAlbumFingerprint(albumPages);
  console.log('⚡ [Preview Cache] Saved hash for future instant preview');
  
  // 🔹 Step 1: Trigger auto-save (so trip data persists)
  try {
    window.dispatchEvent(new CustomEvent('app:saveTrip'));
  } catch (e) {
    console.warn('[AlbumFromPast] app:saveTrip dispatch failed:', e);
  }
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // 🔹 Step 2: Save album cache for return navigation
  saveAlbumCacheForPreview();
  
  // 🔹 Step 3: Set flag for return navigation (NEW)
  try {
    sessionStorage.setItem('vvd:returningFromPreview', 'true');
    console.log('[AlbumFromPast] ✅ Set returning from preview flag');
  } catch (e) {
    console.warn('[AlbumFromPast] Failed to set return flag:', e);
  }
  
  // 🔹 Step 4: Save context INCLUDING trip ID
  try {
    const tripId = sessionStorage.getItem('vvd:currentTripId');
    sessionStorage.setItem('vvd:returnContext', JSON.stringify({
      view: 'past',
      albumFromPastOpen: true,
      tripName: tripName || '',
      currentTripId: tripId, // ← NEW: This lets us reload the trip
      timestamp: Date.now()
    }));
    console.log('[AlbumFromPast] ✅ Saved context with tripId:', tripId);
  } catch (e) {
    console.warn('[AlbumFromPast] Failed to save context:', e);
  }

  // ✅ FIX: Use production URL on Android/localhost
  const baseUrl = (window.location.origin || '').includes('localhost') || 
                  (window.location.origin || '').includes('127.0.0.1') || 
                  (window.location.origin || '').includes('10.0.2.2')
                    ? 'https://travel-app-2-five.vercel.app' 
                    : window.location.origin;
  const storyUrl = `${baseUrl}/story#${hash}`;
  const platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : 'web';

  if (platform === 'web') {
    // On web, open preview in a new tab so the album editor stays in place
    try {
      window.open(storyUrl, '_blank', 'noopener,noreferrer');
      console.log('[AlbumFromPast] Opened story preview in new tab for web');
    } catch (e) {
      console.warn('[AlbumFromPast] window.open failed, falling back to in-app navigation:', e);
      navigate(`/story#${hash}`);
    }
  } else {
    // On native (Android/iOS), keep current same-tab navigation so hardware back works
    navigate(`/story#${hash}`);
  }
}

// --- END REPLACEMENT BLOCK ---

setUploadProgress(null); // ← NEW: Clear progress when done
console.log('✅ Preview navigation initiated');
    
  } catch (e) {
    console.error('Preview failed:', e);
    setUploadProgress(null); // ← NEW: Clear progress on error
    alert('Could not preview story. Please try again.');
  } finally {
    setPreviewBusy(false);
  }
}, [albumPages, tripName, includedSteps, navigate, hydratePages]);


    



  const albumPreviewRef = useRef(null); // used to auto-scroll to preview

  const [showEditor, setShowEditor] = useState(false);
  const [showControls, setShowControls] = useState(false);
  // ✅ ADD THIS: Show controls when geocoding completes
useEffect(() => {
  // Show controls once we have steps and geocoding is done
  if (steps.length > 0 && !geocodingInProgress && !showControls) {
    setShowControls(true);
  }
}, [steps.length, geocodingInProgress, showControls]);
  const [isGenerating, setIsGenerating] = useState(false); // Loading state for album generation
  
  // 🔹 FIX: Track when album generation completes - delays onAlbumReady until after render
  const [pendingAlbumReady, setPendingAlbumReady] = useState(false);
  const albumReadyTimerRef = useRef(null);
  
  // 🔹 FIX LOADER DISAPPEARING: Guard against premature empty-state detection
  // Wait for steps to actually arrive before declaring "no photos found"
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);


  // ---- helpers used by stats/map ----
  function extractCoordinatesFromStep(step) {
    if (Number.isFinite(step.lat) && Number.isFinite(step.lng)) {
      return { lat: step.lat, lng: step.lng };
    }
    const found = (step.photos || []).find(
      p => p.coordinates && Number.isFinite(p.coordinates.lat) && Number.isFinite(p.coordinates.lng)
    );
    return found ? found.coordinates : null;
  }
  function haversineDistance(a, b) {
    const toRad = (x) => x * Math.PI / 180;
    const R = 3958.8;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLon/2)**2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  // Build stats from the list that MAY include Home (first item has isHome=true)
function buildEnhancedStats(stepsWithOptionalHome) {
  // Base stats (cities, countries, days, journeySteps, home label, etc.)
  const base = buildStatsFromSteps(stepsWithOptionalHome);

  // Compute distances along the path
  const coords = stepsWithOptionalHome
    .map(extractCoordinatesFromStep)
    .filter(Boolean);

  let totalMiles = 0;
  if (coords.length >= 2) {
    // forward legs
    for (let i = 1; i < coords.length; i++) {
      totalMiles += haversineDistance(coords[i - 1], coords[i]);
    }
    // if the first step is Home, add the last -> first "return" leg
    if (stepsWithOptionalHome[0]?.isHome) {
      totalMiles += haversineDistance(coords[coords.length - 1], coords[0]);
    }
  }

  return { ...base, totalMiles: Math.round(totalMiles) };
}

  // ✅ FIXED: Map now uses auto-fit for 3+ locations to ensure all pins are visible
  function makeEnhancedRouteSVG(analysisSteps, width = 1200, height = 800) {
  console.log('makeEnhancedRouteSVG called with steps:', analysisSteps?.length);

  // ✅ FIX: Better name extraction - prefer city names
  const pts = analysisSteps.map(s => {
    const c = extractCoordinatesFromStep(s);
    if (!c) return null;
    const name = s.address?.city || s.address?.town || s.city || s.state || s.country || s.title || 'Stop';
    return { x: c.lng, y: c.lat, name };
  }).filter(Boolean);
  
  console.log('Generated points:', pts.length, pts.map(p => p.name));
  
  if (!pts.length) {
    console.log('No GPS points found, returning placeholder SVG');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="#0f172a"/><text x="50%" y="50%" fill="#94a3b8" font-family="system-ui" font-size="16" text-anchor="middle">No GPS data available</text></svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  // Calculate bounds
  const minLng = Math.min(...pts.map(p => p.x));
  const maxLng = Math.max(...pts.map(p => p.x));
  const minLat = Math.min(...pts.map(p => p.y));
  const maxLat = Math.max(...pts.map(p => p.y));
  
  // ✅ FIX: Dynamic padding based on spread (15-25% instead of fixed 2%)
  const latSpread = maxLat - minLat;
  const lngSpread = maxLng - minLng;
  const paddingPercent = Math.max(0.15, Math.min(0.25, 0.15 + (Math.max(latSpread, lngSpread) / 100)));
  const latPadding = Math.max(latSpread * paddingPercent, 0.5); // At least 0.5 degrees
  const lngPadding = Math.max(lngSpread * paddingPercent, 0.5);
  
  console.log('Map bounds:', { minLat, maxLat, minLng, maxLng, latPadding, lngPadding, spread: Math.max(latSpread, lngSpread) });
  
  // Calculate center
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  
  // ✅ FIX: Better zoom calculation - no automatic +2 nudge for far locations
  const zoom = calculateZoom(minLng - lngPadding, minLat - latPadding, maxLng + lngPadding, maxLat + latPadding, width, height);
  // Only nudge in for very close locations (zoom > 10), otherwise use calculated zoom
  const finalZoom = zoom > 10 ? Math.min(14, zoom + 1) : zoom;
  
  console.log('Calculated zoom:', zoom, '-> final:', finalZoom);
  
  // Create route path for Mapbox
  const routePath = pts.map(p => `${p.x.toFixed(5)},${p.y.toFixed(5)}`).join(';');
  
  const MAPBOX_TOKEN = 'pk.eyJ1IjoibmdlcmdlcyIsImEiOiJjbWZsd29xbmUwOXBvMnFwdjkyZGF4NmVkIn0._En58wquwjmqCKIlXf4M7Q';
  
  if (MAPBOX_TOKEN && MAPBOX_TOKEN !== 'pk.YOUR_MAPBOX_TOKEN_HERE') {

    // Build pin overlays - all green, larger for fewer locations
    const pinSize = pts.length <= 5 ? 'l' : 's'; // large or small pins
    const pins = pts.map(p => `pin-${pinSize}+22c55e(${p.x.toFixed(5)},${p.y.toFixed(5)})`).join(',');

    // Route line overlay (yellow/orange) - no encoding needed
    const routeOverlay = pts.length > 1 ? `path-4+fbbf24-0.8(${routePath}),` : '';

    // Combine overlays
    const overlays = `${routeOverlay}${pins}`;

    // ✅ FIX: Use calculated zoom with minimum limit instead of 'auto'
    // This prevents excessive zoom-out from outlier GPS coordinates
    const minZoom = 4; // Never zoom out further than this (roughly continent level)
    const maxZoom = 12; // Never zoom in more than city level for route map
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, finalZoom));

    // Use center/zoom approach with clamped zoom level
    const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/static/${overlays}/${centerLng.toFixed(5)},${centerLat.toFixed(5)},${clampedZoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;

    console.log('Generated Mapbox URL (zoom:', clampedZoom, 'for', pts.length, 'locations)');
    return mapboxUrl;
  }
  
  // Fallback to enhanced SVG (if no Mapbox token)
  const toXY = (p) => {
    const effectiveWidth = width - 120; // More padding for labels
    const effectiveHeight = height - 80;
    const x = 60 + ((p.x - minLng) / Math.max(1e-6, (maxLng - minLng))) * effectiveWidth;
    const y = 40 + ((maxLat - p.y) / Math.max(1e-6, (maxLat - minLat))) * effectiveHeight;
    return [x, y];
  };
  
  const route = pts.map(toXY).map(([x, y]) => `${x},${y}`).join(' ');
  
  // ✅ FIX: Better pin rendering with alternating label positions
  const pins = pts.map((p, i) => {
    const [x, y] = toXY(p);
    const pinRadius = pts.length <= 5 ? 10 : 8;
    const labelY = (i % 2 === 0) ? y - pinRadius - 8 : y + pinRadius + 16;
    const truncatedName = p.name.length > 12 ? p.name.substring(0, 10) + '...' : p.name;
    
    return `<g>
      <circle cx="${x}" cy="${y}" r="${pinRadius}" fill="#22c55e" stroke="#15803d" stroke-width="2"/>
      <text x="${x}" y="${y + 4}" text-anchor="middle" font-size="10" fill="white" font-weight="bold">${i + 1}</text>
      ${pts.length <= 8 ? `<text x="${x}" y="${labelY}" text-anchor="middle" font-size="11" fill="#1e293b" font-weight="600">${truncatedName}</text>` : ''}
    </g>`;
  }).join('');
  
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <defs>
      <linearGradient id="mapBg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#e0f2fe"/>
        <stop offset="100%" stop-color="#bae6fd"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#mapBg)"/>
    ${pts.length > 1 ? `<polyline points="${route}" fill="none" stroke="#fbbf24" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>` : ''}
    ${pins}
    <g transform="translate(${width - 110}, ${height - 40})">
      <rect x="0" y="0" width="100" height="30" rx="6" fill="rgba(15,23,42,0.8)"/>
      <text x="50" y="20" text-anchor="middle" fill="#e2e8f0" font-size="11" font-family="system-ui">${pts.length} location${pts.length !== 1 ? 's' : ''}</text>
    </g>
  </svg>`;
  
  console.log('Generated fallback SVG');
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function makeStepMapSVG(step, width = 300, height = 180) {
  console.log('makeStepMapSVG called for step:', step?.title || step?.location);
  
  // Extract coordinates from step
  const coords = extractCoordinatesFromStep(step);
  
  if (!coords) {
    console.log('No GPS coordinates found for step, returning placeholder');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <rect width="100%" height="100%" fill="#eaeaea"/>
      <text x="50%" y="50%" fill="#999" font-family="system-ui" font-size="12" text-anchor="middle">No GPS data</text>
    </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  // 🆕 Fallback: if no GPS but step has City/Country, show static Mapbox tile
  const addr = step?.address || {};
  const city = addr.city || addr.town || addr.village || step?.location;
  const country = addr.country || addr.country_name;
  if (!coords && (city || country)) {
    const place = [city, country].filter(Boolean).join(', ');
    const encoded = encodeURIComponent(place);
    const MAPBOX_TOKEN = 'pk.eyJ1IjoibmdlcmdlcyIsImEiOiJjbWZsd29xbmUwOXBvMnFwdjkyZGF4NmVkIn0._En58wquwjmqCKIlXf4M7Q';
    const zoom = city ? 8 : 4; // broader if only country
    const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${encoded},${zoom},0,0/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;
    console.log('Generated no-GPS fallback map for', place, mapboxUrl);
    return mapboxUrl;
  }

  const { lat, lng } = coords;
  
  // Use same Mapbox token as the route map
  const MAPBOX_TOKEN = 'pk.eyJ1IjoibmdlcmdlcyIsImEiOiJjbWZsd29xbmUwOXBvMnFwdjkyZGF4NmVkIn0._En58wquwjmqCKIlXf4M7Q';
  
  if (MAPBOX_TOKEN && MAPBOX_TOKEN !== 'pk.YOUR_MAPBOX_TOKEN_HERE') {
    // City-level zoom for individual steps
    const zoom = 12;
    
    // Create pin overlay for the step location
    const pin = `pin-s+10b981(${lng},${lat})`; // Green pin
    
    const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/static/${pin}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;
    
    console.log('Generated step map URL:', mapboxUrl);
    return mapboxUrl;
  }
  
  // Fallback to simple SVG if no token
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <defs>
      <pattern id="stepGrid" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ddd" stroke-width="1" opacity="0.5"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="#f5f5f5"/>
    <rect width="100%" height="100%" fill="url(#stepGrid)"/>
    <circle cx="50%" cy="50%" r="8" fill="#10b981" stroke="#fff" stroke-width="2"/>

    <text x="50%" y="85%" font-size="10" fill="#666" text-anchor="middle" font-family="system-ui">
      ${step?.location || step?.title || 'Location'}
    </text>
  </svg>`;
  
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
// Helper function to calculate appropriate zoom level


function ensureIntroPages(pages, realSteps, analysisSteps) {
  const arr = Array.isArray(pages) ? pages : [];
  const coverIdx = arr.findIndex(p => (p.type === 'cover' || p.templateKey === 'cover'));
  const cover = coverIdx >= 0 ? arr[coverIdx] : null;
  const rest = arr.filter(p => !['cover','stats','map','route'].includes((p.templateKey||p.type||'').toLowerCase()));
  
  // Build stats based on the list that INCLUDES Home (for miles calculation)
  const statsMeta = buildEnhancedStats(analysisSteps);

  // 🔧 FIX: Filter out home step for the MAP (but keep for stats above)
  const stepsForMap = analysisSteps.filter(s => !s.isHome);
  
  const routeSvg = makeEnhancedRouteSVG(stepsForMap);
  const pts = pointsFromSteps(stepsForMap);
  const statsPage = { id:`pg_stats_${Date.now()}`, type:'stats', templateKey:'stats', meta: statsMeta };
  const mapPage   = { id:`pg_map_${Date.now()+1}`, type:'map', templateKey:'map', meta: { svgDataUrl: routeSvg, points: pts, globalMapSvgDataUrl: routeSvg, hideTitle: true }};
  const final = [];
  if (cover) final.push(cover);
  final.push(statsPage, mapPage, ...rest);
  return final;
}

// ✅ FIXED: More granular zoom levels for better map fitting
function calculateZoom(minLng, minLat, maxLng, maxLat, mapWidth, mapHeight) {
  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);
  
  // More granular zoom levels for better fit
  if (maxDiff < 0.005) return 15;   // Street level
  if (maxDiff < 0.01) return 14;    // Neighborhood
  if (maxDiff < 0.05) return 12;    // City district
  if (maxDiff < 0.1) return 11;     // City
  if (maxDiff < 0.3) return 10;     // Metro area
  if (maxDiff < 0.5) return 9;      // Region
  if (maxDiff < 1) return 8;        // State/Province
  if (maxDiff < 2) return 7;        // Large state
  if (maxDiff < 5) return 6;        // Country
  if (maxDiff < 10) return 5;       // Large country
  if (maxDiff < 20) return 4;       // Continent region
  if (maxDiff < 40) return 3;       // Continent
  return 2;                         // Global
}

  // Add this new component to your AlbumFromPast.jsx file
// This handles cover page editing with step tabs


  // Build + analyze
  // REPLACE your entire 'runAnalysis' function with this:
// REPLACE your entire 'runAnalysis' function with this:
// REPLACE your entire 'runAnalysis' function with this:
// REPLACE your entire 'runAnalysis' function with this:
// REPLACE your entire 'runAnalysis' function with this:

// REPLACE your runAnalysis function in AlbumFromPast.jsx with this simplified version:

// src/album/AlbumFromPast.jsx

// In src/album/AlbumFromPast.jsx

const runAnalysis = useCallback(async () => {
  if (!canGenerate) return;

  // 🔹 NEW: Signal to parent to show cinematic gathering loader (Past Trip only)
  if (onGenerateStart && !digestMeta) {
    onGenerateStart(allCandidatePhotos);
  }

  // Signal that a user-initiated generation is in progress
  generateRequestedRef.current = true;

  setIsGenerating(true); // Start loading

  try {
    
    // === NEW: "Current Trip" / Daily Digest Flow ===
    // If digestMeta exists, we are in the "Current Trip" flow.
    if (digestMeta) {
      // 1. Get face hints (required by the digest builder)
      const peopleHints = await faceHintsForPhotos(allCandidatePhotos, { minCoverage: 0.03, resize: 512 });

      // 2. Call the SPECIAL digest builder
      const digest = buildDigestAlbum({
        steps: stepsForAnalysis,
        results: null, // Digest builder uses "diverse", no ML results needed
        likesOnly,
        tripName,
        peopleHints,
        journalText: digestMeta.journalText || '',
        // Note: min/max pages are defaults (5-15) in the builder
      });

      // 3. Set the pages.
      // ⬇️ === FIX === ⬇️
      // We still call ensureIntroPages to generate the correct (new)
      // Mapbox route map and stats pages from AlbumFromPast.jsx.
      // This function will find and replace any "stale" intro pages
      // generated by the builder.
      const digestPages = ensureIntroPages(digest.pages, includedSteps, stepsForAnalysis);
      
      // Convert Mapbox URLs to data URLs for Capacitor compatibility
      const convertedDigestPages = await convertAlbumMapsToDataUrls(digestPages);
      
      beginAlbumSession('digest');
      setAlbumPages(convertedDigestPages);
      setShowEditor(false);
      setShowControls(false);
      
      // 🔹 FIX: Don't call onAlbumReady immediately - let useEffect handle it after render
      setPendingAlbumReady(true);
    } 
    // === Original Flow (Manual Album from Past) ===
    else if (selectionMode === 'diverse') {
      // This is your existing logic for manual "Diverse"
      const peopleHints = await faceHintsForPhotos(allCandidatePhotos, { minCoverage: 0.03, resize: 512 });

      const fast = buildAlbumFromPast({
        steps: stepsForAnalysis,
        results: null,
        likesOnly,
        layoutMode: layout,
        selectionMode: 'diverse',
        peopleHints,
        tripName,
        // No journal text in manual mode
      });

      const diversePages = ensureIntroPages(fast.pages, includedSteps, stepsForAnalysis);
      
      // Convert Mapbox URLs to data URLs for Capacitor compatibility
      const convertedDiversePages = await convertAlbumMapsToDataUrls(diversePages);
      
      beginAlbumSession('diverse');
      setAlbumPages(convertedDiversePages);
      setShowEditor(false);
      setShowControls(false);
      
      // 🔹 FIX: Don't call onAlbumReady immediately - let useEffect handle it after render
      setPendingAlbumReady(true);
      
    } else {
      // This is your existing logic for manual "Vibes"
      setVibesBusy(true);
 try {
   await analyzePhotos(allCandidatePhotos, {
     peopleDetector: 'face-only',
     minFaceCoverage: 0.03
   });
 } catch (err) {
   console.warn('Vibes analysis failed:', err);
   setSelectionMode('diverse'); // fallback to a supported mode
 } finally {
   setVibesBusy(false);
 }

    }
  } finally {
    setIsGenerating(false); // End loading
  }

}, [
    canGenerate, 
    selectionMode, 
    stepsForAnalysis, 
    includedSteps, 
    layout, 
    likesOnly, 
    analyzePhotos, 
    allCandidatePhotos,
    digestMeta, // Dependency is already here
    tripName,    // Dependency is already here
    onGenerateStart,  // 🔹 NEW: Add this line
    onAlbumReady // 🔹 NEW: Added callback to dependency array
    
]);

// 🔹 FIX: Delayed onAlbumReady - waits for album to actually render before hiding loader
// This ensures the loader stays visible until the album pages are painted to screen
useEffect(() => {
  // Clean up any existing timer on unmount or re-run
  return () => {
    if (albumReadyTimerRef.current) {
      clearTimeout(albumReadyTimerRef.current);
      albumReadyTimerRef.current = null;
    }
  };
}, []);

useEffect(() => {
  if (!pendingAlbumReady) return;
  if (albumPages.length === 0) return;
  
  console.log('[AlbumFromPast] Album pages set, waiting for render before calling onAlbumReady...');
  
  // Clear any existing timer
  if (albumReadyTimerRef.current) {
    clearTimeout(albumReadyTimerRef.current);
  }
  
  // Wait for:
  // 1. React to commit the state update (next tick)
  // 2. Browser to paint the DOM (requestAnimationFrame)
  // 3. Additional buffer for images to start loading
  // Android needs more time due to slower rendering
  const isAndroid = Capacitor.getPlatform?.() === 'android';
  const renderDelay = isAndroid ? 1500 : 500;

  albumReadyTimerRef.current = setTimeout(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        console.log('[AlbumFromPast] Album rendered, now calling onAlbumReady');
        if (onAlbumReady) {
          onAlbumReady();
        }
        setPendingAlbumReady(false);
        albumReadyTimerRef.current = null;
      });
    });
  }, renderDelay);
  
}, [pendingAlbumReady, albumPages.length, onAlbumReady]);

const [hasAutoGenerated, setHasAutoGenerated] = useState(false);

// Whenever a new digestMeta arrives (new Daily Digest config), reset the flag
useEffect(() => {
  setHasAutoGenerated(false);
}, [digestMeta]);

// 🔹 FIX LOADER DISAPPEARING: Mark load complete after steps arrive
// This prevents premature "no photos found" if steps haven't propagated yet
useEffect(() => {
  // Reset on new digest
  setInitialLoadComplete(false);
  
  if (digestMeta && steps.length > 0) {
    // Give steps time to fully arrive before allowing empty-state detection
    const timer = setTimeout(() => {
      console.log('[AlbumFromPast] Initial load complete - steps have arrived');
      setInitialLoadComplete(true);
    }, 500);
    return () => clearTimeout(timer);
  }
}, [digestMeta, steps.length]);

// ⬇️ NEW: Force generation if geocoding hangs
  const [forceDigestGeneration, setForceDigestGeneration] = useState(false);

  useEffect(() => {
    if (digestMeta && !hasAutoGenerated) {
      // 2.5s timeout: if addresses aren't ready by then, proceed anyway
      const timer = setTimeout(() => {
        console.log('[AlbumFromPast] ⚠️ Geocoding took too long, forcing digest generation...');
        setForceDigestGeneration(true);
      }, 2500);
      return () => clearTimeout(timer);
    }
    setForceDigestGeneration(false);
  }, [digestMeta, hasAutoGenerated]);

// Check if steps have address data (geocoding completed)
const stepsHaveAddressData = useMemo(() => {
  // Use dateFilteredSteps instead of raw steps to check the correct subset
  const stepsToCheck = dateFilteredSteps || [];
  
  if (stepsToCheck.length === 0) return true; // No steps to check, OK to proceed
  
  const stepsWithGPS = stepsToCheck.filter(s => {
    const hasGPS = (s.photos || []).some(p => 
      p.coordinates && Number.isFinite(p.coordinates.lat) && Number.isFinite(p.coordinates.lng)
    );
    return hasGPS;
  });
  if (stepsWithGPS.length === 0) return true; // No GPS steps, OK
  
  // At least one GPS step must have address
  return stepsWithGPS.some(s => s.address && (s.address.country || s.address.city));
}, [dateFilteredSteps]);

useEffect(() => {
    if (!digestMeta) return;
    if (hasAutoGenerated) return;
    if (!canGenerate) return;
    if (isGenerating) return;
    
    // 🔹 FIX LOADER DISAPPEARING: Don't declare empty until we've given steps time to arrive
    // This prevents premature loader hiding when steps haven't propagated yet
    if (dateFilteredSteps.length === 0) {
      if (!initialLoadComplete) {
        // Still waiting for steps to arrive - don't make a decision yet
        console.log('[Daily Digest] Waiting for steps to arrive before checking empty state...');
        return;
      }
      // Now we can safely say there are no photos
      console.warn('[Daily Digest] No photos found for selected dates. Showing empty state.');
      setHasAutoGenerated(true); 
      // 🔹 FIX: Ensure we call onAlbumReady so loader hides even if empty
      if (onAlbumReady) onAlbumReady(); 
      return;
    }
    
    // 🔹 FIX: Wait for addresses, BUT proceed if forced by timeout
    if (!stepsHaveAddressData && !forceDigestGeneration) return;

    runAnalysis();
    setHasAutoGenerated(true);
  }, [
    digestMeta, 
    hasAutoGenerated, 
    canGenerate, 
    isGenerating, 
    runAnalysis, 
    stepsHaveAddressData, 
    dateFilteredSteps,
    forceDigestGeneration, // <--- New dep
    onAlbumReady,          // <--- New dep
    initialLoadComplete    // <--- New dep for loader fix
  ]);
// Update the ref *after* render to track the previous state
useEffect(() => {
  prevIsAnalyzing.current = isAnalyzing;
}, [isAnalyzing]);
// REPLACE your useEffect with this version that converts the data format:

useEffect(() => {

  if (digestMeta) return;

    // Only proceed if generation was explicitly requested by the user
  if (!generateRequestedRef.current) return;


  // Only build album when analysis just completed in vibes mode
  if (
   selectionMode === 'vibes' &&
   !isAnalyzing &&
   resultsByUrl && Object.keys(resultsByUrl).length > 0
 ) {
    console.log('Converting results for builder. Raw results:', results);

    // Convert array results to URL-keyed object that builder expects
    const resultsMap = {};
    
    // ⬇️ === FIX === ⬇️
    // Add a safety check. If results is not a valid array,
    // resultsMap will remain {} and the builder will use the fallback.
    if (Array.isArray(results) && results.length > 0) {
      let matchCount = 0;
      results.forEach(photoResult => {
        if (!photoResult || !photoResult.url) return;
        const key = normalize(photoResult.url);
        resultsMap[key] = {
          ...photoResult,
          url: key,
          // CRUCIAL: adapter so the builder sees ML scores
          scores: photoResult.scores || photoResult.score || photoResult.vibes || null
        };
      });
      console.log('Converted results map:', resultsMap);
      console.log(`URL matches: ${matchCount}/${results.length}`);
    } else {
      console.log('No ML results returned, will use fallback heuristic.');
    }

    const scored = buildAlbumFromPast({
      steps: stepsForAnalysis,
      results: resultsMap,     // Pass converted format
      vibes,
      likesOnly,
      layoutMode: layout,
      selectionMode: 'vibes',
      tripName,
    });

    const vibesPages = ensureIntroPages(scored.pages, includedSteps, stepsForAnalysis);
    
    // Convert Mapbox URLs to data URLs for Capacitor compatibility
    convertAlbumMapsToDataUrls(vibesPages).then(convertedVibesPages => {
      beginAlbumSession('vibes');
      setAlbumPages(convertedVibesPages);
      setShowEditor(false);
      setShowControls(false);
      generateRequestedRef.current = false;

      // 🔹 FIX: Don't call onAlbumReady immediately - let useEffect handle it after render
      setPendingAlbumReady(true);
    });

  }
}, [
    isAnalyzing, 
    results,
    resultsByUrl,      // ← add this 
    selectionMode, 
    stepsForAnalysis, 
    includedSteps, 
    vibes, 
    likesOnly, 
    layout, 
    allCandidatePhotos,
    digestMeta, // Dependency is already here
    tripName,    // Dependency is already here
    vibesBusy, // <-- FIX: Added 'vibesBusy' to dependency array
    onAlbumReady // 🔹 NEW: Added callback to dependency array
]);



// New function to analyze photos for a single step


// Remove the useEffect that was watching for global analysis completion
// since we're now handling completion directly in the step-by-step loop

// New function to analyze photos for a single step


// Remove the useEffect that was watching for global analysis completion
// since we're now handling completion directly in the step-by-step loop

// Separate effect to build album when analysis completes


  // Debounced toggle to prevent rapid re-renders on large datasets
const toggleStepDebounced = useRef(null);
const toggleStep = useCallback((id) => {
  // Clear any pending toggle
  if (toggleStepDebounced.current) {
    clearTimeout(toggleStepDebounced.current);
  }
  
  // Immediate visual feedback - update local state
  setExcluded(prev => {
    const next = new Set(prev);
    const mergedStep = mergedSteps.find(s => s.id === id || s.mergedIds?.includes(id));
    
    if (mergedStep && mergedStep.mergedIds && mergedStep.mergedIds.length > 1) {
      const isCurrentlyExcluded = mergedStep.mergedIds.some(mid => next.has(mid));
      mergedStep.mergedIds.forEach(mid => {
        if (isCurrentlyExcluded) {
          next.delete(mid);
        } else {
          next.add(mid);
        }
      });
    } else {
      if (next.has(id)) next.delete(id); else next.add(id);
    }
    return next;
  });
}, [mergedSteps]);
  const toggleVibe = (key) =>
  setVibes(prev => {
    const next = new Set(prev);
    if (next.has(key)) {
      // do not allow empty set in 'vibes' mode
      if (next.size === 1 && selectionMode === 'vibes') {
        return next; // keep at least one selected
      }
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  });


  // Step-scoped replacement candidates for editor (by stepId)
  const stepCandidates = useCallback((stepId, limit = 24) => {
    const step = steps.find(s => s.id === stepId);
    if (!step) return [];
    const pool = (step.photos || []).filter(p => (likesOnly ? p.likes > 0 : true));
    try {
      const ranked = semiAutoCandidates(pool, resultsByUrl, { vibes: Array.from(vibes) });
      return ranked.slice(0, limit);
    } catch {
      return pool
        .map(p => {
          const sv = Array.from(vibes).reduce((acc, k) => acc + (((resultsByUrl && resultsByUrl[p.url] && resultsByUrl[p.url].scores && resultsByUrl[p.url].scores[k]) || 0)), 0);
          return { p, score: sv + (p.likes || 0) * 10 };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map(r => r.p);
    }
  }, [likesOnly, results, steps, vibes]);

  // Flipbook adapter
  // In AlbumFromPast.jsx, REPLACE the old map with this one:

// REPLACE WITH THIS:
const TEMPLATE_MAP = {
  cover: 'cover',
  stats: 'stats',
  route: 'map',
  map: 'map',
  'step-intro-left': 'step-intro-left',
  'grid-left': 'grid-2-tall',      // Fixed mapping
  'grid-right': 'grid-2-tall',     // Fixed mapping 
  'hero-right': 'hero-right',          // CORRECTED: Passes the right key
  'journal-left': 'journal-right',      // CORRECTED: Fixes journal pages too
  'journal-right': 'journal-right',
  blank: 'grid-2-tall', // fallback: use 4-up look via meta.variant
};
const TYPE_TO_TEMPLATE = TEMPLATE_MAP;

  function toFlipbook(pages) {
  const seen = new Set();
  const photos = [];
  const flipPages = (pages || []).map((pg) => {
    const items = (pg.photos || []).map((ph) => {
  const id = ph.id || ph.url || `${ph.fileName || 'p'}-${ph.dateTaken || ''}`;
  if (!seen.has(id)) { 
    seen.add(id); 
    // FIXED: Include position data in photos array
    // src/album/AlbumFromPast.jsx

    photos.push({ 
      ...ph, // Pass the *entire* original photo object
      id: id // Ensure the ID matches what the page items use
    });
  }

  // ===== ⬇️ THIS IS THE FIX ⬇️ =====
  // Return the full photo object in the item, not just the ID.
  // This ensures the 'url' is embedded in the page's item list.
  return { 
    ...ph, // Includes the original 'url' and 'position'
    photoId: id, // Keep photoId for consistency
    id: id // Ensure id is present
  };
    });
    const templateKey = pg.templateKey || TYPE_TO_TEMPLATE[pg.type] || DEFAULT_TEMPLATE_KEY;

    const meta = pg.meta ? { ...pg.meta } : null;
    return { templateKey, items, meta, type: pg.type };
  });
  return { pages: flipPages, photos };
}
  const flipbook = useMemo(() => toFlipbook(albumPages), [albumPages]);

  // Prevent background scroll when editor is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (showEditor) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = prev || '';
    return () => { document.body.style.overflow = prev || ''; };
  }, [showEditor]);

  // Communicate overlay state to parent via global flag
  useEffect(() => {
    // Set flag when ANY overlay is open
    if (showEditor || showShareSheet || uploadProgress) {
      window.__vividaOverlayOpen = true;
      console.log('📍 [AlbumFromPast] Setting global flag: overlay OPEN');
    } else {
      window.__vividaOverlayOpen = false;
      console.log('📍 [AlbumFromPast] Setting global flag: overlay CLOSED');
    }
  }, [showEditor, showShareSheet, uploadProgress]);

  // Handle Android hardware back button
  useEffect(() => {
    console.log('🔧 [AlbumFromPast] Registering back button handler');
    
    const onHardwareBack = (e) => {
      console.log('🔙🔙🔙 [AlbumFromPast] Back event received!');
      
      // Close editor
      if (showEditor) {
        console.log('[AlbumFromPast] ✅✅✅ CLOSING EDITOR');
        e.preventDefault();
        setShowEditor(false);
        setShowControls(true);
        document.body.style.overflow = 'auto';
        return;
      }

      // Close share sheet
      if (showShareSheet) {
        console.log('[AlbumFromPast] ✅✅✅ CLOSING SHARE SHEET');
        e.preventDefault();
        setShowShareSheet(false);
        return;
      }

      // Close upload progress
      if (uploadProgress) {
        console.log('[AlbumFromPast] ✅✅✅ CLOSING UPLOAD PROGRESS');
        e.preventDefault();
        setUploadProgress(null);
        return;
      }

      console.log('[AlbumFromPast] ⚠️ No overlay to close');
    };

    window.addEventListener('app:hardwareBack', onHardwareBack, true);
    console.log('✅ [AlbumFromPast] Handler registered');
    
    return () => {
      console.log('🗑️ [AlbumFromPast] Removing handler');
      window.removeEventListener('app:hardwareBack', onHardwareBack, true);
    };
  }, [showEditor, showShareSheet, uploadProgress]);


  // Debug
  useEffect(() => {
    const firstThree = (flipbook.pages || []).slice(0, 3);
    console.log('First 3 pages:', firstThree.map((p, i) => ({
      i, type: p.type, tpl: p.templateKey, meta: p.meta && Object.keys(p.meta)
    })));
  }, [flipbook]);

    // Auto-scroll to the preview as soon as pages are generated and spinner stops
  useEffect(() => {
    if (!isGenerating && albumPages && albumPages.length > 0) {
      // small delay to ensure layout is painted
      const t = setTimeout(() => {
        try {
          // blur any focused input (iOS)
          if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
          }
          const el = albumPreviewRef.current || document.getElementById('album-preview');
          if (!el) return;
          // two RAFs to be extra safe that Flipbook has mounted
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          });
        } catch {}
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isGenerating, albumPages]);


  return (
    <>
    <div className="max-w-7xl mx-auto p-4 space-y-8">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">
            Turn photos into a memorable travel book
          </h1>
          
          <button
            type="button"
            onClick={() => setShowControls(s => !s)}
            aria-expanded={showControls}
            aria-controls="past-album-panel"
            className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs"
            title={showControls ? 'Hide panel' : 'Show panel'}
          >
            {showControls ? '▲' : '▼'}
          </button>
        </div>
      </header>

      {/* Empty state for digest mode with no photos */}
      {digestMeta && dateFilteredSteps.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="max-w-md text-center space-y-4">
            <div className="text-6xl mb-4">📷</div>
            <h2 className="text-2xl font-semibold text-slate-200">No photos found</h2>
            <p className="text-slate-400">
              We couldn't find any photos for the dates you selected.
              Please choose different dates or import photos for these dates.
            </p>
            <button
              onClick={onBack}
              className="mt-6 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {showControls && !(digestMeta && dateFilteredSteps.length === 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 1) Step picker */}
          <Card title="1) Chapters to include">
            <p className="text-sm text-gray-400 mb-2">Uncheck any chapter you want to exclude.</p>
            <div className="max-h-64 overflow-auto space-y-2 pr-2">
              {/* Step 0 (Home) */}
              <div className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={includeHome} onChange={e => setIncludeHome(e.target.checked)} style={{ marginTop: 5 }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">Home (Chapter 0)</div>
 <div className="text-xs text-slate-300 mb-2">Included for distance & map, not for photos</div>
                  <div className="flex flex-wrap items-center gap-2">
                    

<datalist id="statesList">
  {uniqueStates.map(s => <option key={s} value={s} />)}
</datalist>

<div className="relative inline-block">
  <input
    placeholder="Country"
    value={homeCountry}
    onChange={e => { setHomeCountry(e.target.value); setShowCountryMenu(true); }}
    onFocus={e => { e.target.select(); setShowCountryMenu(true); }}
    onKeyDown={(e) => { if (e.key === 'ArrowDown') setShowCountryMenu(true); }}
    onBlur={() => setTimeout(() => setShowCountryMenu(false), 150)}
    className="input input-sm w-40 border border-slate-600 bg-transparent pr-10"
    style={{ minWidth: 160 }}
  />
  {/* Clear (×) */}
  {homeCountry && (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setHomeCountry('')}
      className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
      title="Clear"
      style={{ lineHeight: 1 }}
    >
      ×
    </button>
  )}
  {/* Toggle ▼ */}
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={() => setShowCountryMenu(v => !v)}
    className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
    title="Show options"
    style={{ lineHeight: 1 }}
  >
    ▾
  </button>

  {/* Dropdown */}
  {showCountryMenu && (
    <div className="absolute z-50 mt-1 w-64 max-h-60 overflow-auto rounded-md border border-slate-700 bg-slate-800 shadow-lg">
      {countrySuggestions
        .filter(c => c.toLowerCase().includes((homeCountry || '').toLowerCase()))
        .slice(0, 200)
        .map(c => (
          <button
            key={c}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setHomeCountry(c); setShowCountryMenu(false); }}
            className="block w-full text-left px-3 py-2 hover:bg-slate-700"
          >
            {c}
          </button>
        ))}
      {countrySuggestions.length === 0 && (
        <div className="px-3 py-2 text-slate-400">No suggestions</div>
      )}
    </div>
  )}
</div>



<datalist id="countriesList">
  {countrySuggestions.map(c => <option key={c} value={c} />)}
</datalist>

{/* STATE second — dropdown only when USA */}
<div className="relative inline-block">
  <input
    placeholder="State"
    value={homeState}
    onChange={e => {
      setHomeState(e.target.value);
      if (isUSA(homeCountry)) setShowStateMenu(true); else setShowStateMenu(false);
    }}
    onFocus={e => {
      e.target.select();
      if (isUSA(homeCountry)) setShowStateMenu(true);
    }}
    onKeyDown={(e) => {
      if (e.key === 'ArrowDown' && isUSA(homeCountry)) setShowStateMenu(true);
    }}
    onBlur={() => setTimeout(() => setShowStateMenu(false), 150)}
    className="input input-sm w-40 border border-slate-600 bg-transparent pr-10"
    style={{ minWidth: 160 }}
  />
  {/* Clear (×) */}
  {homeState && (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setHomeState('')}
      className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
      title="Clear"
      style={{ lineHeight: 1 }}
    >
      ×
    </button>
  )}
  {/* Toggle ▼ only if USA */}
  {isUSA(homeCountry) && (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setShowStateMenu(v => !v)}
      className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
      title="Show options"
      style={{ lineHeight: 1 }}
    >
      ▾
    </button>
  )}

  {/* Dropdown menu only if USA */}
  {isUSA(homeCountry) && showStateMenu && (
    <div className="absolute z-50 mt-1 w-64 max-h-60 overflow-auto rounded-md border border-slate-700 bg-slate-800 shadow-lg">
      {stateSuggestions
        .filter(s => s.toLowerCase().includes((homeState || '').toLowerCase()))
        .slice(0, 200)
        .map(s => (
          <button
            key={s}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setHomeState(s); setShowStateMenu(false); }}
            className="block w-full text-left px-3 py-2 hover:bg-slate-700"
          >
            {s}
          </button>
        ))}
      {stateSuggestions.length === 0 && (
        <div className="px-3 py-2 text-slate-400">No suggestions</div>
      )}
    </div>
  )}
</div>


                    <button type="button" className="btn btn-ghost" onClick={() => { try { const el = document.createElement('div'); el.textContent = 'Home updated'; el.style.cssText = 'position:fixed;bottom:16px;right:16px;background:#0ea5e9;color:white;padding:6px 10px;border-radius:8px;z-index:9999'; document.body.appendChild(el); setTimeout(() => el.remove(), 1000); } catch {} }}>
                      Save
                    </button>
                  </div>
                  {(homeState || homeCountry) ? (
                    <div className="text-xs mt-1 opacity-70">
                      Using: {homeState}{homeState && homeCountry ? ', ' : ''}{homeCountry}
                    </div>
                  ) : null}
                </div>
              </div>

              {mergedSteps.map(step => {
                const count = step.photos?.length || 0;
                const checked = !step.mergedIds?.some(id => excludedStepIds.has(id));
                const { city, country } = cityCountryOf(step);
                const isMerged = step.mergedIds && step.mergedIds.length > 1;
                return (
                  <label key={step.id} className="flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={checked} onChange={() => toggleStep(step.id)} />
                    <span className="truncate">
  {(() => {
    const { city, country } = cityCountryOf(step);
    const where = [city, country].filter(Boolean).join(', ');
    return (
      <>
        <b>{where || step.title || step.location || formatDate(step.date) || 'Step'}</b>
        <div className="text-xs text-slate-300">
          {formatDate(step.date)}
          {where && <> • {where}</>}
          {' • '}{pluralize(count, 'photo')}
          {isMerged && <span className="text-blue-400"> (merged)</span>}
        </div>
      </>
    );
  })()}
</span>

                  </label>
                );
              })}
            </div>
            <div className="mt-3 text-xs text-gray-400">
              Selected chapters: {includedSteps.length} / {steps.length}
            </div>
          </Card>

          {/* 2) Vibes & likes */}
          
<Card title="2) Selection Method">
  <div className="space-y-3">
    {/* Radio button for Diverse */}
    <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 has-[:checked]:bg-slate-700/50 has-[:checked]:border-slate-500 cursor-pointer">
      <input type="radio" name="selectionMode" value="diverse" checked={selectionMode === 'diverse'} onChange={() => setSelectionMode('diverse')} className="mt-1" />
      <div>
        <div className="font-semibold">Diverse</div>
        <div className="text-xs text-slate-400">A quick, balanced selection of photos from each step. (Fastest)</div>
      </div>
    </label>

    {/* Radio button for Vibes */}
    <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 has-[:checked]:bg-slate-700/50 has-[:checked]:border-slate-500 cursor-pointer">
      <input type="radio" name="selectionMode" value="vibes" checked={selectionMode === 'vibes'} onChange={() => setSelectionMode('vibes')} className="mt-1" />
      <div>
        <div className="font-semibold">Vibes</div>
        <div className="text-xs text-slate-400">A custom album tailored to specific themes. (Requires photo analysis)</div>
      </div>
    </label>

    {/* Vibe toggles only show when 'vibes' mode is selected */}
    
    {selectionMode === 'vibes' && (
      <div className="pl-9 flex flex-wrap gap-2">
        {ALL_VIBES.map(k => {
          const isSelected = vibes.has(k);
          
          return (
            <button
  key={k}
  type="button"
  aria-pressed={isSelected}
  onClick={() => toggleVibe(k)}
  className={[
    "px-3 py-1 rounded-full text-sm font-medium border transition-colors",
    isSelected
      ? "bg-indigo-600 text-white border-indigo-600"
      : "bg-slate-800 text-slate-200 border-slate-500 hover:bg-slate-700"
  ].join(" ")}
>
  {isSelected ? "✓ " : ""}{k}
</button>

          );
        })}
      </div>
    )}
  </div>
  
  <hr className="border-slate-700 my-4" />

  <label className="flex items-center gap-2 text-sm">
    <input type="checkbox" checked={likesOnly} onChange={e => setLikesOnly(e.target.checked)} />
    Likes only
  </label>

  <div className="mt-3 text-xs text-gray-400">
    Candidate photos: {allCandidatePhotos.length}
  </div>
</Card>
          {/* 3) Layout & Generate */}
          <Card title="3) Layout & Generate">
            <fieldset className="space-y-2 mb-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="layout" checked={layout === LAYOUT.DENSE} onChange={() => setLayout(LAYOUT.DENSE)} />
                Photo-dense (2–4 per page)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="layout" checked={layout === LAYOUT.ELEGANT} onChange={() => setLayout(LAYOUT.ELEGANT)} />
                Elegant (left: grid, right: full-bleed hero)
              </label>
            </fieldset>

            
<button
  disabled={!canGenerate || isGenerating}
  onClick={runAnalysis}

  

  className={`w-full py-3 rounded-xl font-medium transition-all
    ${canGenerate && !isGenerating ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400 cursor-not-allowed'}
  `}
  title={geocodingInProgress ? 'Please wait while locations are being resolved...' : ''}
>
  {isGenerating ? (
    <div className="flex items-center justify-center gap-2">
      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      <span>Generating Album...</span>
    </div>
  ) : selectionMode === 'vibes' && isAnalyzing ? 'Analyzing…' : 
  geocodingInProgress ? 'Resolving locations…' : 
  'Generate Album'}
</button>

{/*
            <div className="mt-3 text-xs text-gray-400">
  Status: {
    geocodingInProgress ? `Resolving ${geoResolutionQueue.length} locations...` :
    isReady ? 'Worker ready' : 
    'Loading models…'
  }
</div>
*/}
          </Card>
        </div>
      )}

      {/* Preview (always show Flipbook; editor opens in overlay) */}
      {!!albumPages.length && (
  <div
    className="space-y-3 pb-96"  // ← Add padding-bottom (pb-96 = 24rem)
    ref={albumPreviewRef}
    id="album-preview"
    style={{ scrollMarginTop: 80 }}
  >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Album preview</h2>
            <div className="flex items-center gap-2">
              {/* 🆕 Save status indicator */}
              {isSaving && (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <span className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              )}
              
              {/* 🔹 Save Album Button */}
              <button
                onClick={handleSaveAlbum}
                disabled={albumSaved || isSaving}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                  albumSaved 
                    ? 'bg-emerald-700 text-emerald-100 cursor-default' 
                    : isSaving
                    ? 'bg-slate-600 text-slate-300 cursor-wait'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                }`}
              >
                {isSaving ? '⏳ Saving...' : albumSaved ? '✓ Saved' : '💾 Save'}
              </button>
              
              <button
                onClick={() => { setShowEditor(true); setShowControls(false); window.scrollTo(0, 0); }}
                className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
              >
                Edit layout
              </button>
              
              {/* 🆕 Shared Links Button */}
              <button
                onClick={() => setShowSharedLinks(true)}
                className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm flex items-center gap-1.5"
                title="Manage shared links"
              >
                🔗 Shared
              </button>
              
            </div>
          </div>
          {!showEditor && (
            <FlipbookPreview
              pages={flipbook.pages}
              photos={flipbook.photos}
              onShare={handleShareAlbumClick}
              onPreview={handlePreviewStory}
              shareBusy={shareBusy}
              previewBusy={previewBusy}
            />
          )}

        </div>
      )}

      {/* Upload Progress Modal */}
      {uploadProgress && <UploadProgressModal progress={uploadProgress} />} {/* ← ADD THIS LINE */}
    </div>

        {/* ===== FULL-SCREEN EDITOR OVERLAY (SYNCHRONIZED) ===== */}
    {showEditor && (
  <SynchronizedEditorModal
    flipbook={flipbook}
    albumPages={albumPages}
    stepCandidates={stepCandidates}
    allSteps={stepsForAnalysis}
    vibes={vibes}
    resultsByUrl={resultsByUrl}
    onChangePages={setAlbumPages}
    onClose={() => {
      setShowEditor(false);
      setShowControls(true);
      document.body.style.overflow = 'auto';
    }}
    onBack={onBack}
  />
)}

{/* ===== SHARE SHEET MODAL ===== */}
{showShareSheet && (
  <div 
    className="fixed inset-0 z-[2000] flex items-center justify-center p-4"
    onClick={() => setShowShareSheet(false)}
  >
    <div className="absolute inset-0 bg-black/60" />
    <div 
      className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
          Share Album
        </h3>
        <button
          onClick={() => setShowShareSheet(false)}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
        Share your travel story! Your friends will see an auto-playing story that they can watch in their browser. No app or account needed.
      </p>
      
      <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={shareUrl}
            readOnly
            className="flex-1 bg-transparent text-sm text-slate-700 dark:text-slate-200 font-mono overflow-x-auto"
            onClick={(e) => e.target.select()}
          />
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                alert('Link copied to clipboard!');
              } catch (err) {
                console.error('Failed to copy:', err);
              }
            }}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
          >
            Copy Link
          </button>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => {
            // Open email client
            window.location.href = `mailto:?subject=${encodeURIComponent(tripName || 'My Album')}&body=${encodeURIComponent('Check out my travel album: ' + shareUrl)}`;
          }}
          className="flex-1 px-4 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded-lg font-medium flex items-center justify-center gap-2"
        >
          📧 Email
        </button>
        <button
          onClick={() => {
            // Open SMS (works on mobile)
            window.location.href = `sms:?body=${encodeURIComponent('Check out my travel album: ' + shareUrl)}`;
          }}
          className="flex-1 px-4 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded-lg font-medium flex items-center justify-center gap-2"
        >
          💬 Message
        </button>
      </div>
    </div>
  </div>
)}

    {/* 🆕 Save Status Indicator (floating) */}
    <SaveStatusIndicator 
      status={saveStatus} 
      progress={saveProgress}
      position="bottom-right"
    />

    {/* 🆕 Migration Banner for users with old localStorage data */}
    <MigrationBanner 
      onMigrationComplete={(result) => {
        console.log('[AlbumFromPast] Migration complete:', result);
        showToast(`Migrated ${result.migrated} albums to new storage`, {
          background: '#059669',
          icon: '✓',
          duration: 3000
        });
      }}
    />

    {/* 🆕 Shared Links Manager Modal */}
    <SharedLinksManager 
      open={showSharedLinks} 
      onClose={() => setShowSharedLinks(false)} 
    />

    </>
  );
}


function CoverPageEditor({ page, allSteps, onChangePages, activePageIndex, pages }) {
  const [selectedStepId, setSelectedStepId] = useState(null);

  // Get photos from selected step, or show current cover photo's step by default
  const currentPhoto = page.photos?.[0];
  
  // Try to find which step the current cover photo belongs to
  const currentStepId = React.useMemo(() => {
    if (!currentPhoto) return allSteps[0]?.id || null;
    
    for (const step of allSteps) {
      if ((step.photos || []).some(p => p.url === currentPhoto.url)) {
        return step.id;
      }
    }
    return allSteps[0]?.id || null;
  }, [currentPhoto, allSteps]);

  // Use selected step or fall back to current photo's step
  const activeStepId = selectedStepId || currentStepId;
  const activeStep = allSteps.find(s => s.id === activeStepId);
  const stepPhotos = activeStep?.photos || [];

  const replaceCoverPhoto = (photo) => {
    const nextPages = pages.map((pg, i) => {
      if (i !== activePageIndex) return pg;
      return { ...pg, photos: [photo] };
    });
    React.startTransition(() => {
      React.startTransition(() => {
        React.startTransition(() => {
      onChangePages(nextPages);
    });
      });
    });
  };

  const getStepPreviewPhoto = (step) => {
    // Get first photo or current cover if it's from this step
    if (currentPhoto && (step.photos || []).some(p => p.url === currentPhoto.url)) {
      return currentPhoto;
    }
    return step.photos?.[0] || null;
  };

  return (
    <div className="space-y-4">
      {/* Current Cover Photo Display */}
      <div className="p-3 bg-slate-800 rounded-lg">
        <h4 className="text-sm font-medium text-slate-300 mb-3">Current Cover Photo</h4>
        {currentPhoto ? (
          <div className="aspect-video bg-slate-700 rounded overflow-hidden">
            <img 
              src={currentPhoto.url} 
              alt="Cover" 
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-video bg-slate-700 rounded flex items-center justify-center">
            <span className="text-slate-400">No cover photo selected</span>
          </div>
        )}
      </div>

      {/* Step Tabs */}
      <div className="p-3 bg-slate-800 rounded-lg">
        <h4 className="text-sm font-medium text-slate-300 mb-3">Choose from Step</h4>
        
        {/* Horizontal scrollable step tabs */}
        {/* Horizontal scrollable step tabs - Ultra compact design */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-4">
          {allSteps.map((step, index) => {
            const isActive = step.id === activeStepId;
            const photoCount = step.photos?.length || 0;
            
            // Create ultra compact step label
            const stepNumber = index + 1;
            const location = step.location || step.state || step.country || 'Unknown';
            // Truncate location to max 8 characters
            const shortLocation = location.length > 8 ? location.substring(0, 8) + '...' : location;
            
            return (
              <button
                key={step.id}
                onClick={() => setSelectedStepId(step.id)}
                className={`flex-shrink-0 px-2 py-1 rounded border transition-all text-xs ${
                  isActive 
                    ? 'border-blue-400 bg-slate-700 text-white' 
                    : 'border-slate-600 bg-slate-700/50 hover:border-slate-500 text-slate-300'
                }`}
                style={{ minWidth: '60px', fontSize: '11px' }}
              >
                <div className="text-center">
                  <div className="font-medium truncate">
                    {stepNumber}. {shortLocation}
                  </div>
                  <div className={`text-xs ${isActive ? 'text-blue-200' : 'text-slate-400'}`} style={{ fontSize: '9px' }}>
                    {photoCount}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Photo selection grid for active step */}
        {stepPhotos.length > 0 ? (
          <div className="bg-slate-700/50 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-600 flex items-center justify-between">
              <h4 className="text-sm font-medium text-slate-300">
                Choose cover photo from {activeStep?.title || activeStep?.location || 'this step'}:
              </h4>
              <span className="text-xs text-slate-400">
                {stepPhotos.length} photos available
              </span>
            </div>
            
            <div 
              className="max-h-80 overflow-y-auto p-3"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="grid grid-cols-4 gap-2">
                {stepPhotos.map((photo, idx) => {
                  const isCurrentCover = currentPhoto && photo.url === currentPhoto.url;
                  return (
                    <button
                      key={photo.url || idx}
                      onClick={() => replaceCoverPhoto(photo)}
                      className={`
                        relative aspect-square rounded-lg overflow-hidden 
                        transition-all duration-150 
                        ${isCurrentCover 
                          ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-800 scale-95' 
                          : 'hover:ring-2 hover:ring-slate-500 hover:scale-105 active:scale-95'
                        }
                      `}
                    >
                      <img 
                        src={photo.url} 
                        alt="" 
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover pointer-events-none"
                        draggable={false}
                      />
                      {isCurrentCover && (
                        <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center pointer-events-none">
                          <div className="bg-blue-500 rounded-full p-1">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-slate-400 text-sm py-4">
            No photos available in this step
          </div>
        )}
      </div>
    </div>
  );
}

function JournalingEditor({ page, onChangePages, activePageIndex, pages }) {
  const [localText, setLocalText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Determine page type and get appropriate text limits and placeholders
  const pageType = ((page.templateKey || page.type) || '').toLowerCase();
const isStepIntro = pageType === 'step-intro-left';
const isJournal = pageType === 'journal';
const isJournalShort = pageType === 'journal-short';  // <-- ADD THIS

const config = isStepIntro ? {
  maxLength: 250,
  placeholder: "Describe this moment... What made this place special?",
  label: "Step Description",
  textKey: 'journalText'
} : isJournalShort ? {  // <-- ADD THIS BLOCK
  maxLength: 300,
  placeholder: "Add a short caption or story for this photo...",
  label: "Short Journal Entry",
  textKey: 'journalText'
} : {
  maxLength: 600, 
  placeholder: "Tell the story of this experience... What happened here? How did it feel?",
  label: "Journal Entry",
  textKey: 'text'
};

  // Initialize text from page meta
  useEffect(() => {
    const currentText = page.meta?.[config.textKey] || '';
    setLocalText(currentText);
    setSavedText(currentText);
    setHasUnsavedChanges(false);
  }, [page, config.textKey, activePageIndex]);

  // Check for unsaved changes
  const checkForChanges = useCallback((text) => {
    setHasUnsavedChanges(text !== savedText);
  }, [savedText]);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    if (newText.length <= config.maxLength) {
      setLocalText(newText);
      checkForChanges(newText);
    }
  };

  const saveChanges = () => {
    const nextPages = pages.map((pg, i) => {
      if (i !== activePageIndex) return pg;
      return {
        ...pg,
        meta: {
          ...pg.meta,
          [config.textKey]: localText
        }
      };
    });
    onChangePages(nextPages);
    setSavedText(localText);
    setHasUnsavedChanges(false);
  };

  const discardChanges = () => {
    setLocalText(savedText);
    setHasUnsavedChanges(false);
  };

  const clearText = () => {
    setLocalText('');
    checkForChanges('');
  };

  const remainingChars = config.maxLength - localText.length;
  const isNearLimit = remainingChars < 50;

  return (
    <div className="space-y-3">
      <div className="p-3 bg-slate-800 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-slate-300">{config.label}</h4>
          {hasUnsavedChanges && (
            <div className="text-xs bg-orange-500 text-white px-2 py-1 rounded font-medium">
              Unsaved
            </div>
          )}
        </div>

        {/* Text input area */}
        <div className="relative">
          <div className="rich-text-editor-wrapper">
  <RichTextEditor
    value={localText}
    onChange={(html) => {
      setLocalText(html);
      setHasUnsavedChanges(true);
    }}
    placeholder={config.placeholder}
  />
</div>
          
          {/* Character counter */}
          <div className={`absolute bottom-2 right-2 text-xs ${
            isNearLimit ? 'text-orange-400' : 'text-slate-400'
          }`}>
            {remainingChars}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            {localText.length > 0 && (
              <button
                onClick={clearText}
                className="text-xs text-slate-400 hover:text-slate-300"
              >
                Clear
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <button
                onClick={discardChanges}
                className="px-3 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded"
              >
                Cancel
              </button>
            )}
            <button
              onClick={saveChanges}
              disabled={!hasUnsavedChanges}
              className={`px-3 py-1 text-xs rounded font-medium ${
                hasUnsavedChanges
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-slate-600 text-slate-400 cursor-not-allowed'
              }`}
            >
              Save Text
            </button>
          </div>
        </div>

        {/* Helpful tip */}
        <div className="text-xs text-slate-500 mt-2">
          {isStepIntro 
            ? "Keep it brief and evocative. This text appears alongside your photos."
            : "Share the story behind these photos. What made this experience memorable?"
          }
        </div>
      </div>

      {/* Live preview of text */}
      {localText && (
        <div className="p-3 bg-slate-700/50 rounded-lg">
          <div className="text-xs text-slate-400 mb-1">Preview:</div>
          <div 
  className="text-sm text-slate-200 italic leading-relaxed"
  dangerouslySetInnerHTML={{ __html: sanitizeRichText(localText) }}
/>
        </div>
      )}
    </div>
  );
}

/* ---------- Timeline Style Editor ---------- */
function TimelineStyleEditor({ page, onChangePages, activePageIndex, pages }) {
  // Get current timeline style settings from page meta
  const currentStyle = page.meta?.timelineStyle || {};
  
  const [fontSize, setFontSize] = useState(currentStyle.fontSize || 'medium');
  const [dateFormat, setDateFormat] = useState(currentStyle.dateFormat || 'MM/DD/YYYY');
  const [placeColor, setPlaceColor] = useState(currentStyle.placeColor || '#000000');
  const [dateColor, setDateColor] = useState(currentStyle.dateColor || '#000000');
  const [fontWeight, setFontWeight] = useState(currentStyle.fontWeight || 'normal');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize from page meta
  useEffect(() => {
    const style = page.meta?.timelineStyle || {};
    setFontSize(style.fontSize || 'medium');
    setDateFormat(style.dateFormat || 'MM/DD/YYYY');
    setPlaceColor(style.placeColor || '#000000');
    setDateColor(style.dateColor || '#000000');
    setFontWeight(style.fontWeight || 'normal');
    setHasUnsavedChanges(false);
  }, [page, activePageIndex]);

  // Check for changes
  const checkForChanges = useCallback(() => {
    const style = page.meta?.timelineStyle || {};
    const hasChanges = 
      fontSize !== (style.fontSize || 'medium') ||
      dateFormat !== (style.dateFormat || 'MM/DD/YYYY') ||
      placeColor !== (style.placeColor || '#000000') ||
      dateColor !== (style.dateColor || '#000000') ||
      fontWeight !== (style.fontWeight || 'normal');
    setHasUnsavedChanges(hasChanges);
  }, [fontSize, dateFormat, placeColor, dateColor, fontWeight, page]);

  useEffect(() => {
    checkForChanges();
  }, [fontSize, dateFormat, placeColor, dateColor, fontWeight, checkForChanges]);

  const saveChanges = () => {
    const nextPages = pages.map((pg, i) => {
      if (i !== activePageIndex) return pg;
      return {
        ...pg,
        meta: {
          ...pg.meta,
          timelineStyle: {
            fontSize,
            dateFormat,
            placeColor,
            dateColor,
            fontWeight
          }
        }
      };
    });
    onChangePages(nextPages);
    setHasUnsavedChanges(false);
  };

  const resetToDefaults = () => {
    setFontSize('medium');
    setDateFormat('MM/DD/YYYY');
    setPlaceColor('#000000');
    setDateColor('#000000');
    setFontWeight('normal');
  };

  return (
    <div className="space-y-3">
      <div className="p-3 bg-slate-800 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-slate-300">Timeline Style</h4>
          {hasUnsavedChanges && (
            <div className="text-xs bg-orange-500 text-white px-2 py-1 rounded font-medium">
              Unsaved
            </div>
          )}
        </div>

        <div className="space-y-3">
          {/* Font Size */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Font Size</label>
            <div className="flex gap-2">
              {['small', 'medium', 'large'].map(size => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`px-3 py-1 text-xs rounded flex-1 ${
                    fontSize === size
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {size.charAt(0).toUpperCase() + size.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Date Format */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Date Format</label>
            <select
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY (e.g., 05/10/2025)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (e.g., 10/05/2025)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (e.g., 2025-05-10)</option>
              <option value="Month DD, YYYY">Month DD, YYYY (e.g., May 10, 2025)</option>
              <option value="DD Month YYYY">DD Month YYYY (e.g., 10 May 2025)</option>
            </select>
          </div>

          {/* Font Weight */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Font Weight</label>
            <div className="flex gap-2">
              {['normal', 'bold'].map(weight => (
                <button
                  key={weight}
                  onClick={() => setFontWeight(weight)}
                  className={`px-3 py-1 text-xs rounded flex-1 ${
                    fontWeight === weight
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {weight.charAt(0).toUpperCase() + weight.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Place Color */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Place/Country Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={placeColor}
                onChange={(e) => setPlaceColor(e.target.value)}
                className="h-8 w-16 rounded cursor-pointer"
              />
              <input
                type="text"
                value={placeColor}
                onChange={(e) => setPlaceColor(e.target.value)}
                className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                placeholder="#ffffff"
              />
            </div>
          </div>

          {/* Date Color */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Date Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={dateColor}
                onChange={(e) => setDateColor(e.target.value)}
                className="h-8 w-16 rounded cursor-pointer"
              />
              <input
                type="text"
                value={dateColor}
                onChange={(e) => setDateColor(e.target.value)}
                className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                placeholder="#94a3b8"
              />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between mt-3">
          <button
            onClick={resetToDefaults}
            className="text-xs text-slate-400 hover:text-slate-300"
          >
            Reset to Defaults
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={saveChanges}
              disabled={!hasUnsavedChanges}
              className={`px-3 py-1 text-xs rounded font-medium ${
                hasUnsavedChanges
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-slate-600 text-slate-400 cursor-not-allowed'
              }`}
            >
              Save Style
            </button>
          </div>
        </div>

        {/* Preview hint */}
        <div className="text-xs text-slate-500 mt-2">
          Changes will apply to the timeline page. The actual timeline content (dates and places) is generated from your trip data.
        </div>
      </div>
    </div>
  );
}

/* ---------- Step Intro Style Editor (Collapsible) ---------- */
function StepIntroStyleEditor({ page, onChangePages, activePageIndex, pages }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Get current step intro style settings from page meta
  const currentStyle = page.meta?.stepIntroStyle || {};
  
  const [titleColor, setTitleColor] = useState(currentStyle.titleColor || '#000000');
  const [dateColor, setDateColor] = useState(currentStyle.dateColor || '#666666');
  const [titleSize, setTitleSize] = useState(currentStyle.titleSize || 'large');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize from page meta
  useEffect(() => {
    const style = page.meta?.stepIntroStyle || {};
    setTitleColor(style.titleColor || '#000000');
    setDateColor(style.dateColor || '#666666');
    setTitleSize(style.titleSize || 'large');
    setHasUnsavedChanges(false);
  }, [page, activePageIndex]);

  // Check for changes
  const checkForChanges = useCallback(() => {
    const style = page.meta?.stepIntroStyle || {};
    const hasChanges = 
      titleColor !== (style.titleColor || '#000000') ||
      dateColor !== (style.dateColor || '#666666') ||
      titleSize !== (style.titleSize || 'large');
    setHasUnsavedChanges(hasChanges);
  }, [titleColor, dateColor, titleSize, page]);

  useEffect(() => {
    checkForChanges();
  }, [titleColor, dateColor, titleSize, checkForChanges]);

  const saveChanges = () => {
    const nextPages = pages.map((pg, i) => {
      if (i !== activePageIndex) return pg;
      return {
        ...pg,
        meta: {
          ...pg.meta,
          stepIntroStyle: {
            titleColor,
            dateColor,
            titleSize
          }
        }
      };
    });
    onChangePages(nextPages);
    setHasUnsavedChanges(false);
  };

  const resetToDefaults = () => {
    setTitleColor('#000000');
    setDateColor('#666666');
    setTitleSize('large');
  };

  return (
    <div className="space-y-3">
      <div className="p-3 bg-slate-800 rounded-lg">
        {/* Collapsible Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-slate-300">Edit Style for Date and Location</h4>
            {hasUnsavedChanges && (
              <div className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded font-medium">
                Unsaved
              </div>
            )}
          </div>
          <svg 
            className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="mt-3 space-y-3 pt-3 border-t border-slate-700">
            {/* Title Size */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Title Size</label>
              <div className="flex gap-2">
                {['medium', 'large', 'xlarge'].map(size => (
                  <button
                    key={size}
                    onClick={() => setTitleSize(size)}
                    className={`px-3 py-1 text-xs rounded flex-1 ${
                      titleSize === size
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {size === 'medium' ? 'Medium' : size === 'large' ? 'Large' : 'XLarge'}
                  </button>
                ))}
              </div>
            </div>

            {/* City/Country Color */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">City/Country Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={titleColor}
                  onChange={(e) => setTitleColor(e.target.value)}
                  className="h-8 w-16 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={titleColor}
                  onChange={(e) => setTitleColor(e.target.value)}
                  className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                  placeholder="#000000"
                />
              </div>
            </div>

            {/* Date Color */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Date Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={dateColor}
                  onChange={(e) => setDateColor(e.target.value)}
                  className="h-8 w-16 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={dateColor}
                  onChange={(e) => setDateColor(e.target.value)}
                  className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                  placeholder="#666666"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={resetToDefaults}
                className="text-xs text-slate-400 hover:text-slate-300"
              >
                Reset to Defaults
              </button>
              
              <button
                onClick={saveChanges}
                disabled={!hasUnsavedChanges}
                className={`px-3 py-1 text-xs rounded font-medium ${
                  hasUnsavedChanges
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                }`}
              >
                Save Style
              </button>
            </div>

            {/* Help text */}
            <div className="text-xs text-slate-500 mt-2">
              Customize how the step intro page displays location and date information.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Small presentational helpers ---------- */
function Card({ title, children }) {
  return (
    <div className="album-builder-card rounded-xl border p-4 space-y-3">
      <div className="album-builder-card-title text-sm font-semibold">{title}</div>
      {children}
    </div>
  );
}
function Badge({ label, ok }) {
  return (
    <span className={`px-2 py-1 rounded-md text-xs ${ok ? 'bg-emerald-700 text-white' : 'bg-rose-800 text-white'}`}>
      {label}: {ok ? 'OK' : '…'}
    </span>
  );
}
function pluralize(n, word) { return `${n} ${word}${n === 1 ? '' : 's'}`; }
function formatDate(iso) {
  if (!iso) return '';
  try {
    // Parse as LOCAL midnight, not UTC (append T00:00:00 without Z)
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
}
function shortPlace(step) { return step.location || step.state || step.country || ''; }
function cityCountryOf(step) {
  // prefer explicit fields, then address, then location fallback
  const city =
    step.city ||
    step.state || // some datasets put city here
    step.address?.city ||
    step.address?.town ||
    step.location || // final fallback if it's a single word
    '';
  const country =
    step.country ||
    step.address?.country ||
    step.address?.countryCode ||
    '';
  return { city, country };
}


// Synchronized Editor Modal - links preview navigation to editor
// Replace the SynchronizedEditorModal function in AlbumFromPast.jsx with this version:

function SynchronizedEditorModal({ flipbook, albumPages, stepCandidates, allSteps, vibes, resultsByUrl, onChangePages, onClose, onBack }) {
  const [activePageIndex, setActivePageIndex] = useState(0);

    // Close just the editor when Android hardware back is pressed
  


  // Safe derivations for use in callbacks declared below
 const currentPage = albumPages?.[activePageIndex] || null;
 const currentStepId = currentPage?.stepId ?? null;
 // If you truly need the object later, you may also derive it now:
 // const currentStep = currentStepId ? allSteps?.find(s => s.id === currentStepId) || null : null;
  
// ===== NEW STATE: View All Pages Modal =====
const [showViewAllPages, setShowViewAllPages] = useState(false);

// ===== UNDO: last deletion stash =====
const [lastDeletion, setLastDeletion] = useState(null); // { page, index, timerId }

const undoDelete = useCallback(() => {
  if (!lastDeletion) return;
  const { page, index, timerId } = lastDeletion;

  // restore page at same index
  const next = [
    ...albumPages.slice(0, index),
    page,
    ...albumPages.slice(index),
  ];
  React.startTransition(() => {
      React.startTransition(() => {
              onChangePages(next);
      });
    });
  setActivePageIndex(index);

  if (timerId) clearTimeout(timerId);
  setLastDeletion(null);
}, [lastDeletion, albumPages, onChangePages]);

// Communicate View All Pages state via global flag
useEffect(() => {
  if (showViewAllPages) {
    window.__vividaViewAllPagesOpen = true;
    console.log('📍 [SynchronizedEditorModal] Setting flag: View All Pages OPEN');
  } else {
    window.__vividaViewAllPagesOpen = false;
    console.log('📍 [SynchronizedEditorModal] Setting flag: View All Pages CLOSED');
  }
}, [showViewAllPages]);

// Handle Android hardware back button for View All Pages
useEffect(() => {
  console.log('🔧 [SynchronizedEditorModal] Registering back button handler');
  
  const onHardwareBack = (e) => {
    console.log('🔙🔙🔙 [SynchronizedEditorModal] Back event received!');
    
    // Close View All Pages modal
    if (showViewAllPages) {
      console.log('[SynchronizedEditorModal] ✅✅✅ CLOSING VIEW ALL PAGES');
      e.preventDefault();
      setShowViewAllPages(false);
      return;
    }

    console.log('[SynchronizedEditorModal] ⚠️ View All Pages not open');
  };

  window.addEventListener('app:hardwareBack', onHardwareBack, true);
  console.log('✅ [SynchronizedEditorModal] Handler registered');
  
  return () => {
    console.log('🗑️ [SynchronizedEditorModal] Removing handler');
    window.removeEventListener('app:hardwareBack', onHardwareBack, true);
  };
}, [showViewAllPages]);


// ===== MODIFIED: Enhanced handleDeletePage =====
// ===== MODIFIED: Enhanced handleDeletePage (with Undo) =====
const handleDeletePage = useCallback(() => {
  if (!albumPages || albumPages.length === 0) return;

  const pageToDelete = albumPages[activePageIndex];
  const tplKey = ((pageToDelete.templateKey || pageToDelete.type) || '').toLowerCase();
  const isSpecial = ['cover', 'stats', 'map', 'route', 'step-intro-left'].includes(tplKey);

  if (isSpecial) {
    alert('Cannot delete special pages (cover, stats, map, route, step intro)');
    return;
  }

  if (!window.confirm(`Delete page ${activePageIndex + 1}?`)) return;

  const nextPages = albumPages.filter((_, i) => i !== activePageIndex);
  onChangePages(nextPages);

  // choose next active index
  if (activePageIndex >= nextPages.length && nextPages.length > 0) {
    setActivePageIndex(nextPages.length - 1);
  } else if (nextPages.length === 0) {
    setActivePageIndex(0);
  }

  // arm 10s undo
  const timerId = setTimeout(() => setLastDeletion(null), 10000);
  setLastDeletion({ page: pageToDelete, index: activePageIndex, timerId });
}, [albumPages, activePageIndex, onChangePages]);


// ===== NEW: Function to add page at specific index =====
const handleAddPageAtIndex = useCallback((insertIndex) => {
   const newPage = {
     templateKey: DEFAULT_TEMPLATE_KEY,
     type: DEFAULT_TEMPLATE_KEY,
     photos: [],
     meta: {},
     stepId: currentStepId || null
   };
  
  const nextPages = [
    ...albumPages.slice(0, insertIndex),
    newPage,
    ...albumPages.slice(insertIndex)
  ];
  
  onChangePages(nextPages);
  setActivePageIndex(insertIndex);
  setShowViewAllPages(false);
}, [albumPages, currentStepId]);

// ===== NEW: Function to add page in current step =====
const handleAddPageInCurrentStep = useCallback(() => {
  const currentPage = albumPages[activePageIndex];
  const stepId = currentPage?.stepId;
  
  const newPage = {
    templateKey: DEFAULT_TEMPLATE_KEY,
    type: DEFAULT_TEMPLATE_KEY,
    photos: [],
    meta: {},
    stepId: stepId || null
  };
  
  const nextPages = [
    ...albumPages.slice(0, activePageIndex + 1),
    newPage,
    ...albumPages.slice(activePageIndex + 1)
  ];
  
  onChangePages(nextPages);
  setActivePageIndex(activePageIndex + 1);
}, [albumPages, activePageIndex]);
  

    // Detect if the active page is the last journal page (journal-right or journal-short)
  const isAtEnd = albumPages?.length > 0 && activePageIndex === (albumPages.length - 1);
  const active = isAtEnd ? albumPages[activePageIndex] : null;
  const isLastJournal = !!active && (['journal-right', 'journal-short'].includes(active.templateKey || active.type));

  // Inline picker: active chip (null = All photos)
const [endPickerStepId, setEndPickerStepId] = useState(null);

const endPickerPhotos = useMemo(() => {
  if (!Array.isArray(allSteps)) return [];
  if (endPickerStepId == null) return allSteps.flatMap(s => s.photos || []);
  const s = allSteps.find(x => x.id === endPickerStepId);
  return s?.photos || [];
}, [allSteps, endPickerStepId]);

const totalAllPhotos = useMemo(
  () => (Array.isArray(allSteps) ? allSteps.reduce((n, s) => n + (s.photos?.length || 0), 0) : 0),
  [allSteps]
);

function labelForStep(step, idx) {
  const city = step.city || step.address?.city || step.address?.town || step.location || step.state || '';
  const country = step.country || step.address?.country || step.address?.country_name || '';
  const where = [city, country].filter(Boolean).join(', ') || (step.title || 'Unknown');
  return `${idx + 1}. ${where}`;
}

const chipClass = (id) =>
  `px-3 py-2 rounded-lg border text-sm min-w-[140px] flex flex-col items-center
   ${endPickerStepId === id ? 'border-sky-500 bg-slate-800 text-white' : 'border-slate-700 bg-slate-900 text-slate-300'}`;

// Replace the very last page photo with the selected one
function replaceEndingPhoto(photo) {
  if (!photo) return;
  const last = albumPages.length - 1;
  const next = albumPages.map((p, i) => (i === last ? { ...p, photos: [photo] } : p));
  onChangePages(next);
}


  // Overlay open/close
  const [showEndPicker, setShowEndPicker] = useState(false);
  // Step selection for the ending-photo picker
const [activeStepId, setActiveStepId] = useState(allSteps?.[0]?.id || null);
const activeStep = useMemo(
  () => (allSteps || []).find(s => s.id === activeStepId) || (allSteps?.[0] || { photos: [] }),
  [allSteps, activeStepId]
);


  // Replace the last page photo
  

  
  return (
    <div
      className="fixed inset-0 z-[1000] bg-slate-950/95 backdrop-blur-sm overflow-hidden"
      role="dialog"
      aria-modal="true"
    >
      <div className="h-full flex flex-col status-safe">
        {/* Modal Header - Compact */}
        <div className="flex items-center justify-between p-3 bg-slate-900 border-b border-slate-700">
          <h1 className="text-lg font-semibold text-white">Album Editor</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Close
            </button>


            

          </div>
        </div>

        

        {/* Modal Body - Vertical Stack */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          
          {/* COMPACT: Preview Section - Reduced height */}
          {/* COMPACT: Preview Section - Mobile Optimized */}
<div className="border-b border-slate-700 bg-slate-800 shrink-0" style={{ minHeight: '240px' }}>
  <div className="p-3 h-full flex flex-col gap-3">
    {/* Button Row - Horizontal on Mobile, visible at top */}
    <div className="flex flex-wrap gap-2 justify-center md:justify-start">
      {/* View All Pages Button */}
      <button
        onClick={() => setShowViewAllPages(true)}
        className="flex-1 sm:flex-initial px-3 py-2 rounded-lg bg-blue-700 hover:bg-blue-600 active:bg-blue-800 text-white text-sm font-medium flex items-center justify-center gap-1.5"
        title="View all album pages"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path d="M4.25 2A2.25 2.25 0 0 0 2 4.25v2.5A2.25 2.25 0 0 0 4.25 9h2.5A2.25 2.25 0 0 0 9 6.75v-2.5A2.25 2.25 0 0 0 6.75 2h-2.5ZM4.25 11A2.25 2.25 0 0 0 2 13.25v2.5A2.25 2.25 0 0 0 4.25 18h2.5A2.25 2.25 0 0 0 9 15.75v-2.5A2.25 2.25 0 0 0 6.75 11h-2.5ZM13.25 2A2.25 2.25 0 0 0 11 4.25v2.5A2.25 2.25 0 0 0 13.25 9h2.5A2.25 2.25 0 0 0 18 6.75v-2.5A2.25 2.25 0 0 0 15.75 2h-2.5ZM11 13.25A2.25 2.25 0 0 1 13.25 11h2.5A2.25 2.25 0 0 1 18 13.25v2.5A2.25 2.25 0 0 1 15.75 18h-2.5A2.25 2.25 0 0 1 11 15.75v-2.5Z" />
        </svg>
        <span className="hidden sm:inline">View All</span>
      </button>

      {/* Delete Button - Only show for non-special pages */}
      {(() => {
        const pageType = ((currentPage?.templateKey || currentPage?.type) || '').toLowerCase();
        const isSpecialPage = ['cover', 'stats', 'map', 'route', 'step-intro-left'].includes(pageType);
        
        if (isSpecialPage) return null;
        
        return (
          <button
            onClick={handleDeletePage}
            className="flex-1 sm:flex-initial px-3 py-2 rounded-lg bg-red-800 hover:bg-red-700 active:bg-red-900 text-white text-sm font-medium flex items-center justify-center gap-1.5"
            title="Delete this page"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.58.11-2.368.11a.75.75 0 0 0-.73.73V16a.75.75 0 0 0 .73.73h12.274a.75.75 0 0 0 .73-.73V4.933a.75.75 0 0 0-.73-.73c-.788 0-1.573-.033-2.368-.11V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM7.5 3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V4.5h-5V3.75Zm-1.493 2h8.986V16a.75.75 0 0 1-.73.73H6.73a.75.75 0 0 1-.73-.73V5.75ZM8.25 8.5a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0v-4a.75.75 0 0 1 .75-.75Zm3 0a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0v-4a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
            </svg>
            <span className="hidden sm:inline">Delete</span>
          </button>
        );
      })()}
      
      {/* Add Page Button */}
      {(() => {
        const pageType = ((currentPage?.templateKey || currentPage?.type) || '').toLowerCase();
        const hasStepId = !!currentPage?.stepId;
        const isSpecialPage = ['cover', 'stats', 'map', 'route', 'step-intro-left', 'timeline'].includes(pageType);
        const isLastPage = activePageIndex === albumPages.length - 1;
        const isJournalPage = pageType.includes('journal');
        const shouldShowAddButton = hasStepId && !isSpecialPage && !(isLastPage && isJournalPage);
        
        if (!shouldShowAddButton) return null;
        
        return (
          <button
            onClick={handleAddPageInCurrentStep}
            className="flex-1 sm:flex-initial px-3 py-2 rounded-lg bg-green-700 hover:bg-green-600 active:bg-green-800 text-white text-sm font-medium flex items-center justify-center gap-1.5"
            title="Add a new page in this step"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
            </svg>
            <span className="hidden sm:inline">Add Page</span>
          </button>
        );
      })()}
    </div>

    {/* Preview Area - Takes remaining space */}
    <div className="flex-1 flex items-center justify-center">
      <CompactPreviewWithSideNav 
        flipbook={flipbook} 
        activePageIndex={activePageIndex}
        onPageChange={setActivePageIndex}
      />
    </div>
  </div>
</div>

          {/* Bottom Section - Editor Controls (takes remaining space) */}
          <div className="flex-1 overflow-auto">
   <div className="p-3 pb-24">
              <h3 className="text-base font-semibold text-white mb-3">Edit Layout</h3>
{/* Undo delete banner */}
{lastDeletion && (
  <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-500/40 bg-amber-500/15 p-3">
    <div className="text-sm text-amber-200">
      Page deleted. <span className="opacity-75">(10s to undo)</span>
    </div>
    <button
      onClick={undoDelete}
      className="rounded bg-amber-600 px-3 py-1 text-xs text-white hover:bg-amber-500"
    >
      Undo
    </button>
  </div>
)}


              <SynchronizedTripAlbumEditor
                pages={albumPages}
                stepCandidates={stepCandidates}
                allSteps={allSteps}
                vibes={vibes}
                resultsByUrl={resultsByUrl}
                onChangePages={onChangePages}
                activePageIndex={activePageIndex}
                onPageChange={setActivePageIndex}
              />
              
              {/* Photo picker for last journal - NOW AFTER template selector and text edit */}
              {isLastJournal && (
  <>
    {/* Step chips (same style as cover) */}
    <div className="mt-4 mb-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700/60">
      <div className="text-slate-300 text-sm font-semibold mb-2">Choose Photo from Step</div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {/* All photos chip */}
        <button
          className={chipClass(null)}
          onClick={() => setEndPickerStepId(null)}
          title="Show all photos in the trip"
        >
          <div>All photos</div>
          <div className="text-[11px] opacity-70">{totalAllPhotos}</div>
        </button>

        {(allSteps || []).map((s, i) => (
          <button
            key={s.id}
            className={chipClass(s.id)}
            onClick={() => setEndPickerStepId(s.id)}
            title={labelForStep(s, i)}
          >
            <div className="truncate w-full text-center">{labelForStep(s, i)}</div>
            <div className="text-[11px] opacity-70">{s.photos?.length || 0}</div>
          </button>
        ))}
      </div>
    </div>

    {/* Thumbnails grid */}
    <div className="mb-6">
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {endPickerPhotos.map((p, i) => (
          <button
            key={p.id || p.url || i}
            onClick={() => replaceEndingPhoto(p)}
            className="group relative rounded-md overflow-hidden bg-slate-800 border border-slate-700 hover:border-emerald-500"
            title={p.fileName || 'Use photo'}
          >
            <img src={p.url} alt="" className="w-full h-32 object-cover" loading="lazy" />
            <div className="absolute bottom-1 right-1 text-[11px] px-1.5 py-0.5 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100">
              Use photo
            </div>
          </button>
        ))}
      </div>
    </div>
  </>
)}
            </div>
          </div>
        </div>
                {/* Ending photo picker overlay */}
        

      </div>

  
      
      {/* ===== View All Pages Modal ===== */}
      {showViewAllPages && (
        <ViewAllPagesModal
          pages={flipbook.pages}
          photos={flipbook?.photos}
          onClose={() => setShowViewAllPages(false)}
          onNavigateToPage={(idx) => {
            setActivePageIndex(idx);
            setShowViewAllPages(false);
          }}
          onDeletePage={(idx) => {
            const nextPages = albumPages.filter((_, i) => i !== idx);
            onChangePages(nextPages);
            if (activePageIndex >= nextPages.length && nextPages.length > 0) {
              setActivePageIndex(nextPages.length - 1);
            }
          }}
          onAddPageAtIndex={handleAddPageAtIndex}
        />
      )}
      
    </div>
  );
}
      


// Editor that syncs with external page control (no internal navigation)
// In AlbumFromPast.jsx, REPLACE the entire SynchronizedTripAlbumEditor function with this:

// In AlbumFromPast.jsx, REPLACE the entire SynchronizedTripAlbumEditor function with this:

// UPDATE your SynchronizedTripAlbumEditor function in AlbumFromPast.jsx:
// Replace the existing applyTemplate function with this enhanced version:

// UPDATE your SynchronizedTripAlbumEditor to include journaling for applicable pages:
// REPLACE your existing SynchronizedTripAlbumEditor function with this enhanced version:

// REPLACE your SynchronizedTripAlbumEditor function with this complete version that includes BOTH journaling AND photo editing:

// REPLACE your SynchronizedTripAlbumEditor function with this corrected version:
// The issue is that step-intro-left is treated as special but still needs photo editing

// ============================================================
// OPTIMIZED SynchronizedTripAlbumEditor - SIMPLE VERSION
// ============================================================
// This version prioritizes WORKING over performance optimization.
// Once confirmed working, we can add memoization back carefully.
// ============================================================

// 🔹 FIXED: Removed custom memo comparison - was causing stale onClick callbacks
const CandidatePhotoBtn = React.memo(({ photo, isSelected, onClick }) => (
  <button
    onClick={() => onClick(photo)}
    className={`
      relative aspect-square rounded-lg overflow-hidden 
      transition-all duration-150 
      ${isSelected 
        ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-800 scale-95' 
        : 'hover:ring-2 hover:ring-slate-500 hover:scale-105 active:scale-95'
      }
    `}
  >
    <img
      src={photo.url}
      alt=""
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover pointer-events-none"
      draggable={false}
    />
    {isSelected && (
      <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center pointer-events-none">
        <div className="bg-blue-500 rounded-full p-1">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    )}
  </button>
));
// ^^^ Using default shallow comparison so onClick changes trigger re-renders

// [The SynchronizedTripAlbumEditor function starts below this]

function SynchronizedTripAlbumEditor({ 
  pages, 
  stepCandidates, 
  onChangePages, 
  activePageIndex, 
  onPageChange, 
  allSteps = [], 
  vibes = new Set(), 
  resultsByUrl = {} 
}) {
  // === FIXED: Perf optimization with proper state management ===
  const [localPages, setLocalPages] = React.useState(pages);
  const commitTimerRef = React.useRef(null);
  const hasPendingEditsRef = React.useRef(false); // Track if we have uncommitted local edits

  // Ref to always access current local pages (avoids stale closures)
  const localPagesRef = React.useRef(pages);
  React.useEffect(() => {
    localPagesRef.current = localPages;
  }, [localPages]);

  // Ref to track current activePageIndex (avoids stale closures)  
  const activePageIndexRef = React.useRef(activePageIndex);
  React.useEffect(() => {
    activePageIndexRef.current = activePageIndex;
  }, [activePageIndex]);

  // Keep local pages in sync if parent replaces the album (e.g., switching trips)
  // BUT only if we don't have pending local edits
  React.useEffect(() => {
    if (!hasPendingEditsRef.current) {
      setLocalPages(pages);
    }
  }, [pages]);

  // Cleanup pending timer on unmount (and flush last local edits)
  React.useEffect(() => {
    return () => {
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      try {
        // Ensure the latest local edits are committed when the editor unmounts
        const latest = localPagesRef.current;
        if (Array.isArray(latest) && latest.length && hasPendingEditsRef.current) {
          React.startTransition(() => {
            onChangePages(latest);
          });
        }
      } catch {}
      hasPendingEditsRef.current = false;
    };
  }, [onChangePages]);

  // FIXED: Debounced commit that actually calls onChangePages (was calling itself!)
  const commitPages = React.useCallback((nextPages) => {
    setLocalPages(nextPages);
    localPagesRef.current = nextPages; // Update ref immediately
    hasPendingEditsRef.current = true; // Mark that we have pending edits

    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      React.startTransition(() => {
        onChangePages(nextPages); // ✅ FIXED: Was calling commitPages (itself) - now calls parent
        hasPendingEditsRef.current = false; // Clear pending flag after commit
      });
    }, 120);
  }, [onChangePages]);

  // Use localPages everywhere for snappy UI
  const pagesLive = localPages || pages;
  const page = pagesLive[activePageIndex] || null;
  const templateOptions = getTemplateOptions(); 
  const SPECIAL_KEYS = new Set(['cover','stats','map','route','step-intro-left']);
  const tplKey = ((page?.templateKey || page?.type) || '').toLowerCase();
  const isJournal = tplKey === 'journal' || tplKey === 'journal-short';

  // Active slot state
  const [activeSlotIndex, setActiveSlotIndex] = React.useState(0);
  const [renderLimit, setRenderLimit] = React.useState(80);
  const scrollContainerRef = React.useRef(null);
  const loadMoreTriggerRef = React.useRef(null);

  function prettyTplKey(p) {
    const k = ((p && (p.templateKey || p.type)) || '').toLowerCase();
    const found = templateOptions.find(t => t.key === k);
    if (k === 'step-intro-left') {
      return 'Step Intro Left';
    }
    if (!found && SPECIAL_KEYS.has(k)) {
        return k.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return found ? found.name : k || '—';
  }

  function applyTemplate(nextKey) {
    const k = (((page && (page.templateKey || page.type)) || '')).toLowerCase();
    if (SPECIAL_KEYS.has(k)) return;

    const newTemplate = templateFor(nextKey);
    const newSlotCount = newTemplate?.slots || 0;
    const currentPhotos = page.photos || [];
    
    let adjustedPhotos = [...currentPhotos];
    
    if (newSlotCount > currentPhotos.length) {
      const emptySlots = newSlotCount - currentPhotos.length;
      const emptySlotsArray = Array(emptySlots).fill({ url: '', position: { x: 50, y: 50, scale: 100 } });
      adjustedPhotos = [...currentPhotos, ...emptySlotsArray];
    }
    else if (newSlotCount < currentPhotos.length && newSlotCount > 0) {
      adjustedPhotos = currentPhotos.slice(0, newSlotCount);
    }
    else if (newSlotCount === 0) {
      adjustedPhotos = [];
    }

    const nextPages = pagesLive.map((pg, i) => 
      i !== activePageIndex ? pg : { 
        ...pg, 
        type: nextKey, 
        templateKey: nextKey,
        photos: adjustedPhotos 
      }
    );
    React.startTransition(() => {
      commitPages(nextPages);
    });
    
    if (activeSlotIndex >= newSlotCount) {
      setActiveSlotIndex(Math.max(0, newSlotCount - 1));
    }
  }

  const tplKeyRaw = ((page && (page.templateKey || page.type)) || '').toLowerCase();
  const isSpecial = SPECIAL_KEYS.has(tplKeyRaw);
  const currentTemplate = templateFor(tplKeyRaw);
  const expectedSlots = currentTemplate?.slots || 0;
  const currentPhotos = page?.photos || [];

  const supportsJournaling = ['step-intro-left', 'journal', 'journal-short', 'journal-right', 'journal-left'].includes(tplKeyRaw);
  const isTimelinePage = tplKeyRaw === 'timeline';
  const canChangeTemplate = !SPECIAL_KEYS.has(tplKeyRaw) && tplKeyRaw !== 'step-intro-left' && tplKeyRaw !== 'timeline';

  // Reset active slot when page changes or slot count changes
  React.useEffect(() => {
    if (activeSlotIndex >= expectedSlots && expectedSlots > 0) {
      setActiveSlotIndex(0);
    }
  }, [activePageIndex, expectedSlots, activeSlotIndex]);

  // Reset render limit when page changes
  React.useEffect(() => {
    setRenderLimit(80);
  }, [activePageIndex]);

  // Photo candidates - same as original
  const cands = React.useMemo(() => {
    const isJournalPage = ['journal', 'journal-short', 'journal-right', 'journal-left'].includes(tplKeyRaw);
    
    if (isJournalPage) {
      const allPhotos = allSteps.flatMap(step => {
        const photos = step.photos || [];
        return photos.map(photo => ({
          ...photo,
          stepId: step.id
        }));
      });
      
      if (allPhotos.length === 0) return [];
      
      try {
        const ranked = semiAutoCandidates(allPhotos, resultsByUrl, { vibes: Array.from(vibes) });
        return Array.isArray(ranked) ? ranked : allPhotos;
      } catch {
        return allPhotos
          .map(p => ({ p, score: (p.likes || 0) * 10 }))
          .sort((a, b) => b.score - a.score)
          .map(r => r.p);
      }
    }
    
    if (!page || !page.stepId) return [];
    
    const matchingStep = allSteps.find(s => s.id === page.stepId);
    
    if (!matchingStep) {      return [];
    }
    
    const pool = (matchingStep.photos || []).filter(p => p && p.url);
    
    if (pool.length === 0) {      return [];
    }
    
    try {
      const ranked = semiAutoCandidates(pool, resultsByUrl, { vibes: Array.from(vibes) });
      return Array.isArray(ranked) ? ranked : pool;
    } catch {
      return pool
        .map(p => ({ p, score: (p.likes || 0) * 10 }))
        .sort((a, b) => b.score - a.score)
        .map(r => r.p);
    }
  }, [page?.stepId, allSteps, tplKeyRaw, vibes, resultsByUrl]);

  // ============================================================
  // FIXED: replaceAt with refs to avoid stale closures
  // ============================================================
  
  // Ref to track current activeSlotIndex (avoids stale closures)
  const activeSlotIndexRef = React.useRef(activeSlotIndex);
  React.useEffect(() => {
    activeSlotIndexRef.current = activeSlotIndex;
  }, [activeSlotIndex]);

  // FIXED: Stable callback that reads current values from refs
  const replaceAt = React.useCallback((photo) => {
    // Read current values from refs to avoid stale closures
    const currentPages = localPagesRef.current;
    const currentPageIndex = activePageIndexRef.current;
    const currentSlotIndex = activeSlotIndexRef.current;
    
    console.log('[replaceAt] Replacing photo:', {
      pageIndex: currentPageIndex,
      slotIndex: currentSlotIndex,
      photoUrl: photo?.url?.slice(-30)
    });
    
    const next = currentPages.map((p, i) => {
      if (i !== currentPageIndex) return p;
      const photos = (p.photos || []).slice();
      // Make sure we have enough slots
      while (photos.length <= currentSlotIndex) {
        photos.push({ url: '', position: { x: 50, y: 50, scale: 100 } });
      }
      photos[currentSlotIndex] = photo;
      return { ...p, photos };
    });
    
    React.startTransition(() => {
      commitPages(next);
    });
  }, [commitPages]); // Only depends on commitPages which is stable

  // FIXED: Position change with refs to avoid stale closures
  const handlePositionChange = React.useCallback((newPosition) => {
    const currentPages = localPagesRef.current;
    const currentPageIndex = activePageIndexRef.current;
    const currentSlotIndex = activeSlotIndexRef.current;
    const page = currentPages[currentPageIndex];
    const currentPhotosArray = page?.photos || [];
    
    const updatedPhotos = [...currentPhotosArray];
    while (updatedPhotos.length <= currentSlotIndex) {
      updatedPhotos.push({ url: '', position: { x: 50, y: 50, scale: 100 } });
    }
    updatedPhotos[currentSlotIndex] = { ...updatedPhotos[currentSlotIndex], position: newPosition };
    
    const nextPages = currentPages.map((pg, i) => 
      i !== currentPageIndex ? pg : { ...pg, photos: updatedPhotos }
    );
    React.startTransition(() => {
      commitPages(nextPages);
    });
  }, [commitPages]);

  // Infinite scroll observer
  React.useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && renderLimit < cands.length) {
          setRenderLimit(prev => Math.min(prev + 50, cands.length));
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [cands.length, renderLimit]);

  // Ensure we have the right number of slots
  React.useEffect(() => {
    if (!page || isSpecial) return;
    
    if (expectedSlots > 0 && currentPhotos.length !== expectedSlots) {
      let adjustedPhotos = [...currentPhotos];
      
      while (adjustedPhotos.length < expectedSlots) {
        adjustedPhotos.push({ url: '', position: { x: 50, y: 50, scale: 100 } });
      }
      
      if (adjustedPhotos.length > expectedSlots) {
        adjustedPhotos = adjustedPhotos.slice(0, expectedSlots);
      }
      
      if (adjustedPhotos.length !== currentPhotos.length) {
        const nextPages = pagesLive.map((pg, i) => 
          i !== activePageIndex ? pg : { ...pg, photos: adjustedPhotos }
        );
        commitPages(nextPages);
      }
    }
  }, [tplKeyRaw, expectedSlots, currentPhotos.length, isSpecial, activePageIndex, pages, onChangePages, page]);

  if (!page) {
    return (
      <div className="text-center p-4 bg-slate-800 rounded-lg">
        <div className="text-slate-400">No page found at index {activePageIndex}</div>
      </div>
    );
  }

  const isCover = tplKeyRaw === 'cover';
  const activePhoto = currentPhotos[activeSlotIndex] || { url: '', position: { x: 50, y: 50, scale: 100 } };
  const activePhotoHasImage = activePhoto && activePhoto.url;
  const visibleCands = cands.slice(0, renderLimit);

  return (
    <div className="space-y-4">
      {/* Cover Page Special Editor */}
      {isCover ? (
        <CoverPageEditor 
          page={page}
          allSteps={allSteps}
          onChangePages={onChangePages}
          activePageIndex={activePageIndex}
          pages={pages}
        />
      ) : (
        <>
          {/* Template Selector */}
          {canChangeTemplate && (
            <div className="p-3 bg-slate-800 rounded-lg">
              <label className="block text-sm text-slate-400 mb-2">Page Template</label>
              <select
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                value={tplKeyRaw}
                onChange={e => applyTemplate(e.target.value)}
              >
                {templateOptions.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
              </select>
            </div>
          )}

          {tplKeyRaw === 'step-intro-left' && (
            <>
              <div className="p-3 bg-slate-800 rounded-lg">
                <label className="block text-sm text-slate-400 mb-2">Page Template</label>
                <select
                  className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  value={tplKeyRaw}
                  disabled={true}
                >
                  <option value={tplKeyRaw}>Step Intro Left (locked)</option>
                </select>
              </div>
              
              <StepIntroStyleEditor 
                page={page}
                onChangePages={onChangePages}
                activePageIndex={activePageIndex}
                pages={pages}
              />
            </>
          )}

          {isTimelinePage && (
            <div className="p-3 bg-slate-800 rounded-lg">
              <label className="block text-sm text-slate-400 mb-2">Page Template</label>
              <select
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                value={tplKeyRaw}
                onChange={e => applyTemplate(e.target.value)}
              >
                {templateOptions.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
              </select>
              <div className="text-xs text-slate-500 mt-1">
                Timeline page contains your trip's date and location summary
              </div>
            </div>
          )}

          {isTimelinePage && (
            <TimelineStyleEditor 
              page={page}
              onChangePages={onChangePages}
              activePageIndex={activePageIndex}
              pages={pages}
            />
          )}

          {supportsJournaling && (
            <JournalingEditor 
              page={page}
              onChangePages={onChangePages}
              activePageIndex={activePageIndex}
              pages={pages}
            />
          )}

          {/* ============================================================ */}
          {/* Single Camera Roll UI */}
          {/* ============================================================ */}
          {expectedSlots > 0 ? (
            <div className="space-y-4">
              
              {/* SLOT SELECTOR PILLS */}
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-slate-300">
                    Select Photo Slot
                  </h4>
                  <span className="text-xs text-slate-500">
                    {expectedSlots} slot{expectedSlots > 1 ? 's' : ''} available
                  </span>
                </div>
                
                <div className="flex gap-2 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {Array.from({ length: expectedSlots }).map((_, idx) => {
                    const slotPhoto = currentPhotos[idx] || { url: '', position: { x: 50, y: 50, scale: 100 } };
                    const hasPhoto = slotPhoto && slotPhoto.url;
                    const isActive = activeSlotIndex === idx;
                    
                    return (
                      <button
                        key={`slot-pill-${idx}`}
                        onClick={() => {
setActiveSlotIndex(idx);
                        }}
                        className={`
                          flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all
                          ${isActive 
                            ? 'border-blue-500 bg-blue-500/20 ring-2 ring-blue-500/30' 
                            : 'border-slate-600 bg-slate-700 hover:border-slate-500'
                          }
                        `}
                      >
                        <div className={`
                          w-10 h-10 rounded overflow-hidden flex-shrink-0
                          ${hasPhoto ? '' : 'bg-slate-600 flex items-center justify-center'}
                        `}>
                          {hasPhoto ? (
                            <img 
                              src={slotPhoto.url} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-slate-400 text-xs">Empty</span>
                          )}
                        </div>
                        
                        <div className="text-left">
                          <div className={`text-sm font-medium ${isActive ? 'text-blue-300' : 'text-slate-300'}`}>
                            Slot {idx + 1}
                          </div>
                          <div className={`text-xs ${isActive ? 'text-blue-400' : 'text-slate-500'}`}>
                            {hasPhoto ? 'Has photo' : 'Empty'}
                          </div>
                        </div>
                        
                        {isActive && (
                          <div className="w-2 h-2 rounded-full bg-blue-400 ml-1 animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ACTIVE SLOT PHOTO EDITOR */}
              <div className="bg-slate-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm font-medium text-slate-200">
                      Editing Slot {activeSlotIndex + 1}
                    </span>
                  </div>
                  {activePhotoHasImage && (
                    <span className="text-xs text-slate-400">
                      Drag to reposition • Use slider to zoom
                    </span>
                  )}
                </div>
                
                {activePhotoHasImage ? (
                  <PhotoPositionEditor
                    photo={activePhoto}
                    onPositionChange={handlePositionChange}
                  />
                ) : (
                  <div className="aspect-video bg-slate-700 flex flex-col items-center justify-center p-6">
                    <div className="text-4xl mb-3 opacity-50">📷</div>
                    <span className="text-slate-400 text-sm text-center">
                      No photo in Slot {activeSlotIndex + 1}
                    </span>
                    <span className="text-slate-500 text-xs mt-1">
                      Select a photo from below to add to this slot
                    </span>
                  </div>
                )}
              </div>

              {/* SINGLE CAMERA ROLL - Using simple inline onClick like original */}
              <div className="bg-slate-800 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-300">
                    Choose Photo for Slot {activeSlotIndex + 1}
                  </h4>
                  <span className="text-xs text-slate-500">
                    {cands.length} photo{cands.length !== 1 ? 's' : ''} available
                  </span>
                </div>
                
                <div 
                  ref={scrollContainerRef}
                  className="max-h-80 overflow-y-auto p-3"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {cands.length > 0 ? (
                    <>
                      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {visibleCands.map((c, i) => (
                          <CandidatePhotoBtn
                            key={c.id || c.url || i}
                            photo={c}
                            isSelected={activePhoto?.url === c.url}
                            onClick={replaceAt} 
                          />
                        ))}
                      </div>
                      
                      {renderLimit < cands.length && (
                        <div 
                          ref={loadMoreTriggerRef}
                          className="flex items-center justify-center py-4 text-slate-500 text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                            <span>Loading more photos...</span>
                          </div>
                        </div>
                      )}
                      
                      {renderLimit >= cands.length && cands.length > 80 && (
                        <div className="text-center py-2 text-xs text-slate-500">
                          Showing all {cands.length} photos
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      No photos available from this step
                    </div>
                  )}
                </div>
              </div>
              
            </div>
          ) : (
            <div className="text-sm text-slate-400 p-4 bg-slate-800 rounded-lg text-center">
              This page has no photo slots.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Add this new component to handle photo positioning in your AlbumFromPast.jsx

// Photo Position Editor Component
// REPLACE your existing PhotoPositionEditor component with this enhanced version:

// REPLACE your PhotoPositionEditor component in AlbumFromPast.jsx with this fixed-size version:

function PhotoPositionEditor({ photo, onPositionChange }) {
  const [localPosition, setLocalPosition] = useState(photo.position || { x: 50, y: 50, scale: 100 });
  const [savedPosition, setSavedPosition] = useState(photo.position || { x: 50, y: 50, scale: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const imageRef = useRef(null);

  // Update local state when photo prop changes (e.g., when switching photos)
  useEffect(() => {
    const newPosition = photo.position || { x: 50, y: 50, scale: 100 };
    setLocalPosition(newPosition);
    setSavedPosition(newPosition);
    setHasUnsavedChanges(false);
  }, [photo.url]); // Key on photo.url to detect photo changes

  // Check if current position differs from saved
  const checkForChanges = useCallback((position) => {
    const hasChanges = 
      position.x !== savedPosition.x || 
      position.y !== savedPosition.y || 
      position.scale !== savedPosition.scale;
    setHasUnsavedChanges(hasChanges);
  }, [savedPosition]);

  // Update position and immediately send to preview (live preview)
  const updatePosition = useCallback((newPosition) => {
    setLocalPosition(newPosition);
    checkForChanges(newPosition);
    // Send live changes to preview immediately
    onPositionChange(newPosition);
  }, [onPositionChange, checkForChanges]);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newPosition = {
      ...localPosition,
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    };
    
    updatePosition(newPosition);
  }, [isDragging, localPosition, updatePosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleScaleChange = (newScale) => {
    const newPosition = { ...localPosition, scale: newScale };
    updatePosition(newPosition);
  };

  const handleSliderChange = (axis, value) => {
    const newPosition = { ...localPosition, [axis]: parseInt(value) };
    updatePosition(newPosition);
  };

  const resetPosition = () => {
    const defaultPosition = { x: 50, y: 50, scale: 100 };
    updatePosition(defaultPosition);
  };

  const saveChanges = () => {
    setSavedPosition(localPosition);
    setHasUnsavedChanges(false);
    // Final save callback could trigger a different action if needed
    console.log('Position saved:', localPosition);
  };

  const discardChanges = () => {
    setLocalPosition(savedPosition);
    setHasUnsavedChanges(false);
    // Revert preview to saved state
    onPositionChange(savedPosition);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const imageStyle = {
    objectPosition: `${localPosition.x}% ${localPosition.y}%`,
    transform: `scale(${localPosition.scale / 100})`,
    transformOrigin: `${localPosition.x}% ${localPosition.y}%`
  };

  return (
    <div className="relative group">
      {/* RESPONSIVE: Main Image Display - adapts to screen size */}
      <div 
        className="bg-slate-700 relative overflow-hidden rounded-lg mx-auto"
        style={{ 
          width: '100%',
          maxWidth: '576px',  // Desktop: 576px (1.2x the preview width)
          aspectRatio: '3 / 2', // Maintains 3:2 aspect ratio
        }}
      >
        <img
  ref={imageRef}
  src={photo.url}
  alt=""
  loading="eager"
  decoding="async"
  className={`w-full h-full object-cover transition-all duration-150 ${
    isDragging ? 'cursor-grabbing' : 'cursor-grab'
  }`}
  style={imageStyle}
  onMouseDown={handleMouseDown}
  draggable={false}
/>
        
        {/* Overlay with positioning crosshair */}
        {(showControls || isDragging) && (
          <div 
            className="absolute pointer-events-none"
            style={{
              left: `${localPosition.x}%`,
              top: `${localPosition.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="w-4 h-4 border-2 border-white rounded-full bg-blue-500 shadow-lg"></div>
            <div className="absolute top-1/2 left-1/2 w-8 h-0.5 bg-white -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-1/2 left-1/2 w-0.5 h-8 bg-white -translate-x-1/2 -translate-y-1/2"></div>
          </div>
        )}
        
        {/* Edit button overlay */}
        <button
          onClick={() => setShowControls(!showControls)}
          className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-xs"
        >
          Edit
        </button>

        {/* Unsaved changes indicator */}
        {hasUnsavedChanges && (
          <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded text-xs font-medium">
            Unsaved
          </div>
        )}
      </div>

      {/* Position Controls */}
      {showControls && (
        <div className="mt-2 p-3 bg-slate-700 rounded-lg space-y-3" style={{ maxWidth: '576px', margin: '8px auto 0' }}>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-300">Position & Scale</span>
            <div className="flex items-center gap-2">
              <button
                onClick={resetPosition}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Reset
              </button>
              {hasUnsavedChanges && (
                <button
                  onClick={discardChanges}
                  className="text-xs text-gray-400 hover:text-gray-300"
                >
                  Discard
                </button>
              )}
            </div>
          </div>
          
          {/* Scale Control */}
          <div className="space-y-1">
            <label className="text-xs text-slate-400">Zoom: {localPosition.scale}%</label>
            <input
              type="range"
              min="50"
              max="200"
              value={localPosition.scale}
              onChange={(e) => handleScaleChange(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          
          {/* Position Fine-tune */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs text-slate-400">X: {Math.round(localPosition.x)}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={localPosition.x}
                onChange={(e) => handleSliderChange('x', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-400">Y: {Math.round(localPosition.y)}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={localPosition.y}
                onChange={(e) => handleSliderChange('y', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          
          {/* Save/Discard Actions */}
          {hasUnsavedChanges && (
            <div className="flex gap-2 pt-2 border-t border-slate-600">
              <button
                onClick={saveChanges}
                className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded font-medium"
              >
                Save Position
              </button>
              <button
                onClick={discardChanges}
                className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded"
              >
                Cancel
              </button>
            </div>
          )}
          
          <div className="text-xs text-slate-500">
            {hasUnsavedChanges 
              ? "Changes are shown in preview. Click Save to keep them."
              : "Drag the image to reposition, or use controls above"
            }
          </div>
        </div>
      )}
    </div>
  );
}
// Add formatNumber helper function
const formatNumber = (num) => (num || 0).toLocaleString();

// Add this NEW component specifically for editor preview
function StatsPreviewCard({ meta }) {
  console.log('StatsPreviewCard rendering with meta:', meta);
  
  // Use the same safe defaults as the real StatsCard component would
  const safeMeta = {
    totalMiles: meta?.totalMiles || 0,
    days: meta?.days || 0,
    cities: meta?.cities || 0,
    photos: meta?.photos || 0,
    furthestPointDistance: meta?.furthestPointDistance || 0,
    homeLocation: meta?.homeLocation || 'Unknown',
    furthestLocation: meta?.furthestLocation || 'Unknown',
    stepsLogged: meta?.stepsLogged || 0
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      backgroundColor: '#f9f7f5',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      fontFamily: "'Lato', sans-serif",
      fontSize: '12px',  // Smaller font for preview
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '12px',
        width: '100%'
      }}>
        <div style={{ 
          fontSize: '10px', 
          letterSpacing: '1px', 
          color: '#666', 
          marginBottom: '4px',
          textTransform: 'uppercase'
        }}>
          YOUR JOURNEY SNAPSHOT
        </div>
        <h2 style={{ 
          fontSize: '16px', 
          margin: '0', 
          color: '#222',
          fontWeight: 'bold'
        }}>
          YOUR JOURNEY IN NUMBERS
        </h2>
      </div>

      {/* Stats Grid - 2x2 layout for better fit */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '12px',
        width: '100%',
        maxWidth: '300px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '20px', marginBottom: '2px' }}>✈️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e74c3c' }}>
            {formatNumber(safeMeta.totalMiles)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
            Miles Traveled
          </div>
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '20px', marginBottom: '2px' }}>📅</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e74c3c' }}>
            {formatNumber(safeMeta.days)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
            Days Explored
          </div>
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '20px', marginBottom: '2px' }}>🏙️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e74c3c' }}>
            {formatNumber(safeMeta.cities)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
            Cities Discovered
          </div>
        </div>
        
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '20px', marginBottom: '2px' }}>📸</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e74c3c' }}>
            {formatNumber(safeMeta.photos)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>
            Photos Captured
          </div>
        </div>
      </div>

      {/* Distance line */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        fontSize: '9px',
        color: '#666'
      }}>
        <span style={{ fontSize: '12px' }}>🏠</span>
        <span>{safeMeta.homeLocation}</span>
        <div style={{ 
          flex: '1', 
          height: '1px', 
          background: '#ddd',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#f9f7f5',
            padding: '0 4px',
            fontSize: '8px',
            color: '#999'
          }}>
            {formatNumber(safeMeta.furthestPointDistance)} MILES
          </div>
        </div>
        <span>{safeMeta.furthestLocation}</span>
        <span style={{ fontSize: '12px' }}>🟢</span>
      </div>

      {/* Steps logged */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '10px',
        color: '#666'
      }}>
        <span style={{ fontSize: '14px' }}>👣</span>
        <span style={{ fontWeight: 'bold', color: '#e74c3c' }}>
          {formatNumber(safeMeta.stepsLogged)}
        </span>
        <span>STEPS LOGGED</span>
      </div>
    </div>
  );
}



// NEW: Compact preview component with side navigation  
const CompactPreviewWithSideNav = React.memo(function CompactPreviewWithSideNav({ flipbook, activePageIndex, onPageChange }) {
  const page = flipbook.pages?.[activePageIndex] || null;
  const totalPages = flipbook.pages?.length || 0;

  const photoIndex = React.useMemo(() => {
    if (!Array.isArray(flipbook.photos)) return {};
    return Object.fromEntries(flipbook.photos.map(p => [p.id, p]));
  }, [flipbook.photos]);

  const goToPage = React.useCallback((newIndex) => {
    const clampedIndex = Math.max(0, Math.min(totalPages - 1, newIndex));
    onPageChange(clampedIndex);
  }, [totalPages, onPageChange]);

  if (!page) {
    return (
      <div className="flex items-center justify-center h-32 bg-slate-700 rounded-lg">
        <span className="text-slate-400 text-sm">No page to preview</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 md:gap-4 w-full">
      <button
        onClick={(e) => { e.stopPropagation(); goToPage(activePageIndex - 1); }}
        disabled={activePageIndex <= 0}
        className="px-2 md:px-3 py-2 rounded bg-slate-700 text-white disabled:opacity-50 text-sm hover:bg-slate-600 flex-shrink-0"
      >
        ←
      </button>

      <div
        className="relative flex-1"
        style={{ maxWidth: '480px', width: '100%' }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'PageUp') goToPage(activePageIndex - 1);
          if (e.key === 'ArrowRight' || e.key === 'PageDown') goToPage(activePageIndex + 1);
          if (e.key === 'Home') goToPage(0);
          if (e.key === 'End') goToPage(totalPages - 1);
        }}
        onWheel={(e) => {
          if (Math.abs(e.deltaY) < 2) return;
          if (e.deltaY > 0) goToPage(activePageIndex + 1);
          else goToPage(activePageIndex - 1);
        }}
        onMouseUp={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          if (x < rect.width / 2) goToPage(activePageIndex - 1);
          else goToPage(activePageIndex + 1);
        }}
        onTouchStart={(e) => {
          const t = e.changedTouches?.[0];
          if (!t) return;
          e.currentTarget.__swipeX = t.clientX;
        }}
        onTouchEnd={(e) => {
          const t = e.changedTouches?.[0];
          if (!t) return;
          const startX = e.currentTarget.__swipeX || 0;
          const dx = t.clientX - startX;
          if (Math.abs(dx) > 40) {
            if (dx < 0) goToPage(activePageIndex + 1);
            else goToPage(activePageIndex - 1);
          }
        }}
      >
        <div 
          className="bg-gray-100 rounded-lg shadow-lg overflow-hidden relative w-full" 
          style={{ aspectRatio: '3 / 2' }}
        >
          <MiniPage 
            page={page} 
            photoIndex={photoIndex}
            targetWidth={null}
          />
        </div>

        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-2 py-1 rounded text-xs z-10">
          {activePageIndex + 1}/{totalPages}
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); goToPage(activePageIndex + 1); }}
        disabled={activePageIndex >= totalPages - 1}
        className="px-2 md:px-3 py-2 rounded bg-slate-700 text-white disabled:opacity-50 text-sm hover:bg-slate-600 flex-shrink-0"
      >
        →
      </button>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.activePageIndex === nextProps.activePageIndex &&
    prevProps.flipbook.pages === nextProps.flipbook.pages &&
    prevProps.flipbook.photos === nextProps.flipbook.photos
  );
});

// Mobile-optimized preview pane with navigation
function MobilePreviewPane({ flipbook }) {
  const [previewIndex, setPreviewIndex] = React.useState(0);
  const page = flipbook.pages?.[previewIndex] || null;
  const totalPages = flipbook.pages?.length || 0;

  const goToPage = (newIndex) => {
    const clampedIndex = Math.max(0, Math.min(totalPages - 1, newIndex));
    setPreviewIndex(clampedIndex);
  };

  if (!page) {
    return (
      <div className="flex items-center justify-center h-32 bg-slate-700 rounded-lg">
        <span className="text-slate-400 text-sm">No page to preview</span>
      </div>
    );
  }

  return (
    <div
  className="space-y-3"
  onWheel={(e) => {
    if (Math.abs(e.deltaY) < 2) return;
    if (e.deltaY > 0) goToPage(previewIndex + 1);
    else goToPage(previewIndex - 1);
  }}
  onTouchStart={(e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    e.currentTarget.__swipeX = t.clientX;
  }}
  onTouchEnd={(e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const startX = e.currentTarget.__swipeX || 0;
    const dx = t.clientX - startX;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goToPage(previewIndex + 1);
      else goToPage(previewIndex - 1);
    }
  }}
>

      {/* Page Navigation Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => goToPage(previewIndex - 1)}
            disabled={previewIndex <= 0}
            className="px-2 py-1 rounded bg-slate-700 text-white disabled:opacity-50 text-xs"
          >
            ←
          </button>
          <span className="text-slate-300 text-xs">
            {previewIndex + 1}/{totalPages}
          </span>
          <button
            onClick={() => goToPage(previewIndex + 1)}
            disabled={previewIndex >= totalPages - 1}
            className="px-2 py-1 rounded bg-slate-700 text-white disabled:opacity-50 text-xs"
          >
            →
          </button>
        </div>
      </div>

      {/* Full-width preview with correct aspect ratio */}
      <div 
        className="bg-white rounded-lg overflow-hidden shadow-lg mx-auto" 
        style={{ 
          maxWidth: '900px',
          width: '100%',
          aspectRatio: '1024 / 680',
          position: 'relative'
        }}
      >
        <FlipbookPreview 
          pages={[page]} 
          photos={flipbook.photos} 
          current={0}
          pageWidth={1024}
          pageHeight={680}
          debug={false}
          showButtons={false}
          fillContainer={true}
          offsetX={0}
          offsetY={0}
        />
      </div>

      {/* Compact page info */}
      <div className="text-xs text-slate-400 text-center space-x-4">
        <span>Type: {page.type}</span>
        <span>Photos: {(page.items || []).length}</span>
      </div>
    </div>
  );
}



// Mobile-optimized editor - preserves ALL existing functionality

// =======================================================
// Reusable photo picker (Cover + Journal use the same UI)
// =======================================================
function PhotoPickerOverlay({ open, title, steps, initialStepId = null, onSelect, onClose }) {
  const [activeStepId, setActiveStepId] = React.useState(initialStepId);

  // label like: "1. City, Country" (fallbacks if fields are missing)
  const labelForStep = React.useCallback((step, idx) => {
    const city =
      step.city ||
      step.address?.city ||
      step.address?.town ||
      step.location ||
      step.state ||
      '';
    const country =
      step.country ||
      step.address?.country ||
      step.address?.country_name ||
      '';
    const where = [city, country].filter(Boolean).join(', ') || (step.title || 'Unknown');
    return `${idx + 1}. ${where}`;
  }, []);

  const totalAllPhotos = React.useMemo(
    () => (Array.isArray(steps) ? steps.reduce((n, s) => n + (s.photos?.length || 0), 0) : 0),
    [steps]
  );

  const photosForPicker = React.useMemo(() => {
    if (!Array.isArray(steps)) return [];
    if (activeStepId == null) return steps.flatMap(s => s.photos || []);
    const s = steps.find(x => x.id === activeStepId);
    return s?.photos || [];
  }, [steps, activeStepId]);

  const chipClass = (id) =>
    `px-3 py-2 rounded-lg border text-sm min-w-[140px] flex flex-col items-center
     ${activeStepId === id ? 'border-sky-500 bg-slate-800 text-white' : 'border-slate-700 bg-slate-900 text-slate-300'}`;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="absolute inset-6 md:inset-12 bg-slate-900 rounded-lg shadow-2xl overflow-hidden border border-slate-700 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="text-white font-semibold">{title || 'Choose a photo'}</div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm"
          >
            Close
          </button>
        </div>

        {/* Step chips */}
        <div className="p-3 border-b border-slate-700">
          <div className="text-slate-300 text-sm font-semibold mb-2">Choose from Step</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              className={chipClass(null)}
              onClick={() => setActiveStepId(null)}
              title="Show all photos in the trip"
            >
              <div>All photos</div>
              <div className="text-[11px] opacity-70">{totalAllPhotos}</div>
            </button>
            {(steps || []).map((s, i) => (
              <button
                key={s.id}
                className={chipClass(s.id)}
                onClick={() => setActiveStepId(s.id)}
                title={labelForStep(s, i)}
              >
                <div className="truncate w-full text-center">{labelForStep(s, i)}</div>
                <div className="text-[11px] opacity-70">{s.photos?.length || 0}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Thumbnails grid */}
        <div 
  className="flex-1 overflow-auto p-3" 
  style={{ 
    WebkitOverflowScrolling: 'touch'
    // REMOVED: overscrollBehavior - was blocking scrolls
  }}
>
  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
    {photosForPicker.map((p, i) => (
      <button
        key={p.id || p.url || i}
        onClick={(e) => {
          e.stopPropagation(); // Keep this
          onSelect && onSelect(p);
        }}
        // REMOVED: onTouchEnd - was interfering with scrolling
        // REMOVED: touchAction - was blocking scroll gestures
        className="group relative rounded-md overflow-hidden bg-slate-800 border border-slate-700 hover:border-emerald-500 active:border-emerald-600 cursor-pointer"
        title={p.fileName || 'Use photo'}
      >
        {/* 🆕 Use LazyPhoto for better performance with large albums */}
        <LazyPhoto 
          photoId={p.id}
          fallbackUrl={p.url} 
          useThumbnail={true}
          alt="" 
          className="w-full h-32 object-cover pointer-events-none" 
        />
        <div className="absolute bottom-1 right-1 text-[11px] px-1.5 py-0.5 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100">
          Use photo
        </div>
      </button>
    ))}
  </div>
</div>
      </div>
    </div>
  );
}

// Custom styles for rich text editor in dark theme
const styles = `
  .rich-text-editor-wrapper {
    border-radius: 8px;
    overflow: hidden;
  }
  
  .rich-text-editor-wrapper [contentEditable] {
    background: #334155 !important;
    color: #e2e8f0 !important;
    border: 1px solid #475569 !important;
    min-height: 120px !important;
  }
  
  .rich-text-editor-wrapper button {
    background: #475569 !important;
    color: #e2e8f0 !important;
  }
  
  .rich-text-editor-wrapper button:hover {
    background: #64748b !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}